import { RelojProcesalCard } from "../components/RelojProcesalCard";
import { MatrizProbatoriaPanel } from "../components/MatrizProbatoriaPanel";
import { BulkIngestionModal } from "../components/BulkIngestionModal";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Copy, Printer,
    ArrowLeft, Save, Tag, Trash2, Share2, Paperclip,
    ChevronDown, ChevronUp, ChevronRight, Plus, Users, User, Building2, FileText,
    MessageSquare, Clock, Briefcase, Upload, Mail,
    Calendar, CheckCircle2, AlertCircle, ExternalLink,
    Zap, Brain, Layout, Shield, TrendingUp, CheckSquare, Sparkles, Send, Loader2, Search, Database, FileCheck, FileEdit, X, Eye,
    ClipboardList, AlertTriangle, RefreshCw, Coins, Box, Play, Pause, Scale, MailCheck, CheckCheck, Download,
} from "lucide-react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useDespachos } from "@/hooks/useDespachos";
import { supabase } from "@/lib/supabase";
import * as scraperApi from "@/services/scraperApi";
import * as legalEmailsApi from "@/services/legalEmailsApi";
import { createLegalAIAssistantClient } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { classifyActuacion } from "@/lib/actuacionClassification";
import { toast as sonnerToast } from "sonner";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import LegalEditor from "@/components/editor/LegalEditor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import JoditEditor from "jodit-react";
import { UsageTracker } from "@/lib/services/usage-tracker";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import useDrivePicker from "react-google-drive-picker";
import { cn } from "@/lib/utils";
import { getVigenteStatusInfo } from "@/lib/processStatus";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

const renderSafeText = (val: any, fallback: string = ""): React.ReactNode => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (Array.isArray(val)) {
        return (
            <div className="space-y-1">
                {val.map((item, idx) => (
                    <div key={idx}>
                        {typeof item === 'object' && item !== null
                            ? (item.nombre || item.descripcion || item.texto || item.punto || item.label || JSON.stringify(item))
                            : String(item)}
                    </div>
                ))}
            </div>
        );
    }
    if (typeof val === 'object') {
        if (val.nombre && typeof val.nombre === 'string') return val.nombre;
        if (val.descripcion && typeof val.descripcion === 'string') return val.descripcion;
        if (val.texto && typeof val.texto === 'string') return val.texto;
        if (val.resumen && typeof val.resumen === 'string') return val.resumen;
        if (val.estrategia && typeof val.estrategia === 'string') return val.estrategia;
        if (val.punto && typeof val.punto === 'string') return val.punto;
        if (val.label && typeof val.label === 'string') return val.label;

        const entries = Object.entries(val);
        if (entries.length > 0) {
            return (
                <div className="space-y-2">
                    {entries.map(([k, v], i) => (
                        <div key={i} className="text-xs">
                            <strong className="font-bold text-gray-800">{k.replace(/_/g, " ")}: </strong>
                            {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
                        </div>
                    ))}
                </div>
            );
        }
        return JSON.stringify(val);
    }
    return String(val);
};

const isUuid = (val?: string): boolean =>
    !!val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim());

export const ProcessDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { processes, updateProcess, addProcess, deleteProcess, clients } = useDespachos();
    const process = useMemo(() => {
        if (!id) return undefined;
        const cleanId = id.trim();
        const digitsId = cleanId.replace(/\D/g, "");
        return processes.find(p => {
            if (p.id === cleanId) return true;
            if (p.case_number === cleanId) return true;
            if (p.radicado === cleanId) return true;
            if (digitsId.length >= 8) {
                const pNumDigits = (p.case_number || "").replace(/\D/g, "");
                const pRadDigits = (p.radicado || "").replace(/\D/g, "");
                if (pNumDigits && pNumDigits === digitsId) return true;
                if (pRadDigits && pRadDigits === digitsId) return true;
            }
            return false;
        });
    }, [processes, id]);
    const { user, profile, subscriptions = [] } = useAuth();

    const userPlan = (
        subscriptions?.[0] ||
        (profile as any)?.plan ||
        (profile as any)?.plan_id ||
        user?.user_metadata?.plan_id ||
        "free"
    ).toLowerCase();

    const hasProAccess =
        (profile as any)?.role === 'admin' ||
        user?.email?.includes('admin') ||
        subscriptions.some(s => ["profesional", "empresarial", "pro", "enterprise"].includes(s.toLowerCase())) ||
        ["profesional", "empresarial", "pro", "enterprise"].includes(userPlan);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialSection = searchParams.get("section") || "generales";
    const [activeSection, setActiveSection] = useState(initialSection);
    
    useEffect(() => {
        const section = searchParams.get("section");
        if (section && section !== activeSection) {
            setActiveSection(section);
            if (section === "analisis") {
                setTimeout(() => {
                    const el = document.getElementById("analisis-ia-pro-section");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 500);
            }
        }
    }, [searchParams]);

    const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
    const [highlightedFileId, setHighlightedFileId] = useState<string | null>(null);
    const [datosObligatoriosOpen, setDatosObligatoriosOpen] = useState(true);
    const [datosOpcionalesOpen, setDatosOpcionalesOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isTagsOpen, setIsTagsOpen] = useState(false);
    const [isLinkClientOpen, setIsLinkClientOpen] = useState(false);
    const [isAddPartOpen, setIsAddPartOpen] = useState(false);
    const [isAddTramiteOpen, setIsAddTramiteOpen] = useState(false);
    const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
    const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
    const [isProcessFileSelectorOpen, setIsProcessFileSelectorOpen] = useState(false);
    const [isLinkDraftingModalOpen, setIsLinkDraftingModalOpen] = useState(false);
    const [myDocuments, setMyDocuments] = useState<any[]>([]);

    // --- Email Module & Single Sender States ---
    const [legalEmails, setLegalEmails] = useState<legalEmailsApi.LegalEmail[]>([]);
    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [selectedEmailDetail, setSelectedEmailDetail] = useState<legalEmailsApi.LegalEmailDetail | null>(null);
    const [isEmailDetailOpen, setIsEmailDetailOpen] = useState(false);
    const [verifiedSenders, setVerifiedSenders] = useState<legalEmailsApi.VerifiedSender[]>([]);
    const [selectedSender, setSelectedSender] = useState<string>("");

    // Compose Form states
    const [emailToAddress, setEmailToAddress] = useState("");
    const [emailToName, setEmailToName] = useState("");
    const [emailToAddressProof, setEmailToAddressProof] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailBodyHtml, setEmailBodyHtml] = useState("<p>Estimados,</p><p>Por medio de la presente...</p>");
    const [emailIsJudicial, setEmailIsJudicial] = useState(true);
    const [emailOathAccepted, setEmailOathAccepted] = useState(false);
    const [emailTermsAccepted, setEmailTermsAccepted] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [emailAttachedFiles, setEmailAttachedFiles] = useState<any[]>([]);

    // Quick verify single sender inside modal
    const [isQuickVerifyOpen, setIsQuickVerifyOpen] = useState(false);
    const [quickSenderEmail, setQuickSenderEmail] = useState("");
    const [quickSenderName, setQuickSenderName] = useState("");
    const [quickSenderOtp, setQuickSenderOtp] = useState("");
    const [quickVerifyingEmail, setQuickVerifyingEmail] = useState<string | null>(null);
    const [isQuickRegistering, setIsQuickRegistering] = useState(false);
    const [isQuickVerifying, setIsQuickVerifying] = useState(false);

    const [loadingMyDocs, setLoadingMyDocs] = useState(false);
    const [storageUsage, setStorageUsage] = useState<any>(null);
    const [loadingStorage, setLoadingStorage] = useState(false);

    const loadStorageUsage = async () => {
        setLoadingStorage(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            const res = await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8001"}/api/v1/user/storage-usage`, {
                headers: {
                    "Authorization": `Bearer ${token || ""}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setStorageUsage(data);
                window.dispatchEvent(new Event("storage-updated"));
            }
        } catch (error) {
            console.error("Error loading storage usage:", error);
        } finally {
            setLoadingStorage(false);
        }
    };
    const [selectedDocId, setSelectedDocId] = useState("");
    const [linkFormat, setLinkFormat] = useState<"docx" | "pdf">("docx");
    const [customFileName, setCustomFileName] = useState("");
    const [isLinkingDraft, setIsLinkingDraft] = useState(false);
    const [selectedClientToLink, setSelectedClientToLink] = useState<string>("");
    const [selectedRoleToLink, setSelectedRoleToLink] = useState<string>("Demandante");
    const [clientRole, setClientRole] = useState<string>("neutral");

    // --- New Item Form States ---
    const [commentText, setCommentText] = useState("");
    const [newPartName, setNewPartName] = useState("");
    const [newPartRole, setNewPartRole] = useState("demandante");
    const [newTramiteTitle, setNewTramiteTitle] = useState("");
    const [newTramiteResponsible, setNewTramiteResponsible] = useState("");
    const [newTramiteEntity, setNewTramiteEntity] = useState("");
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [newTaskDate, setNewTaskDate] = useState("");
    const [newAdelantoAmount, setNewAdelantoAmount] = useState("");
    const [newAdelantoConcept, setNewAdelantoConcept] = useState("");
    const [isAddAdelantoOpen, setIsAddAdelantoOpen] = useState(false);
    const [localComments, setLocalComments] = useState<{ name: string, text: string, date: string }[]>([]);
    type CaseEntry = {
        id: string;
        title: string;
        description?: string | null;
        status?: string | null;
        due_date?: string | null;
        priority?: string | null;
        created_at?: string | null;
    };
    const [caseEntries, setCaseEntries] = useState<CaseEntry[]>([]);

    // Compatibility wrapper for old shadcn toast syntax using sonner
    const toast = (args: any) => {
        if (typeof args === 'string') return sonnerToast(args);
        const { title, description, variant, ...rest } = args;
        if (variant === 'destructive') return sonnerToast.error(title || "Error", { description, ...rest });
        return sonnerToast(title || "Aviso", { description, ...rest });
    };
    const { openDocument } = useDocumentViewer();

    // --- Google Drive Integration ---
    const [openPicker, authResponse] = useDrivePicker();
    const [pickerData, setPickerData] = useState<any>(null);

    useEffect(() => {
        if (pickerData && pickerData.action === 'picked') {
            const handlePickedFile = async () => {
                const pickedFile = pickerData.docs[0];
                const token = authResponse?.access_token;
                if (!token) return;

                try {
                    setIsUploading(true);
                    let downloadUrl = `https://www.googleapis.com/drive/v3/files/${pickedFile.id}?alt=media`;
                    let fileName = pickedFile.name;
                    let fileType = pickedFile.mimeType;

                    // Export native Google formats (like Docs) to PDF
                    if (fileType.startsWith('application/vnd.google-apps')) {
                        downloadUrl = `https://www.googleapis.com/drive/v3/files/${pickedFile.id}/export?mimeType=application/pdf`;
                        fileName = `${fileName}.pdf`;
                        fileType = 'application/pdf';
                    }

                    const res = await fetch(downloadUrl, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (!res.ok) throw new Error("No se pudo descargar el archivo de Google Drive");

                    const blob = await res.blob();
                    const file = new File([blob], fileName, { type: fileType });

                    const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
                    await scraperApi.uploadProcesoFile(radicado, file);
                    toast({ title: "Archivo importado", description: `${fileName} se ha vinculado al expediente.` });
                    loadProcessFiles(radicado);
                } catch (err: any) {
                    toast({ variant: "destructive", title: "Error al importar", description: err.message || "Error descargando archivo desde Google Drive." });
                } finally {
                    setIsUploading(false);
                    setPickerData(null);
                }
            };
            handlePickedFile();
        }
    }, [pickerData, authResponse]);

    // --- Scrapper Integrated State ---
    const [timeline, setTimeline] = useState<scraperApi.Actuacion[]>([]);
    const [analysis, setAnalysis] = useState<scraperApi.AIAnalysisResult | null>(null);
    const [selectedActionForFeedback, setSelectedActionForFeedback] = useState<any | null>(null);
    const [feedbackReason, setFeedbackReason] = useState("");
    const [feedbackRelatedActuacion, setFeedbackRelatedActuacion] = useState("");
    const [feedbackRelatedPrueba, setFeedbackRelatedPrueba] = useState("");
    const [feedbackEtapaProcesal, setFeedbackEtapaProcesal] = useState("Pruebas / Instrucción");
    const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
    const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
    const [userId, setUserId] = useState<string>("anonymous");
    const [userName, setUserName] = useState<string>("Abogado");

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const { data } = await supabase.auth.getUser();
                if (data.user) {
                    setUserId(data.user.id);
                    const name = data.user.user_metadata?.full_name || data.user.email?.split('@')[0] || "Abogado";
                    setUserName(name);
                }
            } catch (err) {
                console.error("Error fetching user for editor:", err);
            }
        };
        fetchUser();
    }, []);

    const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant', content: string, citations?: scraperApi.Citation[] }[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isChatting, setIsChatting] = useState(false);
    const [isDrafting, setIsDrafting] = useState(false);
    const [draftContent, setDraftContent] = useState("");
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
    const [showAllActions, setShowAllActions] = useState(false);
    // --- Pre-generation Validation Modal State ---
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [pendingDraftTask, setPendingDraftTask] = useState<any>(null);
    const [draftValidationData, setDraftValidationData] = useState({
        fecha_actual: "",
        radicado: "",
        despacho: "",
        demandante: "",
        demandado: "",
        clase_proceso: "",
        ciudad: "",
        abogado: "",
        tarjeta_profesional: "",
        direccion_notificacion: "",
        asunto: "",
        objetivo: "",
        instrucciones: ""
    });
    const [missingFieldsList, setMissingFieldsList] = useState<string[]>([]);
    const [emailBody, setEmailBody] = useState("<p>Estimados,</p><p>Por medio de la presente se le notifica sobre el estado del proceso...</p>");
    const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
    const [emailSenderName, setEmailSenderName] = useState("");
    const [isUploadingEmailFile, setIsUploadingEmailFile] = useState(false);
    const [uploadingEmailFileName, setUploadingEmailFileName] = useState("");
    const [localTags, setLocalTags] = useState<string[]>([]);
    const [newTagInput, setNewTagInput] = useState("");
    const [jurisFoundations, setJurisFoundations] = useState<string | null>(null);
    const [draftReferences, setDraftReferences] = useState<any[]>([]);
    const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
    const [isVectorizing, setIsVectorizing] = useState(false);
    const [vectorizingFiles, setVectorizingFiles] = useState<Record<string, boolean>>({});
    const [vectorizedFiles, setVectorizedFiles] = useState<Record<string, boolean>>({});

    // --- AI Artifacts State ---
    const [caseResearch, setCaseResearch] = useState<any[]>([]);
    const [caseContracts, setCaseContracts] = useState<any[]>([]);
    const [caseDrafts, setCaseDrafts] = useState<any[]>([]);
    const [activityTypeFilter, setActivityTypeFilter] = useState<"all" | "investigacion" | "contrato" | "documento">("all");
    const [activityStatusFilter, setActivityStatusFilter] = useState<"all" | "completado" | "en_proceso" | "error">("all");
    const [activityOwnerFilter, setActivityOwnerFilter] = useState<string>("all");
    /** Filter CPNU timeline rows by heuristic estado / traslado electrónico. */
    const [actuacionKindFilter, setActuacionKindFilter] = useState<"all" | "estado" | "traslado">("all");

    useEffect(() => {
        if (highlightedFileId) {
            const scrollTimer = setTimeout(() => {
                let element = document.getElementById(`file-row-${highlightedFileId}`);
                if (!element) {
                    const elements = document.querySelectorAll('[data-doc-url]');
                    element = Array.from(elements).find(
                        (el) => el.getAttribute('data-doc-url') === highlightedFileId
                    ) as HTMLElement | null;
                }
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 150);

            const timer = setTimeout(() => {
                setHighlightedFileId(null);
            }, 4000);

            return () => {
                clearTimeout(scrollTimer);
                clearTimeout(timer);
            };
        }
    }, [highlightedFileId]);

    const getEntryKind = (entry: CaseEntry): "tramite" | "tarea" | "adelanto" | "parte" => {
        const d = String(entry.description || "").toLowerCase();
        if (d.includes("[adelanto]")) return "adelanto";
        if (d.includes("[parte]")) return "parte";
        if (d.includes("[tramite]")) return "tramite";
        return "tarea";
    };

    const caseTramites = useMemo(() => caseEntries.filter((e) => getEntryKind(e) === "tramite"), [caseEntries]);
    const caseTareas = useMemo(() => caseEntries.filter((e) => getEntryKind(e) === "tarea"), [caseEntries]);
    const caseAdelantos = useMemo(() => caseEntries.filter((e) => getEntryKind(e) === "adelanto"), [caseEntries]);
    const casePartes = useMemo(() => caseEntries.filter((e) => getEntryKind(e) === "parte"), [caseEntries]);

    const loadCaseEntries = async (caseId?: string) => {
        if (!caseId) {
            setCaseEntries([]);
            return;
        }
        const { data, error } = await supabase
            .from("legal_tasks")
            .select("*")
            .eq("case_id", caseId)
            .order("created_at", { ascending: false });
        if (error) {
            console.error("Error loading case entries:", error);
            return;
        }
        setCaseEntries((data || []) as CaseEntry[]);
    };

    useEffect(() => {
        loadCaseEntries(process?.id);
    }, [process?.id]);

    useEffect(() => {
        if (!process?.id) return;

        const fetchArtifacts = async () => {
            try {
                const [{ data: res }, { data: con }, { data: draf }] = await Promise.all([
                    supabase.from('legal_research_searches_v2').select('*').eq('case_id', process.id).order('created_at', { ascending: false }),
                    supabase.from('contract_reviews_v2').select('*').eq('case_id', process.id).order('created_at', { ascending: false }),
                    supabase.from('document_drafts_v2').select('*').eq('case_id', process.id).order('created_at', { ascending: false })
                ]);
                if (res) setCaseResearch(res);
                if (con) setCaseContracts(con);
                if (draf) setCaseDrafts(draf);
            } catch (err) {
                console.error("Error fetching AI artifacts", err);
            }
        };
        fetchArtifacts();
    }, [process?.id]);

    // --- Jodit Memoized Config ---
    const editorRef = useRef(null);
    const editorConfig = useMemo(() => ({
        readonly: false,
        height: 400,
        placeholder: "Escribe el cuerpo del mensaje...",
        toolbarButtonSize: "middle" as "middle",
        buttons: ["bold", "italic", "underline", "|", "ul", "ol", "|", "font", "fontsize", "paragraph", "|", "table", "link", "|", "align", "undo", "redo"],
        showCharsCounter: false,
        showWordsCounter: false,
        showStatusbar: false,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: "insert_clear_html" as "insert_clear_html"
    }), []);

    const [scrapperMetadata, setScrapperMetadata] = useState<scraperApi.ScrapperProceso | null>(null);
    const isPaused = (scrapperMetadata?.watch_status || "").toLowerCase() === "paused" || scrapperMetadata?.is_watched === false;
    const [processFiles, setProcessFiles] = useState<scraperApi.ProcesoArchivo[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    const [formData, setFormData] = useState<{
        denominacion: string;
        numero: string;
        tipoProceso: string;
        grupos: string[];
        estado: string;
        descripcion: string;
        juzgado: string;
        jurisdiccion: string;
        cuantia: string;
        fechaInicio: string;
        fechaRadicacion: string;
        instancia: string;
        ponente: string;
        clase: string;
        subclase: string;
        recurso: string;
        naturaleza: string;
        medidaCautelar: string;
        despacho?: string;
        demandante?: string;
        demandado?: string;
        ciudad?: string;
        abogado?: string;
        tarjeta_profesional?: string;
        direccion?: string;
        fuente?: string;
        tipo_documento?: string;
        nit_cedula?: string;
        anho?: string;
        delegatura?: string;
        nit_sociedad?: string;
        razon_social?: string;
    }>({
        denominacion: "",
        numero: "",
        tipoProceso: "administrativo",
        grupos: ["DESPACHO"],
        estado: "active",
        descripcion: "",
        juzgado: "",
        jurisdiccion: "",
        cuantia: "",
        fechaInicio: "",
        fechaRadicacion: "",
        instancia: "primera",
        ponente: "",
        clase: "",
        subclase: "",
        recurso: "",
        naturaleza: "",
        medidaCautelar: "",
        fuente: "RAMA",
        tipo_documento: "CC",
        nit_cedula: "",
        anho: new Date().getFullYear().toString(),
        delegatura: "",
        nit_sociedad: "",
        razon_social: "",
    });

    const DEFAULT_TIPOS_PROCESO = ["administrativo", "judicial", "extrajudicial", "tutela", "laboral", "penal", "civil"];
    const [customTiposProceso, setCustomTiposProceso] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem("custom_tipos_proceso");
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const [isAddingNewTipo, setIsAddingNewTipo] = useState(false);
    const [newTipoInputValue, setNewTipoInputValue] = useState("");

    const [newGrupoInput, setNewGrupoInput] = useState("");

    const handleAddGrupo = () => {
        if (!newGrupoInput.trim()) return;
        const tag = newGrupoInput.trim().toUpperCase();
        if (!formData.grupos.includes(tag)) {
            setFormData(prev => ({ ...prev, grupos: [...prev.grupos, tag] }));
        }
        setNewGrupoInput("");
    };

    const handleRemoveGrupo = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            grupos: prev.grupos.filter(tag => tag !== tagToRemove)
        }));
    };

    useEffect(() => {
        if (process) {
            const meta = (process as any).metadata || {};
            const detectedFuente = (process as any).origen || (process as any).fuente || meta.origen || meta.fuente || (
                (process.case_number || "").includes("-01-") ? "SUPERSOCIEDADES" :
                ((process.case_number || "").includes("-") && (process.case_number || "").length <= 11) ? "SIC" : "RAMA"
            );
            setFormData(prev => ({
                ...prev,
                denominacion: process.title || "",
                numero: process.case_number || "",
                estado: (process as any).vigente || process.status || meta.vigente || meta.estado || "VIGENTE",
                descripcion: process.description || meta.description || "",
                tipoProceso: (process as any).case_type || (process as any).tipo_proceso || "judicial",
                juzgado: process.juzgado || meta.juzgado || "",
                jurisdiccion: (process as any).jurisdiccion || meta.jurisdiccion || "",
                cuantia: (process as any).cuantia || meta.cuantia || "",
                fechaInicio: (process as any).fechaInicio || meta.fecha_inicio || "",
                fechaRadicacion: (process as any).fechaRadicacion || meta.fecha_radicacion || "",
                instancia: (process as any).instancia || meta.instancia || "primera",
                ponente: process.ponente || meta.ponente || "",
                clase: process.clase || meta.clase || "",
                subclase: (process as any).subclase || meta.subclase || "",
                recurso: (process as any).recurso || meta.recurso || "",
                naturaleza: (process as any).naturaleza || meta.naturaleza || "",
                medidaCautelar: (process as any).medidaCautelar || meta.medida_cautelar || "",
                grupos: (process as any).grupos || meta.grupos || ["DESPACHO"],
                fuente: detectedFuente,
                tipo_documento: meta.tipo_documento || prev.tipo_documento || "CC",
                nit_cedula: meta.nit_cedula || meta.cedula || meta.nit || prev.nit_cedula || "",
                anho: meta.anho || meta.year || prev.anho || "",
                delegatura: meta.delegatura || prev.delegatura || "",
                nit_sociedad: meta.nit_sociedad || meta.nit || prev.nit_sociedad || "",
                razon_social: meta.razon_social || prev.razon_social || "",
            }));

            if (process.tags) {
                setLocalTags(process.tags);
            }

            // Fetch Scrapper Data if it's a tracked process (radicado looks like valid identifier)
            const radicado = process.case_number?.replace(/\s/g, "");
            if (radicado && radicado.length >= 4) {
                loadScrapperData(radicado);
            }
            // Cargar historial de correos vinculados al expediente
            loadProcessEmails();
        } else if (id && !isUuid(id) && id.replace(/\s/g, "").length >= 4) {
            // Fallback: fetch from scrapper if not in local DB but has a radicado ID
            const radicado = id.replace(/\s/g, "");
            loadScrapperData(radicado);
            setFormData(prev => ({
                ...prev,
                numero: radicado,
                denominacion: prev.denominacion || `Proceso ${radicado}`,
            }));
            loadProcessEmails();
        }
    }, [process, id]);


    const loadScrapperData = async (radicado: string) => {
        if (!radicado || isUuid(radicado) || radicado.length < 4) return;
        setIsLoadingTimeline(true);

        // 1. Fetch Timeline / Actuaciones
        try {
            let history: scraperApi.Actuacion[] = [];
            if (scraperApi.UNIFIED_CASE_EVENTS_ENABLED) {
                try {
                    history = await scraperApi.fetchCaseEvents(radicado, 500);
                } catch {
                    history = await scraperApi.fetchTimeline(radicado);
                }
            } else {
                history = await scraperApi.fetchTimeline(radicado);
            }
            setTimeline(history || []);
        } catch (errTimeline) {
            console.error("Error fetching timeline for radicado:", radicado, errTimeline);
        }

        // 2. Fetch Process Metadata
        try {
            const metadata = await scraperApi.fetchScrapperProceso(radicado);
            if (metadata) {
                if (metadata.metadata && typeof metadata.metadata === 'string') {
                    try {
                        metadata.metadata = JSON.parse(metadata.metadata);
                    } catch (e) {
                        console.error("Error parsing process metadata JSON:", e);
                    }
                }

                setScrapperMetadata(metadata);

                const sMeta = (metadata as any).metadata || {};
                const origenDetected = metadata.origen || metadata.fuente || sMeta.origen || sMeta.fuente || "";

                setFormData(prev => ({
                    ...prev,
                    denominacion: (prev.denominacion && !prev.denominacion.startsWith("Cargando") && prev.denominacion !== `Proceso ${radicado}`)
                        ? prev.denominacion
                        : (metadata.asunto || metadata.clase || metadata.tipo_proceso || `Proceso ${metadata.numero_proceso || radicado}`),
                    numero: metadata.numero_proceso || prev.numero || radicado,
                    tipoProceso: metadata.tipo_proceso || (metadata as any).metadata?.["Tipo de Proceso"] || prev.tipoProceso || "judicial",
                    juzgado: prev.juzgado || metadata.sala_conoce || (metadata as any).metadata?.Despacho || metadata.ubicacion || "",
                    ponente: metadata.ponente || (metadata as any).metadata?.Ponente || prev.ponente || "",
                    clase: metadata.clase || (metadata as any).metadata?.["Clase de Proceso"] || prev.clase || "",
                    subclase: metadata.subclase || (metadata as any).metadata?.Subclase || prev.subclase || "",
                    recurso: metadata.recurso || (metadata as any).metadata?.Recurso || prev.recurso || "",
                    naturaleza: metadata.naturaleza || (metadata as any).metadata?.Naturaleza || prev.naturaleza || "",
                    medidaCautelar: metadata.medida_cautelar || (metadata as any).metadata?.["Medida Cautelar"] || prev.medidaCautelar || "",
                    estado: metadata.vigente || (metadata as any).metadata?.vigente || prev.estado || "VIGENTE",
                    fuente: prev.fuente || origenDetected || "RAMA",
                    tipo_documento: sMeta.tipo_documento || prev.tipo_documento || "CC",
                    nit_cedula: sMeta.nit_cedula || sMeta.cedula || sMeta.nit || prev.nit_cedula || "",
                    anho: sMeta.anho || sMeta.year || prev.anho || "",
                    delegatura: sMeta.delegatura || (metadata as any).delegatura || prev.delegatura || "",
                    nit_sociedad: sMeta.nit_sociedad || prev.nit_sociedad || "",
                    razon_social: sMeta.razon_social || prev.razon_social || "",
                }));

                if (metadata.analisis_ia) {
                    if (typeof metadata.analisis_ia === 'string') {
                        try {
                            setAnalysis(JSON.parse(metadata.analisis_ia));
                        } catch (e) {
                            console.error("Error parsing stored analysis:", e);
                        }
                    } else {
                        setAnalysis(metadata.analisis_ia as scraperApi.AIAnalysisResult);
                    }
                }
            }
        } catch (errMetadata) {
            console.error("Error fetching process metadata for radicado:", radicado, errMetadata);
        }

        // 3. Fetch Process Files
        try {
            await loadProcessFiles(radicado);
        } catch (errFiles) {
            console.error("Error loading process files:", errFiles);
        }

        setIsLoadingTimeline(false);

        // Cleanup placeholder title if still set
        setFormData(prev => ({
            ...prev,
            denominacion: (prev.denominacion && prev.denominacion.startsWith("Cargando"))
                ? `Proceso ${radicado}`
                : (prev.denominacion || `Proceso ${radicado}`)
        }));
    };

    // Auto-polling for new actuaciones
    useEffect(() => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || radicado.length < 10 || isPaused) return;

        const interval = setInterval(async () => {
            try {
                let history: scraperApi.Actuacion[] = [];
                if (scraperApi.UNIFIED_CASE_EVENTS_ENABLED) {
                    try {
                        history = await scraperApi.fetchCaseEvents(radicado, 500);
                    } catch {
                        history = await scraperApi.fetchTimeline(radicado);
                    }
                } else {
                    history = await scraperApi.fetchTimeline(radicado);
                }

                setTimeline(prev => {
                    // Only alert if we already had data loaded, and now we have MORE data.
                    if (prev.length > 0 && history.length > prev.length) {
                        const newCount = history.length - prev.length;
                        toast({
                            title: "¡Actualización Judicial!",
                            description: `Se han detectado ${newCount} nuevas actuaciones/traslados en el portal judicial. La línea de tiempo ha sido actualizada.`,
                            className: "bg-emerald-50 border-emerald-200 text-emerald-800"
                        });
                        return history;
                    } else if (history.length !== prev.length) {
                        // Just silently update if they were different for some reason
                        return history;
                    }
                    return prev;
                });
            } catch (err) {
                console.error("Polling error for timeline", err);
            }
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [formData.numero, process?.case_number, id, toast]);;

    const uniqueTimeline = useMemo<scraperApi.Actuacion[]>(() => {
        const seen = new Set<string>();
        return timeline.filter(item => {
            const key = `${item.fecha}-${item.actuacion}-${item.descripcion || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [timeline]);

    const timelineEntries = useMemo(
        () =>
            uniqueTimeline.map((item, idx) => ({
                item,
                idx,
                cat: classifyActuacion(item.actuacion, item.descripcion),
            })),
        [uniqueTimeline]
    );

    const actuacionKindStats = useMemo(() => {
        let estado = 0;
        let traslado = 0;
        let otro = 0;
        for (const row of timelineEntries) {
            if (row.cat.kind === "estado") estado += 1;
            else if (row.cat.kind === "traslado") traslado += 1;
            else otro += 1;
        }
        return { estado, traslado, otro, total: uniqueTimeline.length };
    }, [timelineEntries, uniqueTimeline.length]);

    const filteredTimelineEntries = useMemo(() => {
        if (actuacionKindFilter === "all") return timelineEntries;
        return timelineEntries.filter((row) => row.cat.kind === actuacionKindFilter);
    }, [timelineEntries, actuacionKindFilter]);

    const sortedActuaciones = useMemo(() => {
        const mapped = uniqueTimeline.map((item, index) => ({ item, index }));
        mapped.sort((a, b) => {
            const dateA = a.item.fecha || "";
            const dateB = b.item.fecha || "";
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return b.index - a.index; // Higher original index (older) comes first
        });
        return mapped.map((m) => m.item);
    }, [uniqueTimeline]);

    /** Match processFiles to an actuacion by actuacion_id first, then embedded archivos, then by token/text matching. */
    const getFilesForActuacion = (item: scraperApi.Actuacion): scraperApi.ProcesoArchivo[] => {
        if (!item) return [];

        // 1. Direct match by actuacion_id foreign key in processFiles
        if (item.id && processFiles && processFiles.length > 0) {
            const matchesByActId = processFiles.filter((f: any) => f.actuacion_id === item.id);
            if (matchesByActId.length > 0) {
                return matchesByActId;
            }
        }

        // 2. Direct embedded archivos on actuacion object (from timeline API)
        if ((item as any).archivos && Array.isArray((item as any).archivos) && (item as any).archivos.length > 0) {
            return (item as any).archivos;
        }

        if (!processFiles || !processFiles.length) return [];

        // 3. Fallback: match by filename token similarity / keywords in actuacion text
        const actText = ((item.actuacion || "") + " " + (item.descripcion || "")).toLowerCase();
        const actTokens = actText.split(/[\s,.:;|()\-\/]+/).filter(t => t.length >= 4);

        return processFiles.filter(f => {
            const fileName = f.file_name || "";
            const fname = fileName.toLowerCase().replace(/[\d_\-.]/g, " ").trim();
            const fTokens = fname.split(/\s+/).filter(t => t.length >= 4);
            if (fTokens.length > 0 && fTokens.some(ft => actTokens.some(at => at.includes(ft) || ft.includes(at)))) {
                return true;
            }
            const nameNoExt = fileName.replace(/\.[^.]+$/, "").toLowerCase();
            if (nameNoExt.length >= 5 && actText.includes(nameNoExt)) return true;
            return false;
        });
    };

    /** Format file date preferring the actual Rama Judicial / actuacion date over the upload date. */
    const getFileDisplayDate = (file: scraperApi.ProcesoArchivo): string => {
        // 1. Direct date property on file object if populated by DB/API
        const directDate = (file as any).fecha_actuacion || (file as any).fecha_documento || (file as any).fecha_providencia || (file as any).fecha;
        if (directDate) {
            const d = new Date(directDate);
            if (!isNaN(d.getTime())) return d.toLocaleDateString();
            return String(directDate);
        }

        // 2. Match with actuacion in timeline / sortedActuaciones
        if (sortedActuaciones && sortedActuaciones.length > 0) {
            const matchingActuacion = sortedActuaciones.find(act => {
                const linked = getFilesForActuacion(act);
                if (linked.some(f => f.id === file.id || f.file_name === file.file_name)) return true;
                const actDoc = act.document_url || act.anexo_path || "";
                if (actDoc && (actDoc.includes(file.file_name) || (file.file_path && actDoc.includes(file.file_path)))) {
                    return true;
                }
                return false;
            });

            if (matchingActuacion?.fecha) {
                const actDateStr = matchingActuacion.fecha;
                const parsed = new Date(actDateStr);
                if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString();
                return actDateStr;
            }

            // Check numerical prefix index match (e.g., "04_SENTENCIA.pdf" -> index 4 in sortedActuaciones)
            const matchNum = (file.file_name || "").match(/^0*([1-9]\d*)/);
            if (matchNum) {
                const idx = parseInt(matchNum[1], 10) - 1;
                if (idx >= 0 && idx < sortedActuaciones.length && sortedActuaciones[idx]?.fecha) {
                    const actDateStr = sortedActuaciones[idx].fecha;
                    const parsed = new Date(actDateStr);
                    if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString();
                    return actDateStr;
                }
            }
        }

        // 3. Extract date from file_name if present (e.g. 2024-05-15 or 15-05-2024 or 20240515)
        const name = file.file_name || "";
        const isoMatch = name.match(/\b(20\d{2})[-_/.](0[1-9]|1[0-2])[-_/.](0[1-9]|[12]\d|3[01])\b/);
        if (isoMatch) {
            const [_, y, m, d] = isoMatch;
            const parsed = new Date(`${y}-${m}-${d}`);
            if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString();
        }

        const latamMatch = name.match(/\b(0[1-9]|[12]\d|3[01])[-_/.](0[1-9]|1[0-2])[-_/.]*(20\d{2})\b/);
        if (latamMatch) {
            const [_, d, m, y] = latamMatch;
            const parsed = new Date(`${y}-${m}-${d}`);
            if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString();
        }

        const compactMatch = name.match(/\b(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\b/);
        if (compactMatch) {
            const [_, y, m, d] = compactMatch;
            const parsed = new Date(`${y}-${m}-${d}`);
            if (!isNaN(parsed.getTime())) return parsed.toLocaleDateString();
        }

        // 4. Fallback to created_at
        if (file.created_at) {
            const d = new Date(file.created_at);
            if (!isNaN(d.getTime())) return d.toLocaleDateString();
        }

        return "—";
    };

    const checkVectorizedFiles = async (radicado: string) => {
        try {
            const { data, error } = await supabase
                .from("document_chunks")
                .select("metadata")
                .eq("proceso_id", radicado);
            if (error) throw error;

            const vectorizedMap: Record<string, boolean> = {};
            if (data) {
                data.forEach((chunk: any) => {
                    const source = chunk.metadata?.source || chunk.metadata?.filename;
                    if (source) {
                        vectorizedMap[source] = true;
                    }
                });
            }
            setVectorizedFiles(vectorizedMap);
        } catch (err) {
            console.error("Error checking vectorized files:", err);
        }
    };

    const handleVectorizeFile = async (fileName: string) => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || !fileName) return;

        setVectorizingFiles(prev => ({ ...prev, [fileName]: true }));
        try {
            await scraperApi.vectorizeProcesoFile(radicado, fileName);
            toast({
                title: "Archivo vectorizado",
                description: `El archivo ${fileName} ha sido vectorizado y cargado a la base de conocimientos.`
            });
            await checkVectorizedFiles(radicado);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error al vectorizar",
                description: error.message || "No se pudo vectorizar el archivo."
            });
        } finally {
            setVectorizingFiles(prev => ({ ...prev, [fileName]: false }));
        }
    };

    const loadProcessFiles = async (radicado: string) => {
        try {
            const files = await scraperApi.getProcesoFiles(radicado);
            const uniqueFiles: scraperApi.ProcesoArchivo[] = [];
            const seenKeys = new Set<string>();
            (files || []).forEach(f => {
                const key = (f.file_name || f.file_path || f.id).toLowerCase();
                if (!seenKeys.has(key)) {
                    seenKeys.add(key);
                    uniqueFiles.push(f);
                }
            });
            setProcessFiles(uniqueFiles);
            checkVectorizedFiles(radicado);
            loadStorageUsage();
        } catch (error) {
            console.error("Error loading process files:", error);
        }
    };

    const getFileUrl = (file: any) => {
        if (!file?.file_path) return "";
        const { data } = supabase.storage
            .from("proceso_files")
            .getPublicUrl(file.file_path);
        return data?.publicUrl || "";
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;
        const filesArray = Array.from(fileList);
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) return;

        setIsUploading(true);
        try {
            if (filesArray.length === 1) {
                await scraperApi.uploadProcesoFile(radicado, filesArray[0]);
                toast({ title: "Archivo subido", description: `${filesArray[0].name} se ha vinculado al expediente.` });
            } else {
                await scraperApi.uploadProcesoFiles(radicado, filesArray);
                toast({ title: "Archivos subidos", description: `${filesArray.length} archivos se han vinculado al expediente.` });
            }
            loadProcessFiles(radicado);
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error al subir",
                description: error.message || "No se pudieron cargar los archivos."
            });
        } finally {
            setIsUploading(false);
            if (e.target) e.target.value = ''; // Reset input
        }
    };

    const handleFileDelete = async (archivoId: string) => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        try {
            await scraperApi.deleteProcesoFile(archivoId);
            toast({ title: "Archivo eliminado", description: "El documento ha sido removido del expediente." });
            if (radicado) loadProcessFiles(radicado);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "No se pudo eliminar el archivo." });
        }
    };

    const loadMyDocuments = async () => {
        setLoadingMyDocs(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            const res = await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8001"}/api/v1/my-documents?limit=50`, {
                headers: {
                    "Authorization": `Bearer ${token || ""}`
                }
            });
            if (!res.ok) throw new Error("No se pudieron cargar los documentos redactados.");
            const data = await res.json();
            setMyDocuments(data.documents || []);
        } catch (error: any) {
            console.error("Error loading my documents:", error);
            toast({ variant: "destructive", title: "Error", description: error.message || "Error al cargar tus documentos redactados." });
        } finally {
            setLoadingMyDocs(false);
        }
    };

    const handleLinkDraftingDocument = async () => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) {
            toast({ variant: "destructive", title: "Error", description: "No se encontró el radicado del proceso." });
            return;
        }
        const selectedDoc = myDocuments.find(d => d.document_id === selectedDocId);
        if (!selectedDoc) {
            toast({ variant: "destructive", title: "Error", description: "Por favor, selecciona un documento." });
            return;
        }

        const fileName = (customFileName.trim() || selectedDoc.document_type || "Documento_Redactado").replace(/\s+/g, "_");
        const finalFileName = fileName.endsWith(`.${linkFormat}`) ? fileName : `${fileName}.${linkFormat}`;

        setIsLinkingDraft(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;

            const scrapperUrl = import.meta.env.VITE_SCRAPPER_API_URL || "http://localhost:8082";
            const exportRes = await fetch(`${scrapperUrl}/api/procesos/${radicado}/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "ngrok-skip-browser-warning": "true",
                    "Authorization": `Bearer ${token || ""}`
                },
                body: JSON.stringify({
                    content: selectedDoc.content_text,
                    format: linkFormat,
                    title: fileName
                })
            });

            if (!exportRes.ok) {
                const errText = await exportRes.text().catch(() => "Unknown error");
                throw new Error(`Error en la generación del documento (${exportRes.status}): ${errText}`);
            }

            const blob = await exportRes.blob();

            const mimeType = linkFormat === "pdf"
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            const fileToUpload = new File([blob], finalFileName, { type: mimeType });

            await scraperApi.uploadProcesoFile(radicado, fileToUpload);

            toast({ title: "Documento vinculado", description: `El documento "${finalFileName}" ha sido vinculado exitosamente a este expediente.` });
            setIsLinkDraftingModalOpen(false);

            loadProcessFiles(radicado);
        } catch (error: any) {
            console.error("Error linking drafting document:", error);
            toast({
                variant: "destructive",
                title: "Error al vincular",
                description: error.message || "Ocurrió un error inesperado al vincular el documento."
            });
        } finally {
            setIsLinkingDraft(false);
        }
    };

    const handleDeepSync = async () => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) return;

        setIsVectorizing(true);
        try {
            await scraperApi.vectorizeProcess(radicado);
            toast({
                title: "Deep Sync Iniciado",
                description: "Se están sincronizando todas las actuaciones y vectorizando anexos en segundo plano."
            });
            // Reload after a delay or just let the worker do its thing
            setTimeout(() => loadScrapperData(radicado), 3000);
        } catch (error) {
            toast({ variant: "destructive", title: "Error en Deep Sync", description: "No se pudo iniciar la sincronización profunda." });
        } finally {
            setIsVectorizing(false);
        }
    };

    const handleRunAnalysis = async () => {
        if (!hasProAccess) {
            sonnerToast.error("Acceso restringido", {
                description: "La Estrategia Legal IA solo está disponible para usuarios con suscripción Profesional o Empresarial."
            });
            return;
        }

        const hasNoData = timeline.length === 0 && processFiles.length === 0;
        if (hasNoData) {
            sonnerToast.error("Sin información para analizar", {
                description: "No se puede ejecutar la Estrategia Legal IA porque el expediente no cuenta con actuaciones ni archivos cargados."
            });
            return;
        }

        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || radicado.length < 10) return;

        // Descontar créditos para "analyze_legal_process" (5 créditos)
        try {
            const api = createLegalAIAssistantClient(import.meta.env.VITE_BACKEND_URL || "http://localhost:8001");
            await api.checkAndDeductCredits("analyze_legal_process");
        } catch (credErr: any) {
            sonnerToast.error("Créditos Insuficientes", {
                description: credErr.message || "No tienes suficientes créditos para realizar la Estrategia Legal IA (requiere 5 créditos)."
            });
            return;
        }

        const targetProcessId = process?.id || id || radicado;

        const navigateToAnalisisSection = () => {
            navigate(`/dashboard/despachos/procesos/${targetProcessId}?section=analisis`);
            setActiveSection("analisis");
            setTimeout(() => {
                const el = document.getElementById("analisis-ia-pro-section");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        };

        // 1. Toast notificando que la generación toma tiempo
        sonnerToast.info("⚡ Generando Estrategia Legal IA", {
            description: "Este análisis procesa el expediente completo y toma su tiempo. Te notificaremos apenas finalice.",
            duration: 12000,
            action: {
                label: "Ver Sección",
                onClick: navigateToAnalisisSection
            }
        });

        setIsAnalyzing(true);
        try {
            const result = await scraperApi.analyzeProcess(radicado, clientRole);
            if (result.analysis) {
                setAnalysis(result.analysis as scraperApi.AIAnalysisResult);

                // 2. Toast clicleable notificando la finalización
                sonnerToast.success("✨ ¡Estrategia Legal IA Completada!", {
                    description: "Se generó la matriz de riesgos y estrategia procesal exitosamente. Haz clic para ver los resultados.",
                    duration: 15000,
                    action: {
                        label: "Ir a la Estrategia",
                        onClick: navigateToAnalisisSection
                    }
                });
            }
        } catch (error: any) {
            sonnerToast.error("Error en el análisis", {
                description: error.message || "No se pudo completar el análisis de IA o tus créditos son insuficientes."
            });
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleSendMessage = async () => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || !chatInput.trim()) return;

        const userMsg = { role: 'user' as const, content: chatInput };
        setChatMessages(prev => [...prev, userMsg]);
        setChatInput("");
        setIsChatting(true);

        try {
            const res = await scraperApi.chatWithAnexos(radicado, userMsg.content);
            setChatMessages(prev => [...prev, {
                role: 'assistant',
                content: res.answer,
                citations: res.citations
            }]);
        } catch (error) {
            toast({ variant: "destructive", title: "Error en el chat", description: "No se pudo obtener respuesta del asistente legal." });
        } finally {
            setIsChatting(false);
        }
    };

    const openValidationModal = (task: any) => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) {
            toast({ variant: "destructive", title: "Radicado no disponible", description: "No hay un número de radicado asociado al proceso." });
            return;
        }

        const formattedDate = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
        const initialData = {
            fecha_actual: formattedDate,
            radicado: radicado,
            despacho: formData.despacho || formData.juzgado || process?.ubicacion || process?.despacho || "",
            demandante: formData.demandante || process?.demandante || "",
            demandado: formData.demandado || process?.demandado || "",
            clase_proceso: formData.clase || process?.clase || process?.tipo_proceso || "",
            ciudad: formData.ciudad || process?.ciudad || "",
            abogado: userName || formData.abogado || "Dr. Abogado Apoderado",
            tarjeta_profesional: formData.tarjeta_profesional || "",
            direccion_notificacion: formData.direccion || process?.direccion || "",
            asunto: task.titulo || task.accion || "Escrito Judicial",
            objetivo: task.descripcion || task.objetivo || "Generar memorial formal respetando las actuaciones del proceso.",
            instrucciones: ""
        };

        const missing: string[] = [];
        if (!initialData.demandante) missing.push("demandante");
        if (!initialData.demandado) missing.push("demandado");
        if (!initialData.despacho) missing.push("despacho");
        if (!initialData.tarjeta_profesional) missing.push("tarjeta_profesional");
        if (!initialData.direccion_notificacion) missing.push("direccion_notificacion");

        setPendingDraftTask(task);
        setDraftValidationData(initialData);
        setMissingFieldsList(missing);
        setIsValidationModalOpen(true);
    };

    const handleGenerateDraft = (task: any) => {
        openValidationModal(task);
    };

    const handleConfirmAndGenerateDraft = async () => {
        if (!pendingDraftTask) return;
        setIsValidationModalOpen(false);

        const radicado = draftValidationData.radicado || (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) return;

        setIsDrafting(true);
        setIsDraftModalOpen(true);
        setDraftContent("");
        setJurisFoundations(null);
        setDraftReferences([]);

        try {
            // Deduct credits for "generate_strategic_draft" (4 credits)
            try {
                const api = createLegalAIAssistantClient(import.meta.env.VITE_BACKEND_URL || "http://localhost:8001");
                await api.checkAndDeductCredits("generate_strategic_draft");
            } catch (credErr: any) {
                toast({
                    variant: "destructive",
                    title: "Créditos Insuficientes",
                    description: credErr.message || "No tienes suficientes créditos para generar el borrador estratégico (requiere 4 créditos)."
                });
                setIsDrafting(false);
                setIsDraftModalOpen(false);
                return;
            }

            const actionLabel = draftValidationData.asunto || pendingDraftTask.titulo || pendingDraftTask.accion;
            const objectiveLabel = draftValidationData.objetivo || pendingDraftTask.descripcion || pendingDraftTask.objetivo;

            const res = await scraperApi.generateDraft(radicado, actionLabel, objectiveLabel, analysis, {
                fecha_actual: draftValidationData.fecha_actual,
                form_data: draftValidationData,
                missing_fields: missingFieldsList
            });
            setDraftContent(res.draft);
            setJurisFoundations(res.jurisprudencia_foundations || null);
            setDraftReferences(res.references || []);
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "No se pudo generar el borrador." });
            setIsDraftModalOpen(false);
        } finally {
            setIsDrafting(false);
        }
    };

    const handleExport = async (format: 'docx' | 'pdf') => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || !draftContent) return;

        try {
            await scraperApi.exportDocument(radicado, draftContent, format, "Escrito_Judicial");
            toast({ title: "Éxito", description: `Documento ${format.toUpperCase()} generado correctamente.` });
        } catch (error) {
            toast({ variant: "destructive", title: "Error", description: "No se pudo exportar el documento." });
        }
    };

    const handleAcceptSuggestion = async (task: any) => {
        try {
            let currentProcessId = process?.id;

            if (!currentProcessId) {
                const radicado = (formData.numero || id || "").replace(/\s/g, "");
                if (!radicado) {
                    toast({ variant: "destructive", title: "Error", description: "No hay un expediente asociado para crear la tarea." });
                    return;
                }

                const newProc = await addProcess({
                    title: formData.denominacion || "Expediente " + radicado,
                    case_number: radicado,
                    description: formData.descripcion || "Proceso bajo vigilancia judicial.",
                    status: "active",
                    client_name: ""
                });

                if (newProc && newProc.id) {
                    currentProcessId = newProc.id;
                } else {
                    throw new Error("No se pudo registrar el proceso base para la tarea.");
                }
            }

            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: currentProcessId,
                title: task.titulo || task.accion,
                description: `${task.descripcion || task.objetivo}\n\nJustificación: ${task.justificacion || "Sugerencia de IA Defensiva"}`,
                status: "pending",
                priority: task.prioridad === "alta" ? "high" : "medium",
                due_date: new Date(Date.now() + (task.plazo_dias || 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(currentProcessId);
            toast({ title: "Tarea aceptada", description: "La recomendación se ha convertido en una tarea formal." });
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error", description: error.message || "No se pudo crear la tarea." });
        }
    };

    const handleFeedbackSubmit = async () => {
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado || !selectedActionForFeedback || !feedbackReason.trim()) {
            toast({ variant: "destructive", title: "Error", description: "Por favor describe el motivo por el cual no aplica la acción." });
            return;
        }

        setIsSubmittingFeedback(true);
        try {
            const res = await scraperApi.submitActionFeedback(radicado, {
                rejected_action: selectedActionForFeedback,
                feedback_reason: feedbackReason,
                related_actuacion: feedbackRelatedActuacion || undefined,
                related_prueba: feedbackRelatedPrueba || undefined,
                etapa_procesal: feedbackEtapaProcesal || undefined,
            });

            if (res.status === "success" && res.new_action) {
                // Update local state analysis.tareas_estrategicas
                if (analysis) {
                    const updatedTareas = (analysis.tareas_estrategicas || []).map((t: any) => {
                        const tTitle = t.titulo || t.accion;
                        const selTitle = selectedActionForFeedback.titulo || selectedActionForFeedback.accion;
                        if (tTitle === selTitle) {
                            return res.new_action; // Swap rejected action with the new one
                        }
                        return t;
                    });
                    const updatedAnalysis = {
                        ...analysis,
                        tareas_estrategicas: updatedTareas
                    };
                    setAnalysis(updatedAnalysis);

                    // Persist updated analysis in Supabase procesos table
                    try {
                        const { error } = await supabase
                            .from("procesos")
                            .update({ analisis_ia: updatedAnalysis })
                            .eq("numero_proceso", radicado);
                        if (error) throw error;
                    } catch (dbErr: any) {
                        console.error("Error saving updated analysis to Supabase:", dbErr);
                    }
                }

                toast({ title: "Acción personalizada generada", description: "La IA ha registrado tu preferencia y adaptado el plan estratégico." });
                setIsFeedbackDialogOpen(false);
                // Clear fields
                setFeedbackReason("");
                setFeedbackRelatedActuacion("");
                setFeedbackRelatedPrueba("");
                setSelectedActionForFeedback(null);
            } else {
                throw new Error("No se pudo generar la nueva acción.");
            }
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error al generar acción", description: error.message || "Hubo un problema al contactar con el asistente legal." });
        } finally {
            setIsSubmittingFeedback(false);
        }
    };

    // ── Email Module Functions ────────────────────────────────────────────────

    const loadProcessEmails = async () => {
        if (!process?.id) return;
        setIsLoadingEmails(true);
        try {
            const [emails, senders] = await Promise.all([
                legalEmailsApi.getProcessEmails(process.id),
                legalEmailsApi.getVerifiedSenders()
            ]);
            setLegalEmails(emails);
            setVerifiedSenders(senders);

            // Seleccionar remitente por defecto
            if (senders.length > 0) {
                const verified = senders.find(s => s.is_verified) || senders[0];
                setSelectedSender(verified.email);
                if (!emailSenderName) {
                    setEmailSenderName(verified.name || userName || "Abogado");
                }
            }
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error", description: err.message || "No se pudieron cargar los correos o remitentes." });
        } finally {
            setIsLoadingEmails(false);
        }
    };

    const toggleEmailAttachment = (file: any) => {
        setAttachedFiles(prev => {
            const exists = prev.some(a => a.id === file.id);
            if (exists) return prev.filter(a => a.id !== file.id);
            return [...prev, file];
        });
    };

    const handleEmailFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        if (!radicado) {
            toast({ variant: "destructive", title: "Radicado requerido", description: "El expediente debe tener un número de radicado para subir adjuntos." });
            return;
        }

        setIsUploadingEmailFile(true);
        setUploadingEmailFileName(file.name);
        try {
            const uploadedFile = await scraperApi.uploadProcesoFile(radicado, file);

            // Cargar archivos del expediente actualizados para obtener la lista oficial con IDs
            const filesList = await scraperApi.getProcesoFiles(radicado);
            if (filesList && filesList.length > 0) {
                setProcessFiles(filesList);

                // Buscar el archivo recién subido en la lista por ID o por nombre
                const targetFile = (uploadedFile && (uploadedFile.id || uploadedFile.file_id))
                    ? filesList.find(f => f.id === (uploadedFile.id || uploadedFile.file_id))
                    : filesList.find(f => f.file_name?.toLowerCase() === file.name.toLowerCase());

                const fileToAdd = targetFile || uploadedFile || { id: `temp-${Date.now()}`, file_name: file.name };

                setAttachedFiles(prev => {
                    const exists = prev.some(a => (a.id && a.id === fileToAdd.id) || a.file_name === fileToAdd.file_name);
                    return exists ? prev : [...prev, fileToAdd];
                });
            } else if (uploadedFile) {
                setAttachedFiles(prev => {
                    const exists = prev.some(a => a.id === uploadedFile.id);
                    return exists ? prev : [...prev, uploadedFile];
                });
            }
        } catch (err: any) {
            console.error("Error al subir adjunto de correo:", err);
            toast({ variant: "destructive", title: "Error al subir", description: err.message || "No se pudo subir el archivo desde tu equipo." });
        } finally {
            setIsUploadingEmailFile(false);
            setUploadingEmailFileName("");
            e.target.value = "";
        }
    };

    const handleQuickRegister = async () => {
        if (!quickSenderEmail.trim()) {
            toast({ variant: "destructive", title: "Email requerido", description: "Ingresa el email a verificar." });
            return;
        }
        setIsQuickRegistering(true);
        try {
            const res = await legalEmailsApi.registerSingleSender(quickSenderEmail.trim(), quickSenderName.trim() || undefined);
            toast({ title: "Código OTP Enviado", description: res.message || "Código enviado al correo de destino." });
            setQuickVerifyingEmail(quickSenderEmail.trim());
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error al registrar", description: err.message || "No se pudo registrar la dirección." });
        } finally {
            setIsQuickRegistering(false);
        }
    };

    const handleQuickVerify = async () => {
        if (!quickVerifyingEmail || quickSenderOtp.length !== 6) {
            toast({ variant: "destructive", title: "Datos requeridos", description: "Código de 6 dígitos requerido." });
            return;
        }
        setIsQuickVerifying(true);
        try {
            await legalEmailsApi.verifySingleSender(quickVerifyingEmail, quickSenderOtp);
            toast({ title: "Email Verificado", description: `El correo ${quickVerifyingEmail} ha sido activado.` });

            // Reload verified senders
            const list = await legalEmailsApi.getVerifiedSenders();
            setVerifiedSenders(list);

            // Select the newly verified email
            setSelectedSender(quickVerifyingEmail);

            // Clean states & close modal
            setIsQuickVerifyOpen(false);
            setQuickSenderEmail("");
            setQuickSenderName("");
            setQuickSenderOtp("");
            setQuickVerifyingEmail(null);
        } catch (err: any) {
            toast({ variant: "destructive", title: "Verificación fallida", description: err.message || "El código ingresado es incorrecto." });
        } finally {
            setIsQuickVerifying(false);
        }
    };

    const [isDeletingSender, setIsDeletingSender] = useState(false);

    const handleDeleteSender = async (emailToDelete: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!emailToDelete) {
            toast({ variant: "destructive", title: "Error", description: "No se ha seleccionado ningún remitente para eliminar." });
            return;
        }

        if (emailToDelete === "notificacionesjudiciales@miasistentelegalia.com") {
            toast({ variant: "destructive", title: "Acción no permitida", description: "El correo predeterminado del sistema no se puede eliminar." });
            return;
        }

        if (!window.confirm(`¿Deseas eliminar el correo remitente "${emailToDelete}" de tus remitentes autorizados?`)) {
            return;
        }

        setIsDeletingSender(true);
        try {
            await legalEmailsApi.deleteSender(emailToDelete);
            toast({ title: "Remitente eliminado", description: `Se eliminó ${emailToDelete} de la lista de remitentes.` });

            const updatedList = await legalEmailsApi.getVerifiedSenders();
            setVerifiedSenders(updatedList);

            if (updatedList.length > 0) {
                const nextSender = updatedList[0];
                setSelectedSender(nextSender.email);
                setEmailSenderName(nextSender.name || userName || "Abogado");
            } else {
                setSelectedSender("");
                setEmailSenderName("");
            }
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error al eliminar", description: err.message || "No se pudo eliminar el remitente." });
        } finally {
            setIsDeletingSender(false);
        }
    };

    const handleSendEmail = async () => {
        if (!process?.id) {
            toast({ variant: "destructive", title: "Expediente no guardado", description: "Guarda el expediente antes de enviar correos." });
            return;
        }
        if (!emailToAddress.trim()) {
            toast({ variant: "destructive", title: "Destinatario requerido", description: "Ingresa la dirección de correo del destinatario." });
            return;
        }
        if (!emailToAddressProof.trim()) {
            toast({ variant: "destructive", title: "Origen de dirección requerido", description: "Debes indicar cómo obtuviste esta dirección de correo (Ley 2213 Art. 8)." });
            return;
        }
        if (!emailSubject.trim()) {
            toast({ variant: "destructive", title: "Asunto requerido", description: "El correo debe tener un asunto." });
            return;
        }
        if (!emailOathAccepted) {
            toast({ variant: "destructive", title: "Juramento requerido", description: "Debes aceptar el juramento de que la dirección es correcta (Ley 2213/2022)." });
            return;
        }
        if (!emailTermsAccepted) {
            toast({ variant: "destructive", title: "Términos requeridos", description: "Debes aceptar los Términos y Condiciones del Servicio de Correo Certificado." });
            return;
        }

        setIsSendingEmail(true);
        try {
            const result = await legalEmailsApi.sendLegalEmail({
                process_id: process.id,
                process_number: formData.numero || process.case_number || "",
                from_email: selectedSender || undefined,
                from_name: emailSenderName.trim() || undefined,
                to_email: emailToAddress.trim(),
                to_name: emailToName.trim() || undefined,
                to_address_proof: emailToAddressProof.trim(),
                subject: emailSubject.trim(),
                body_html: emailBodyHtml,
                attached_file_ids: attachedFiles.map(f => f.id),
                oath_accepted: emailOathAccepted,
                is_judicial_notification: emailIsJudicial,
            });

            // Dispatch credits-updated to refresh user credit balance in UI
            window.dispatchEvent(new CustomEvent("credits-updated"));

            toast({
                title: "✅ Correo enviado",
                description: `Enviado a ${emailToAddress}. Hash: ${result.body_hash.slice(0, 12)}...`,
            });

            // Reset form
            setEmailToAddress("");
            setEmailToName("");
            setEmailToAddressProof("");
            setEmailSubject("");
            setEmailBodyHtml("<p>Estimados,</p><p>Por medio de la presente...</p>");
            setEmailIsJudicial(true);
            setEmailOathAccepted(false);
            setEmailAttachedFiles([]);
            setAttachedFiles([]);
            setIsComposeEmailOpen(false);

            // Reload list
            await loadProcessEmails();
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error al enviar", description: err.message || "No se pudo enviar el correo." });
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleViewEmailDetail = async (emailId: string) => {
        try {
            const detail = await legalEmailsApi.getEmailDetail(emailId);
            setSelectedEmailDetail(detail);
            setIsEmailDetailOpen(true);
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error", description: "No se pudo cargar el detalle del correo." });
        }
    };

    const handleDeleteEmail = async (emailId: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!window.confirm("¿Estás seguro de que deseas eliminar este correo del historial?")) return;
        try {
            await legalEmailsApi.deleteLegalEmail(emailId);
            toast({ title: "Correo eliminado", description: "El correo ha sido removido del historial del expediente." });
            if (isEmailDetailOpen && selectedEmailDetail?.id === emailId) {
                setIsEmailDetailOpen(false);
                setSelectedEmailDetail(null);
            }
            await loadProcessEmails();
        } catch (err: any) {
            toast({ variant: "destructive", title: "Error al eliminar", description: err.message || "No se pudo eliminar el correo." });
        }
    };


    const handleSave = async () => {
        const loadingToast = toast({ title: "Actualizando expediente...", description: "Guardando los cambios en el sistema." });
        try {
            const currentMeta = (process as any)?.metadata || {};
            const processPayload = {
                title: formData.denominacion,
                case_number: formData.numero,
                description: formData.descripcion,
                status: formData.estado,
                case_type: formData.tipoProceso,
                tags: localTags,
                grupos: localTags,
                juzgado: formData.juzgado,
                jurisdiccion: formData.jurisdiccion,
                cuantia: formData.cuantia,
                fecha_inicio: formData.fechaInicio,
                fecha_radicacion: formData.fechaRadicacion,
                instancia: formData.instancia,
                ponente: formData.ponente,
                clase: formData.clase,
                subclase: formData.subclase,
                recurso: formData.recurso,
                naturaleza: formData.naturaleza,
                medida_cautelar: formData.medidaCautelar,
                metadata: {
                    ...currentMeta,
                    origen: formData.fuente || currentMeta.origen,
                    tipo_documento: formData.tipo_documento || currentMeta.tipo_documento,
                    nit_cedula: formData.nit_cedula || currentMeta.nit_cedula,
                    anho: formData.anho || currentMeta.anho,
                    delegatura: formData.delegatura || currentMeta.delegatura,
                    nit_sociedad: formData.nit_sociedad || currentMeta.nit_sociedad,
                    razon_social: formData.razon_social || currentMeta.razon_social,
                }
            };

            if (process) {
                await updateProcess(process.id, processPayload);

                // Track usage
                if (process.user_id) {
                    UsageTracker.trackProcessUpdate(process.user_id, "Actualización de datos generales", formData.numero);
                }

                toast({ title: "Expediente actualizado", description: "Los cambios se han guardado correctamente." });
            } else {
                // Create new process from scrapper data
                const newProcess = await addProcess(processPayload);

                if (newProcess && newProcess.user_id) {
                    UsageTracker.trackProcessUpdate(newProcess.user_id, "Creación de nuevo expediente", formData.numero);
                }

                toast({ title: "Expediente guardado", description: "El proceso se ha registrado exitosamente en su despacho." });
            }
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error", description: error.message || "No se pudo guardar el expediente." });
        }
    };

    const handleSavePart = async () => {
        if (!newPartName.trim()) return;
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de agregar una parte."
                });
                return;
            }
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: newPartName.trim(),
                description: `[PARTE] rol=${newPartRole}`,
                status: "pending",
                priority: "low",
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            toast({ title: "Parte agregada", description: `${newPartName} ha sido vinculada como ${newPartRole}.` });
            setNewPartName("");
            setIsAddPartOpen(false);
        } catch (error: any) {
            toast({ variant: "destructive", title: "Error", description: error.message || "No se pudo guardar la parte." });
        }
    };

    const handleSaveTramite = async () => {
        if (!newTramiteTitle.trim()) return;
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de registrar un trámite."
                });
                return;
            }
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: newTramiteTitle,
                description: `[TRAMITE] entidad=${newTramiteEntity || "—"}\nresponsable=${newTramiteResponsible || "Dirección"}`,
                status: "pending",
                priority: "medium",
                due_date: new Date().toISOString(),
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            setNewTramiteTitle("");
            setNewTramiteResponsible("");
            setNewTramiteEntity("");
            setIsAddTramiteOpen(false);
        } catch (error) {
            console.error("Error saving tramite:", error);
        }
    };

    const handleSaveTask = async () => {
        if (!newTaskTitle.trim()) return;
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de agregar una tarea."
                });
                return;
            }
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: newTaskTitle,
                description: `[TAREA] responsable=Dirección`,
                status: "pending",
                priority: "medium",
                due_date: newTaskDate || new Date().toISOString(),
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            setNewTaskTitle("");
            setNewTaskDate("");
            setIsAddTaskOpen(false);
        } catch (error) {
            console.error("Error saving task:", error);
        }
    };

    const handleCreateTask = async (task: { titulo: string; descripcion?: string; prioridad?: string; due_date?: string }) => {
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de crear una tarea."
                });
                return;
            }
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: task.titulo,
                description: `[TAREA] responsable=Dirección\n${task.descripcion || ""}`.trim(),
                status: "pending",
                priority: task.prioridad || "medium",
                due_date: task.due_date || new Date().toISOString(),
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            toast({ title: "Tarea creada", description: `Se programó: ${task.titulo}` });
        } catch (error) {
            console.error("Error creating task:", error);
            toast({ variant: "destructive", title: "Error", description: "No se pudo crear la tarea." });
        }
    };

    const handleSaveAdelanto = async () => {
        if (!newAdelantoConcept.trim()) return;
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de registrar un adelanto."
                });
                return;
            }
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: newAdelantoConcept.trim(),
                description: `[ADELANTO] valor=${newAdelantoAmount || "0"}`,
                status: "completed",
                priority: "low",
                due_date: new Date().toISOString(),
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            setNewAdelantoAmount("");
            setNewAdelantoConcept("");
            setIsAddAdelantoOpen(false);
            toast({ title: "Adelanto registrado", description: "El pago ha sido procesado correctamente." });
        } catch (error) {
            console.error("Error saving adelanto:", error);
        }
    };

    const handleSaveComment = () => {
        if (!commentText.trim()) return;
        const newComment = {
            name: "Dirección",
            text: commentText,
            date: new Date().toLocaleString()
        };
        setLocalComments(prev => [newComment, ...prev]);
        setCommentText("");
        toast({ title: "Comentario publicado" });
    };

    const handleDelete = async () => {
        if (!process) return;
        const success = await deleteProcess(process.id);
        if (success) {
            setIsDeleteDialogOpen(false);
            navigate("/dashboard/despachos/procesos");
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        const textToShare = `Consulta el expediente judicial: ${formData.denominacion} (Radicado: ${formData.numero})\nEnlace: ${url}`;

        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Expediente: ${formData.numero}`,
                    text: `Consulta el expediente judicial: ${formData.denominacion}`,
                    url
                });
                return;
            }
            await navigator.clipboard.writeText(textToShare);
            toast({ title: "Información copiada", description: "Los datos del expediente y enlace se copiaron al portapapeles." });
        } catch (err) {
            if (err instanceof Error && err.name !== 'AbortError') {
                toast({ variant: "destructive", title: "Error", description: "No se pudo compartir o copiar el enlace." });
            }
        }
    };

    const toggleTag = async (tag: string) => {
        const newTags = localTags.includes(tag)
            ? localTags.filter(t => t !== tag)
            : [...localTags, tag];

        setLocalTags(newTags);
        if (process) {
            await updateProcess(process.id, { tags: newTags });
        }
    };

    const handleAddCustomTag = async () => {
        if (!newTagInput.trim()) return;
        const tag = newTagInput.trim();
        if (!localTags.includes(tag)) {
            const newTags = [...localTags, tag];
            setLocalTags(newTags);
            if (process) {
                await updateProcess(process.id, { tags: newTags });
            }
        }
        setNewTagInput("");
    };

    const effectiveClientName = process?.client_name || (typeof scrapperMetadata?.metadata === 'object' ? scrapperMetadata?.metadata?.client_name : "") || "";
    const effectiveClientEmail = (process as any)?.client_email || (typeof scrapperMetadata?.metadata === 'object' ? scrapperMetadata?.metadata?.client_email : "") || "";
    const effectiveClientPhone = (process as any)?.client_phone || (typeof scrapperMetadata?.metadata === 'object' ? scrapperMetadata?.metadata?.client_phone : "") || "";
    const effectiveClientRole = (process as any)?.client_role || (typeof scrapperMetadata?.metadata === 'object' ? scrapperMetadata?.metadata?.client_role : "") || "Demandante";
    const linkedClientObj = clients.find(c => c.name?.trim().toLowerCase() === effectiveClientName?.trim().toLowerCase());

    const handleLinkClient = async () => {
        if (!selectedClientToLink) {
            toast({
                variant: "destructive",
                title: "Selecciona un cliente",
                description: "Por favor, elige un cliente de la lista para vincular."
            });
            return;
        }

        const client = clients.find(c => c.id === selectedClientToLink);
        if (!client) return;

        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");

        try {
            if (process) {
                await updateProcess(process.id, {
                    client_name: client.name,
                    client_email: client.email || "",
                    client_phone: client.phone || "",
                    client_role: selectedRoleToLink,
                });
            } else if (radicado) {
                await addProcess({
                    case_number: radicado,
                    title: formData.denominacion || scrapperMetadata?.asunto || scrapperMetadata?.clase || `Expediente ${radicado}`,
                    status: "active",
                    client_name: client.name,
                    client_email: client.email || "",
                    client_phone: client.phone || "",
                    client_role: selectedRoleToLink,
                    description: formData.descripcion || "",
                    tags: localTags,
                    grupos: localTags
                });
            }

            if (scrapperMetadata) {
                const currentMeta = typeof scrapperMetadata.metadata === 'object' ? (scrapperMetadata.metadata || {}) : {};
                setScrapperMetadata({
                    ...scrapperMetadata,
                    metadata: {
                        ...currentMeta,
                        client_name: client.name,
                        client_email: client.email || "",
                        client_phone: client.phone || "",
                        client_role: selectedRoleToLink
                    }
                });
            }

            toast({
                title: "Cliente vinculado",
                description: `${client.name} ha sido vinculado como ${selectedRoleToLink} a este expediente.`
            });
            setIsLinkClientOpen(false);
        } catch (err: any) {
            console.error("Error al vincular cliente:", err);
            toast({
                variant: "destructive",
                title: "Error al vincular",
                description: err.message || "No se pudo vincular el cliente al expediente."
            });
        }
    };

    const handleUnlinkClient = async () => {
        try {
            if (process) {
                await updateProcess(process.id, {
                    client_name: "",
                    client_email: "",
                    client_phone: "",
                    client_role: "",
                });
            }
            if (scrapperMetadata) {
                const currentMeta = typeof scrapperMetadata.metadata === 'object' ? (scrapperMetadata.metadata || {}) : {};
                setScrapperMetadata({
                    ...scrapperMetadata,
                    metadata: {
                        ...currentMeta,
                        client_name: "",
                        client_email: "",
                        client_phone: "",
                        client_role: ""
                    }
                });
            }
            toast({
                title: "Cliente desvinculado",
                description: "Se ha removido la asociación del cliente en este expediente."
            });
        } catch (err: any) {
            console.error("Error al desvincular cliente:", err);
        }
    };

    const handleOpenExternalQuery = () => {
        const radicado = (formData.numero || process?.case_number || "").replace(/\s/g, "");
        const fuenteUpper = (formData.fuente || "").toUpperCase();
        const isSic = fuenteUpper === "SIC" || (radicado.includes("-") && radicado.length <= 11);
        const isSuper = fuenteUpper.includes("SOCIEDAD") || radicado.includes("-01-");

        if (isSic) {
            window.open("https://serviciosweb.sic.gov.co/recursos/servicios/busqueda.php", "_blank", "noopener,noreferrer");
            return;
        }
        if (isSuper) {
            window.open("https://barandavirtual.supersociedades.gov.co/", "_blank", "noopener,noreferrer");
            return;
        }
        if (radicado && /^\d{23}$/.test(radicado)) {
            const first7 = radicado.slice(0, 7);
            const samaiUrl = `https://samai.consejodeestado.gov.co/Vistas/Casos/list_procesos.aspx?guid=${radicado}${first7}`;
            window.open(samaiUrl, "_blank", "noopener,noreferrer");
            return;
        }
        window.open("https://samai.consejodeestado.gov.co/Vistas/Casos/procesos.aspx", "_blank", "noopener,noreferrer");
    };

    const handleOpenResearchWorkspace = (item: any) => {
        const metadata = item?.metadata || {};
        if (item?.id) {
            navigate("/dashboard/agents/legal-research-v2", {
                state: {
                    historyItemId: item.id,
                    openHistory: true,
                    fromExpediente: true,
                    linkedArtifact: item,
                },
            });
            return;
        }
        const researchTopic =
            item?.query ||
            metadata?.query ||
            metadata?.research_topic ||
            item?.title ||
            "Investigación jurídica del expediente";

        navigate("/dashboard/agents/legal-research-v2", {
            state: {
                prefill: {
                    research_topic: researchTopic,
                    jurisdiction: metadata?.jurisdiction || "Constitucional",
                    specific_areas: metadata?.specific_areas || [],
                },
            },
        });
    };

    const handleOpenContractWorkspace = (item: any) => {
        const metadata = item?.metadata || {};
        if (item?.id) {
            navigate("/dashboard/agents/contract-review-v2", {
                state: {
                    historyItemId: item.id,
                    openHistory: true,
                    fromExpediente: true,
                    linkedArtifact: item,
                },
            });
            return;
        }
        const contractText =
            item?.contract_text ||
            metadata?.contract_text ||
            metadata?.contract_content ||
            item?.summary_bluf ||
            "Analizar contrato vinculado al expediente";

        navigate("/dashboard/agents/contract-review-v2", {
            state: {
                prefill: {
                    contract_text: contractText,
                    contract_type: item?.contract_type || metadata?.contract_type || "General",
                },
            },
        });
    };

    const getArtifactUrl = (item: any): string | null => {
        const metadata = item?.metadata || {};
        return (
            metadata?.document_url ||
            metadata?.summary_pdf_url ||
            item?.document_url ||
            item?.summary_pdf_url ||
            null
        );
    };

    const getNormalizedStatus = (item: any): "completado" | "en_proceso" | "error" => {
        const raw = String(item?.status || item?.compliance_status || "").toLowerCase();
        if (raw.includes("error") || raw.includes("fall")) return "error";
        if (raw.includes("pend") || raw.includes("proceso") || raw.includes("running")) return "en_proceso";
        return "completado";
    };

    const getArtifactOwner = (item: any): string => {
        const metadata = item?.metadata || {};
        return (
            metadata?.owner_name ||
            metadata?.created_by_name ||
            item?.owner_name ||
            item?.created_by_name ||
            item?.user_id ||
            "Sistema IA"
        );
    };

    const activityFeed = useMemo(() => {
        const researches = caseResearch.map((r) => ({
            id: `research-${r.id}`,
            type: "investigacion" as const,
            title: r.query || r.metadata?.query || r.metadata?.research_topic || "Investigación legal",
            description: r.metadata?.summary || r.metadata?.executive_summary || "Investigación asociada al expediente",
            createdAt: r.created_at,
            status: getNormalizedStatus(r),
            owner: getArtifactOwner(r),
            artifact: r,
        }));

        const contracts = caseContracts.map((c) => ({
            id: `contract-${c.id}`,
            type: "contrato" as const,
            title: c.summary_bluf || c.metadata?.summary_bluf || "Análisis contractual",
            description: `Tipo: ${c.contract_type || "General"}${c.risk_score ? ` · Riesgo ${c.risk_score}%` : ""}`,
            createdAt: c.created_at,
            status: getNormalizedStatus(c),
            owner: getArtifactOwner(c),
            artifact: c,
        }));

        const drafts = caseDrafts.map((d) => ({
            id: `draft-${d.id}`,
            type: "documento" as const,
            title: d.document_type?.replace(/_/g, " ") || "Documento generado",
            description: d.session_id || "Documento generado por IA",
            createdAt: d.created_at,
            status: getNormalizedStatus(d),
            owner: getArtifactOwner(d),
            artifact: d,
        }));

        return [...researches, ...contracts, ...drafts].sort(
            (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        );
    }, [caseResearch, caseContracts, caseDrafts]);

    const activityOwnerOptions = useMemo(() => {
        return Array.from(new Set(activityFeed.map((item) => item.owner))).filter(Boolean).slice(0, 50);
    }, [activityFeed]);

    const filteredActivityFeed = useMemo(() => {
        return activityFeed.filter((item) => {
            const typeOk = activityTypeFilter === "all" || item.type === activityTypeFilter;
            const statusOk = activityStatusFilter === "all" || item.status === activityStatusFilter;
            const ownerOk = activityOwnerFilter === "all" || item.owner === activityOwnerFilter;
            return typeOk && statusOk && ownerOk;
        });
    }, [activityFeed, activityTypeFilter, activityStatusFilter, activityOwnerFilter]);

    const handleOpenActivity = (activity: any) => {
        if (activity.type === "investigacion") {
            handleOpenResearchWorkspace(activity.artifact);
            return;
        }
        if (activity.type === "contrato") {
            handleOpenContractWorkspace(activity.artifact);
            return;
        }
        if (activity.artifact?.id) {
            navigate(`/dashboard/agents/document-history/${activity.artifact.id}`);
            return;
        }
        navigate("/dashboard/agents/document-drafting-v2");
    };

    const handlePreviewActivity = (activity: any) => {
        if (activity.type === "investigacion" || activity.type === "contrato") {
            handleOpenActivity(activity);
            return;
        }
        if (activity.type === "documento" && activity.artifact?.id) {
            navigate(`/dashboard/agents/document-history/${activity.artifact.id}`);
            return;
        }
        const url = getArtifactUrl(activity.artifact);
        if (!url) {
            toast({ title: "Vista previa no disponible", description: "Este artefacto aún no tiene un archivo asociado." });
            return;
        }
        openDocument(url, activity.title);
    };

    const handleShareActivity = async (activity: any) => {
        const url = getArtifactUrl(activity.artifact);
        const fallbackRef = activity.artifact?.session_id || activity.artifact?.id;
        const payload = url || `Referencia: ${fallbackRef || activity.title}`;
        await navigator.clipboard.writeText(payload);
        toast({ title: "Copiado", description: "Se copió la referencia del artefacto al portapapeles." });
    };

    const handleCreateTaskFromActivity = async (activity: any) => {
        try {
            if (!process?.id) {
                toast({
                    variant: "destructive",
                    title: "Expediente no guardado",
                    description: "Por favor, guarda los datos generales del expediente antes de crear una tarea."
                });
                return;
            }
            const labels: Record<string, string> = {
                investigacion: "Investigación",
                contrato: "Análisis de contrato",
                documento: "Documento generado",
            };
            const ref = activity.artifact?.id || activity.artifact?.session_id || "sin-ref";
            const { error } = await supabase.from("legal_tasks").insert([{
                case_id: process.id,
                title: `Revisar ${labels[activity.type] || "artefacto"}: ${activity.title}`,
                description: `[TAREA] responsable=Dirección\nTarea creada desde Actividad IA. Referencia: ${ref}`,
                status: "pending",
                due_date: new Date().toISOString(),
                priority: "medium",
                user_id: (await supabase.auth.getUser()).data.user?.id,
            }]);
            if (error) throw error;
            await loadCaseEntries(process.id);
            toast({ title: "Tarea creada", description: "Se agregó una tarea de seguimiento en el expediente." });
        } catch (error) {
            console.error("Error creating activity task:", error);
            toast({ variant: "destructive", title: "Error", description: "No se pudo crear la tarea." });
        }
    };


    // --- Client Report Module States & Functions ---
    const [clientReportHtml, setClientReportHtml] = useState<string>("");
    const reportEditorRef = useRef(null);
    const reportEditorConfig = useMemo(() => ({
        readonly: false,
        height: 650,
        placeholder: "Generando informe ejecutivo del proceso...",
        toolbarButtonSize: "middle" as "middle",
        buttons: ["bold", "italic", "underline", "|", "ul", "ol", "|", "font", "fontsize", "paragraph", "|", "table", "link", "|", "align", "undo", "redo"],
        showCharsCounter: true,
        showWordsCounter: true,
        showStatusbar: true,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: "insert_clear_html" as "insert_clear_html"
    }), []);

    const generateClientReportContent = () => {
        const today = new Date().toLocaleDateString('es-CO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        const radicado = (formData.numero || process?.case_number || id || "").replace(/\s/g, "");
        const clientName = effectiveClientName || (linkedClientObj ? linkedClientObj.name : "Poderdante / Cliente");
        const despacho = formData.juzgado || formData.despacho || scrapperMetadata?.ubicacion || (typeof scrapperMetadata?.metadata === 'object' ? (scrapperMetadata?.metadata as any)?.Despacho : "") || "Despacho Judicial de Conocimiento";
        const clase = formData.clase || formData.tipoProceso || "Proceso Judicial";
        const demandante = formData.demandante || (effectiveClientRole.toLowerCase().includes("demandante") ? clientName : "Parte Demandante");
        const demandado = formData.demandado || (effectiveClientRole.toLowerCase().includes("demandado") ? clientName : "Parte Demandada");
        const estado = (formData.estado || "Vigente / En trámite").toUpperCase();
        const abogado = formData.abogado || "Dirección Jurídica";
        const tp = formData.tarjeta_profesional ? `T.P. N.° ${formData.tarjeta_profesional}` : "Abogado Apoderado";

        // Timeline de actuaciones recientes (hasta 25 actuaciones)
        const recentActuaciones = Array.isArray(timeline) ? timeline.slice(0, 25) : [];
        let actuacionesRows = "";
        if (recentActuaciones.length > 0) {
            actuacionesRows = recentActuaciones.map((act: any, idx: number) => {
                const f = act.fecha_actuacion || act.fecha || "—";
                const a = act.actuacion || "Providencia Judicial";
                const d = act.anotacion || act.descripcion || "Actuación registrada en el sistema de la Rama Judicial.";
                return `
                <tr style="border-bottom: 1px solid #e2e8f0; background-color: ${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                    <td style="padding: 10px 12px; font-weight: bold; color: #1e293b; white-space: nowrap; vertical-align: top;">${f}</td>
                    <td style="padding: 10px 12px; font-weight: 600; color: #0f172a; vertical-align: top;">${a}</td>
                    <td style="padding: 10px 12px; color: #334155; vertical-align: top; line-height: 1.5;">${d}</td>
                </tr>`;
            }).join("");
        } else {
            actuacionesRows = `
            <tr>
                <td colspan="3" style="padding: 16px; text-align: center; color: #64748b; font-style: italic;">
                    No se registran actuaciones públicas pendientes en el sistema de la Rama Judicial a la fecha de este informe.
                </td>
            </tr>`;
        }

        // Trámites del apoderado
        const recentTramites = caseTramites.slice(0, 15);
        let tramitesHtml = "";
        if (recentTramites.length > 0) {
            tramitesHtml = `<ul style="margin-left: 20px; line-height: 1.8;">` + recentTramites.map((t: any) => `
                <li style="margin-bottom: 6px;">
                    <strong>${t.title || "Trámite"}:</strong> ${t.description ? t.description.replace(/\[tramite\]/g, "").trim() : "Gestión realizada ante el despacho o entidad."} 
                    ${t.due_date ? `<span style="color: #64748b; font-size: 9.5pt;">(Fecha: ${t.due_date})</span>` : ""}
                </li>
            `).join("") + `</ul>`;
        } else {
            tramitesHtml = `
            <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
                La representación judicial ha efectuado el seguimiento permanente y la revisión de publicaciones oficiales en estados electrónicos, sin que a la fecha se hayan requerido trámites extraordinarios pendientes de desahogo.
            </p>`;
        }

        return `
        <h1 style="text-align: center; color: #0f2744; font-size: 18pt; margin-bottom: 4px; text-transform: uppercase;">Informe de Estado y Gestión Procesal</h1>
        <p style="text-align: center; color: #64748b; font-size: 10pt; margin-bottom: 24px; letter-spacing: 0.5px;">DOCUMENTO OFICIAL DE RENDICIÓN AL PODERDANTE</p>
        
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 24px; font-size: 10.5pt; background-color: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px;">
            <tbody>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; width: 28%; color: #334155;">Fecha de Emisión:</td>
                    <td style="padding: 8px 14px; color: #0f172a;">${today}</td>
                </tr>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Destinatario / Cliente:</td>
                    <td style="padding: 8px 14px; color: #0f172a; font-weight: bold;">${clientName} ${effectiveClientRole ? `(${effectiveClientRole})` : ""}</td>
                </tr>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Radicado Judicial (23 dígitos):</td>
                    <td style="padding: 8px 14px; color: #0f172a; font-family: monospace; font-weight: bold; font-size: 11pt;">${radicado || "En trámite de radicación"}</td>
                </tr>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Despacho de Conocimiento:</td>
                    <td style="padding: 8px 14px; color: #0f172a;">${despacho}</td>
                </tr>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Clase de Proceso:</td>
                    <td style="padding: 8px 14px; color: #0f172a;">${clase}</td>
                </tr>
                <tr style="border-bottom: 1px solid #e2e8f0;">
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Partes del Proceso:</td>
                    <td style="padding: 8px 14px; color: #0f172a;"><strong>Demandante:</strong> ${demandante} &nbsp;|&nbsp; <strong>Demandado:</strong> ${demandado}</td>
                </tr>
                <tr>
                    <td style="padding: 8px 14px; font-weight: bold; color: #334155;">Estado Actual de la Causa:</td>
                    <td style="padding: 8px 14px; color: #0284c7; font-weight: bold;">${estado}</td>
                </tr>
            </tbody>
        </table>

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">1. Objeto y Alcance del Informe</h2>
        <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
            El presente informe tiene por objeto enterar formalmente a <strong>${clientName}</strong> sobre el estado actual, las providencias proferidas por el despacho judicial de conocimiento y las actuaciones adelantadas por su apoderado dentro del expediente radicado bajo el número <strong>${radicado || "S.N."}</strong>, en cabal cumplimiento del deber de información profesional.
        </p>

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">2. Resumen Ejecutivo del Estado Procesal</h2>
        <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
            El trámite litigioso se surte de manera regular ante el <strong>${despacho}</strong>. Durante el período examinado se ha mantenido la vigilancia continua de los términos legales procesales, verificando que los derechos e intereses de nuestro representado se encuentren debidamente salvaguardados mediante la atención oportuna de traslados, autos y requerimientos judiciales.
        </p>

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">3. Historial de Actuaciones Surtidas ante la Rama Judicial</h2>
        <p style="text-align: justify; font-size: 10.5pt; color: #475569; margin-bottom: 10px;">
            A continuación se relacionan en orden cronológico las actuaciones y decisiones registradas en el sistema de consulta oficial:
        </p>
        <table style="width: 100%; border-collapse: collapse; margin-top: 8px; margin-bottom: 18px; font-size: 10pt;">
            <thead>
                <tr style="background-color: #f1f5f9; border-bottom: 2px solid #cbd5e1; text-align: left;">
                    <th style="padding: 8px 10px; width: 110px; color: #0f2744; font-weight: bold;">Fecha</th>
                    <th style="padding: 8px 10px; width: 180px; color: #0f2744; font-weight: bold;">Actuación</th>
                    <th style="padding: 8px 10px; color: #0f2744; font-weight: bold;">Anotación / Detalle</th>
                </tr>
            </thead>
            <tbody>
                ${actuacionesRows}
            </tbody>
        </table>

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">4. Gestiones y Trámites Adelantados por el Apoderado</h2>
        <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
            En ejecución del mandato judicial conferido, esta representación ha adelantado las siguientes diligencias, memoriales e impulsos:
        </p>
        ${tramitesHtml}

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">5. Próximas Actuaciones y Términos</h2>
        <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
            El proceso se encuentra a la espera del próximo pronunciamiento del juzgado o del vencimiento del término legal respectivo para dar paso a la siguiente etapa procesal. Esta firma continuará con la vigilancia diaria del expediente.
        </p>

        <h2 style="color: #0f2744; font-size: 12.5pt; border-bottom: 1.5px solid #0f2744; padding-bottom: 4px; margin-top: 24px; margin-bottom: 10px;">6. Observaciones y Recomendaciones</h2>
        <p style="text-align: justify; font-size: 11pt; line-height: 1.6;">
            A la fecha de este reporte no se requieren gestiones urgentes por parte del cliente. En caso de requerirse documentos o información complementaria, le serán solicitados con debida antelación.
        </p>

        <div style="margin-top: 45px; padding-top: 18px; border-top: 1px solid #cbd5e1; font-size: 10.5pt; line-height: 1.5;">
            <p style="font-weight: bold; margin-bottom: 4px;">Atentamente,</p>
            <p style="font-weight: bold; color: #0f2744; font-size: 12pt; margin-bottom: 2px;">${abogado}</p>
            <p style="color: #475569; font-size: 10pt; margin-bottom: 2px;">${tp}</p>
            <p style="color: #64748b; font-size: 9pt;">Despacho Jurídico & Representación Legal</p>
        </div>
        `;
    };

    useEffect(() => {
        if (activeSection === "informe_cliente" && !clientReportHtml) {
            setClientReportHtml(generateClientReportContent());
        }
    }, [activeSection, timeline, caseTramites, effectiveClientName]);

    const handleRegenerateClientReport = () => {
        const fresh = generateClientReportContent();
        setClientReportHtml(fresh);
        toast({ title: "Informe actualizado", description: "Se ha regenerado el contenido con los datos y actuaciones más recientes del proceso." });
    };

    const handleCopyClientReportText = async () => {
        if (!clientReportHtml) return;
        try {
            const tmp = document.createElement("div");
            tmp.innerHTML = clientReportHtml;
            const plainText = tmp.innerText || tmp.textContent || "";
            await navigator.clipboard.writeText(plainText);
            toast({ title: "Copiado", description: "El texto del informe ha sido copiado al portapapeles." });
        } catch (err) {
            toast({ variant: "destructive", title: "Error", description: "No se pudo copiar el contenido." });
        }
    };

    const handleDownloadClientReportPdf = () => {
        if (!clientReportHtml) {
            toast({ variant: "destructive", title: "Sin contenido", description: "No hay texto generado para exportar." });
            return;
        }

        const radicado = (formData.numero || process?.case_number || id || "expediente").replace(/\s/g, "");
        const safeFilename = `Informe_Cliente_${radicado}_${new Date().toISOString().slice(0, 10)}`;

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            toast({ variant: "destructive", title: "Ventana bloqueada", description: "Por favor permite ventanas emergentes para descargar/imprimir el PDF." });
            return;
        }

        const fullDoc = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${safeFilename}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@400;500;600;700&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    @page {
      size: letter portrait;
      margin: 20mm 20mm 20mm 20mm;
    }
    
    body {
      font-family: 'Crimson Text', Georgia, serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #0f172a;
      background: #ffffff;
      padding: 0;
      margin: 0 auto;
      max-width: 780px;
    }
    
    .report-header-banner {
      border-bottom: 2.5pt solid #0f2744;
      padding-bottom: 12pt;
      margin-bottom: 18pt;
      text-align: center;
    }
    
    .firm-name {
      font-family: 'Inter', sans-serif;
      font-size: 13.5pt;
      font-weight: 700;
      letter-spacing: 1.2px;
      color: #0f2744;
      text-transform: uppercase;
      margin-bottom: 3pt;
    }
    
    .firm-subtitle {
      font-family: 'Inter', sans-serif;
      font-size: 9pt;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.8px;
    }
    
    h1 {
      font-family: 'Inter', sans-serif;
      font-size: 15pt;
      font-weight: 700;
      color: #0f2744;
      text-align: center;
      margin-top: 10pt;
      margin-bottom: 6pt;
      text-transform: uppercase;
    }
    
    h2 {
      font-family: 'Inter', sans-serif;
      font-size: 11.5pt;
      font-weight: 700;
      color: #0f2744;
      border-bottom: 1.5pt solid #0f2744;
      padding-bottom: 3pt;
      margin-top: 18pt;
      margin-bottom: 8pt;
      page-break-after: avoid;
      text-transform: uppercase;
    }
    
    p {
      text-align: justify;
      margin-bottom: 9pt;
      font-size: 11pt;
      line-height: 1.65;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 10pt;
      margin-bottom: 14pt;
      font-size: 9.5pt;
      page-break-inside: auto;
    }
    
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    
    th {
      background-color: #f1f5f9;
      color: #0f2744;
      font-family: 'Inter', sans-serif;
      font-weight: 600;
      border: 1pt solid #cbd5e1;
      padding: 6pt 8pt;
      text-align: left;
    }
    
    td {
      border: 1pt solid #e2e8f0;
      padding: 6pt 8pt;
      vertical-align: top;
    }
    
    ul, ol {
      margin-left: 20pt;
      margin-bottom: 10pt;
    }
    
    li {
      margin-bottom: 4pt;
      text-align: justify;
    }
  </style>
</head>
<body>
  <div class="report-header-banner">
    <div class="firm-name">${formData.abogado || "Despacho Jurídico & Representación Legal"}</div>
    <div class="firm-subtitle">Informe Ejecutivo de Gestión Procesal · Rendición al Poderdante</div>
  </div>
  ${clientReportHtml}
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 500);
    };
  </script>
</body>
</html>`;

        printWindow.document.open();
        printWindow.document.write(fullDoc);
        printWindow.document.close();
        toast({ title: "Preparando PDF", description: "Se abrió la vista preliminar para imprimir o guardar en PDF." });
    };

    const menuItems = [
        { id: "generales", label: "Datos Generales", icon: Briefcase },
        { id: "clientes", label: "Clientes", icon: Users },
        { id: "partes", label: "Partes", icon: Users },
        { id: "timeline", label: "Actuaciones", icon: Zap },
        { id: "analisis", label: "Estrategia Legal IA", icon: Brain, isPro: true },
        // { id: "rag", label: "Chat Legal RAG", icon: MessageSquare },
        { id: "adelantos", label: "Finanzas / Provisiones", icon: Coins },
        { id: "tramites", label: "Trámites", icon: FileText },
        { id: "informe_cliente", label: "Informe al Cliente", icon: FileCheck },
        { id: "tareas", label: "Tareas", icon: CheckCircle2 },
        { id: "archivos", label: "Archivos", icon: FileText },
        { id: "emails", label: "Emails (Beta)", icon: Mail },
        // { id: "docs", label: "Docs. BB.DD.", icon: ExternalLink },
        // { id: "investigaciones", label: "Investigaciones", icon: Search, isBeta: true },
        // { id: "contratos", label: "Análisis de Contratos", icon: FileCheck, isBeta: true },
        // { id: "documentacion", label: "Documentos Generados", icon: FileEdit, isBeta: true },
        // { id: "actividad_ia", label: "Actividad IA", icon: Layout, isBeta: true },
        { id: "comentarios", label: `Comentarios (${localComments.length})`, icon: MessageSquare },
    ];

    const sectionIcons: Record<string, { from: string; to: string }> = {
        generales: { from: "from-blue-500", to: "to-indigo-600" },
        clientes: { from: "from-violet-500", to: "to-purple-600" },
        partes: { from: "from-teal-500", to: "to-cyan-600" },
        adelantos: { from: "from-amber-500", to: "to-orange-600" },
        tramites: { from: "from-emerald-500", to: "to-teal-600" },
        informe_cliente: { from: "from-blue-600", to: "to-cyan-600" },
        tareas: { from: "from-rose-500", to: "to-pink-600" },
        archivos: { from: "from-sky-500", to: "to-blue-600" },
        emails: { from: "from-indigo-500", to: "to-violet-600" },
        docs: { from: "from-cyan-500", to: "to-teal-600" },
        investigaciones: { from: "from-fuchsia-500", to: "to-pink-600" },
        contratos: { from: "from-emerald-500", to: "to-teal-600" },
        documentacion: { from: "from-blue-500", to: "to-sky-600" },
        actividad_ia: { from: "from-violet-500", to: "to-indigo-600" },
        comentarios: { from: "from-gray-500", to: "to-slate-600" },
    };

    return (
        <div className="flex flex-col h-full bg-gray-50/30">
            {/* Header */}
            <div className="flex flex-col bg-white border-b border-gray-100 transition-all duration-300">
                {!isHeaderExpanded ? (
                    <div className="px-4 py-2 flex items-center justify-between gap-3 animate-fade-in bg-gray-50/40">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate("/dashboard/despachos/procesos")}
                            className="text-gray-500 hover:text-legal-blue rounded-xl p-1 h-7 w-7 hover:bg-blue-50/50 shrink-0 flex items-center justify-center"
                            title="Volver"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex-1 min-w-0 flex items-center gap-1.5">
                            <span className="text-xs font-bold text-legal-blue truncate max-w-[120px] sm:max-w-[200px]" title={formData.numero}>{formData.numero}</span>
                            <span className="text-gray-300 text-[10px]">•</span>
                            <span className="text-xs text-gray-500 font-semibold truncate flex-1" title={formData.denominacion}>{formData.denominacion}</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsHeaderExpanded(true)}
                            className="text-slate-500 hover:text-legal-blue hover:bg-blue-50/50 rounded-lg h-7 px-2.5 text-xs font-bold gap-1 shrink-0 flex items-center"
                        >
                            <ChevronDown className="h-3.5 w-3.5" /> Mostrar cabecera
                        </Button>
                    </div>
                ) : (
                    <div className="animate-fade-in">
                        <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate("/dashboard/despachos/procesos")}
                                className="text-gray-500 hover:text-legal-blue rounded-xl gap-1.5 hover:bg-blue-50/50 self-start"
                            >
                                <ArrowLeft className="h-4 w-4" /> Volver listado de expedientes
                            </Button>
                            <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end items-center">
                                <Dialog open={isTagsOpen} onOpenChange={setIsTagsOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" className="rounded-xl border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors">
                                            <Tag className="h-3.5 w-3.5 mr-1.5" /> Etiquetas
                                            {localTags.length > 0 && (
                                                <Badge className="ml-1.5 h-4 w-4 p-0 flex items-center justify-center bg-legal-blue text-[8px]">
                                                    {localTags.length}
                                                </Badge>
                                            )}
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="rounded-2xl">
                                        <DialogHeader>
                                            <DialogTitle>Gestionar Etiquetas</DialogTitle>
                                        </DialogHeader>
                                        <div className="py-4 space-y-4">
                                            <p className="text-sm text-gray-500">Selecciona etiquetas para organizar este expediente.</p>
                                            <div className="flex flex-wrap gap-2">
                                                {["Urgente", "Importante", "En Revisión", "Fiscalía", "Civil", "Penal", "Laboral"].map(tag => (
                                                    <Badge
                                                        key={tag}
                                                        onClick={() => toggleTag(tag)}
                                                        className={`cursor-pointer transition-colors py-1.5 px-3 rounded-lg border ${localTags.includes(tag)
                                                            ? "bg-legal-blue text-white border-legal-blue"
                                                            : "bg-white text-gray-600 border-gray-200 hover:bg-blue-50"
                                                            }`}
                                                    >
                                                        {tag}
                                                    </Badge>
                                                ))}
                                            </div>

                                            <div className="pt-2 border-t border-gray-100">
                                                <Label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Nueva etiqueta personalizada</Label>
                                                <div className="flex gap-2">
                                                    <Input
                                                        placeholder="Ej: Cobro Coactivo..."
                                                        className="rounded-xl"
                                                        value={newTagInput}
                                                        onChange={e => setNewTagInput(e.target.value)}
                                                        onKeyDown={e => e.key === 'Enter' && handleAddCustomTag()}
                                                    />
                                                    <Button size="sm" className="rounded-xl bg-gray-100 text-gray-900 hover:bg-gray-200" onClick={handleAddCustomTag}>
                                                        Agregar
                                                    </Button>
                                                </div>
                                            </div>

                                            {localTags.length > 0 && (
                                                <div className="space-y-2">
                                                    <Label className="text-xs font-bold text-gray-400 uppercase">Seleccionadas</Label>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {localTags.map(tag => (
                                                            <Badge key={tag} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 flex items-center gap-1 group">
                                                                {tag}
                                                                <button onClick={() => toggleTag(tag)} className="hover:text-red-500">
                                                                    <X className="h-2.5 w-2.5" />
                                                                </button>
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <DialogFooter>
                                            <Button className="rounded-xl bg-legal-blue" onClick={() => setIsTagsOpen(false)}>Listo</Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" size="sm" className="rounded-xl border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors">
                                            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="rounded-2xl">
                                        <DialogHeader>
                                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                                <AlertCircle className="h-5 w-5" /> Confirmar Eliminación
                                            </DialogTitle>
                                        </DialogHeader>
                                        <div className="py-4">
                                            <p className="text-sm text-gray-600 italic font-medium">¿Estás seguro de que deseas eliminar este expediente? Esta acción no se puede deshacer.</p>
                                        </div>
                                        <DialogFooter className="gap-2">
                                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsDeleteDialogOpen(false)}>Cancelar</Button>
                                            <Button variant="destructive" className="rounded-xl" onClick={handleDelete}>Eliminar Expediente</Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>

                                <BulkIngestionModal onIngestionSuccess={() => {
                                    const rad = (formData.numero || id || "").replace(/\s/g, "");
                                    if (rad) loadScrapperData(rad);
                                    toast({ title: "Ingesta completada", description: "Procesos cargados exitosamente." });
                                }} />

                                {scrapperMetadata && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                            try {
                                                const nextWatched = isPaused;
                                                const { setScrapperProcesoWatch } = await import("@/services/scraperApi");
                                                const rad = (formData.numero || id || "").replace(/\s/g, "");
                                                await setScrapperProcesoWatch(rad, nextWatched);
                                                setScrapperMetadata((prev: any) => prev ? { ...prev, is_watched: nextWatched, watch_status: nextWatched ? "active" : "paused" } : prev);
                                                toast({
                                                    title: nextWatched ? "Vigilancia reactivada" : "Vigilancia pausada",
                                                    description: `El proceso está ahora ${nextWatched ? 'activo' : 'pausado'}.`,
                                                });
                                            } catch (e: any) {
                                                toast({ title: "Error", description: e.message || "Error cambiando vigilancia", variant: "destructive" });
                                            }
                                        }}
                                        className={cn(
                                            "rounded-xl transition-colors font-semibold text-xs h-9 px-3 border shadow-sm",
                                            isPaused
                                                ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                : "border-amber-200 bg-amber-50/70 text-amber-700 hover:bg-amber-100"
                                        )}
                                    >
                                        {isPaused ? (
                                            <>
                                                <Play className="h-3.5 w-3.5 mr-1.5 fill-emerald-600 text-emerald-600" />
                                                Reactivar vigilancia
                                            </>
                                        ) : (
                                            <>
                                                <Pause className="h-3.5 w-3.5 mr-1.5 fill-amber-600 text-amber-600" />
                                                Pausar vigilancia
                                            </>
                                        )}
                                    </Button>
                                )}

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleShare}
                                    className="rounded-xl border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors"
                                >
                                    <Share2 className="h-3.5 w-3.5 mr-1.5" /> Compartir
                                </Button>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsHeaderExpanded(false)}
                                    className="rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-legal-blue transition-colors h-9 px-3 gap-1 shadow-sm active:scale-95 flex items-center"
                                >
                                    <ChevronUp className="h-3.5 w-3.5" /> Ocultar cabecera
                                </Button>
                            </div>
                        </div>

                        <div className="px-4 md:px-6 pb-4 flex items-start sm:items-center gap-3">
                            <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/20">
                                <Briefcase className="h-5 w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 flex flex-wrap items-center gap-1.5 break-all sm:break-normal max-w-full" title={`${formData.numero} - ${formData.denominacion}`}>
                                    <span className="font-semibold text-legal-blue">{formData.numero}</span>
                                    <span className="text-gray-400 font-normal hidden sm:inline">—</span>
                                    <span className="text-gray-800 break-words line-clamp-2 md:line-clamp-none max-w-full">
                                        {formData.denominacion && formData.denominacion.length > 70 ? `${formData.denominacion.slice(0, 70)}...` : formData.denominacion}
                                    </span>
                                    <Paperclip className="h-4 w-4 text-gray-300 shrink-0" />
                                </h1>
                                <p className="text-xs text-gray-400 mt-0.5">Expediente judicial • {formData.tipoProceso}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Navigation Tabs */}
            <div className="md:hidden border-b border-gray-100 bg-white sticky top-0 z-20">
                <div className="flex overflow-x-auto py-2.5 px-4 gap-2 scrollbar-none" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveSection(item.id)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0 ${isActive
                                    ? "bg-legal-blue text-white shadow-sm shadow-legal-blue/15"
                                    : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-gray-900"
                                    }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Internal Sidebar */}
                <div className="w-56 bg-white border-r border-gray-100 overflow-y-auto hidden md:flex md:flex-col justify-between">
                    <div className="flex-1 overflow-y-auto px-4 py-6 scrollbar-hide">
                        <div className="space-y-1.5">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = activeSection === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => setActiveSection(item.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300 group ${isActive
                                            ? "bg-gradient-to-r from-legal-blue/10 to-transparent text-legal-blue shadow-sm border border-legal-blue/10"
                                            : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                            }`}
                                    >
                                        <div className={`p-2 rounded-xl transition-all duration-300 ${isActive ? "bg-legal-blue text-white shadow-md shadow-legal-blue/20 rotate-0" : "bg-gray-100 group-hover:bg-white group-hover:shadow-sm group-hover:rotate-3"}`}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <span className={`text-sm font-bold tracking-tight ${isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"}`}>
                                            {item.label}
                                        </span>
                                        {item.isPro && !hasProAccess && (
                                            <Badge className="ml-auto bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md">
                                                PRO
                                            </Badge>
                                        )}
                                        {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-legal-blue animate-pulse" />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Extra Scrapper Controls (Bottom of internal sidebar) */}
                        {scrapperMetadata?.link_descarga && (
                            <div className="mt-10 pt-6 border-t border-gray-100">
                                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-4 mb-4">Expediente Digital</h4>
                                <a
                                    href={scrapperMetadata.link_descarga}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mx-2 flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 transition-colors shadow-sm group"
                                >
                                    <div className="p-2 bg-emerald-500 text-white rounded-xl shadow-md shadow-emerald-200 group-hover:scale-110 transition-transform">
                                        <Upload className="h-4 w-4 rotate-180" />
                                    </div>
                                    <span className="text-xs font-bold">Descargar PDF</span>
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Footer Info */}
                    <div className="px-4 py-4 border-t border-gray-100 text-[11px] text-gray-400 space-y-1.5">
                        <p>Creado por: <span className="font-semibold text-gray-500 block">Dirección</span></p>
                        <p>Creación: <span className="text-gray-500">21/02/2025 07:47</span></p>
                        <p>Modificación: <span className="text-gray-500">21/02/2025 07:47</span></p>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-5 md:p-6">
                    <div className="max-w-4xl mx-auto animate-fade-in-up">
                        {scrapperMetadata && (
                            isPaused ? (
                                <div className="mb-6 p-4 bg-amber-50/90 border border-amber-200/90 rounded-2xl flex items-center justify-between shadow-sm animate-fade-in">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-200 shrink-0">
                                            <Box className="h-5 w-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-amber-950">Vigilancia Judicial Pausada</p>
                                            <p className="text-xs text-amber-800/90 font-medium mt-0.5">El seguimiento automático de este proceso está pausado y no se actualizará hasta que no se reactive nuevamente.</p>
                                        </div>
                                    </div>
                                    <Badge className="bg-amber-100/90 text-amber-800 border-amber-300 uppercase text-[9px] font-black px-3 py-1">Pausado</Badge>
                                </div>
                            ) : (timeline.length === 0 || !analysis) && (
                                <div className="mb-6 p-4 bg-amber-50/50 border border-amber-200 rounded-2xl flex items-center justify-between shadow-sm animate-fade-in">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-200">
                                            <Zap className="h-5 w-5 text-white animate-pulse" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-amber-900">Seguimiento en Tiempo Real Activo</p>
                                            <p className="text-xs text-amber-700/80 font-medium italic">Estamos procesando y refinando las actuaciones de este radicado...</p>
                                        </div>
                                    </div>
                                    <Badge className="bg-amber-100 text-amber-700 border-amber-200 uppercase text-[9px] font-black px-2 animate-pulse">Sincronizando</Badge>
                                </div>
                            )
                        )}

                        {/* ====== DATOS GENERALES ====== */}
                        {activeSection === "generales" && (
                            <div className="space-y-5">
                                {/* Datos Obligatorios */}
                                <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                                    <button
                                        onClick={() => setDatosObligatoriosOpen(!datosObligatoriosOpen)}
                                        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50/50 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                                            {datosObligatoriosOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                                            Datos Obligatorios
                                        </span>
                                        <Badge className="bg-blue-50 text-blue-600 border border-blue-200 text-[10px]">Requerido</Badge>
                                    </button>
                                    {datosObligatoriosOpen && (
                                        <CardContent className="p-6 space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-[2fr_auto] gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Denominación *</Label>
                                                    <Textarea
                                                        value={formData.denominacion}
                                                        onChange={e => setFormData({ ...formData, denominacion: e.target.value })}
                                                        className="rounded-xl bg-white min-h-[80px]"
                                                    />
                                                </div>
                                                <div className="space-y-2 flex flex-col items-center">
                                                    <Label className="text-sm font-medium text-gray-600">Estado del proceso</Label>
                                                    {(() => {
                                                        const statusInfo = getVigenteStatusInfo(formData.estado || scrapperMetadata?.vigente || (process as any)?.vigente);
                                                        return (
                                                            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium shadow-sm ${statusInfo.headerBadgeClass}`}>
                                                                <div className="h-2 w-2 bg-white rounded-full animate-pulse" />
                                                                {statusInfo.text}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Número *</Label>
                                                    <Input
                                                        value={formData.numero}
                                                        onChange={e => setFormData({ ...formData, numero: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-sm font-medium text-gray-600">Tipo de proceso *</Label>
                                                        {!isAddingNewTipo ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setIsAddingNewTipo(true);
                                                                    setNewTipoInputValue("");
                                                                }}
                                                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-1"
                                                            >
                                                                + Agregar otro tipo
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => setIsAddingNewTipo(false)}
                                                                className="text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors"
                                                            >
                                                                Volver a la lista
                                                            </button>
                                                        )}
                                                    </div>

                                                    {isAddingNewTipo ? (
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                value={newTipoInputValue}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    setNewTipoInputValue(val);
                                                                    setFormData(prev => ({ ...prev, tipoProceso: val }));
                                                                }}
                                                                placeholder="Escribe el nuevo tipo (ej: Arbitraje)..."
                                                                className="rounded-xl bg-white flex-1"
                                                                autoFocus
                                                            />
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-3 shrink-0"
                                                                onClick={() => {
                                                                    const trimmed = newTipoInputValue.trim();
                                                                    if (trimmed) {
                                                                        setFormData(prev => ({ ...prev, tipoProceso: trimmed }));
                                                                        if (!DEFAULT_TIPOS_PROCESO.includes(trimmed.toLowerCase()) && !customTiposProceso.includes(trimmed)) {
                                                                            const updated = [...customTiposProceso, trimmed];
                                                                            setCustomTiposProceso(updated);
                                                                            try { localStorage.setItem("custom_tipos_proceso", JSON.stringify(updated)); } catch {}
                                                                        }
                                                                    }
                                                                    setIsAddingNewTipo(false);
                                                                }}
                                                            >
                                                                Guardar
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Select
                                                            value={formData.tipoProceso}
                                                            onValueChange={v => {
                                                                if (v === "__ADD_NEW__") {
                                                                    setIsAddingNewTipo(true);
                                                                    setNewTipoInputValue("");
                                                                } else {
                                                                    setFormData(prev => ({ ...prev, tipoProceso: v }));
                                                                }
                                                            }}
                                                        >
                                                            <SelectTrigger className="rounded-xl bg-white capitalize"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                {Array.from(new Set([
                                                                    ...DEFAULT_TIPOS_PROCESO,
                                                                    ...customTiposProceso,
                                                                    ...(formData.tipoProceso ? [formData.tipoProceso] : [])
                                                                ])).map(t => (
                                                                    <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                                                                ))}
                                                                <SelectItem value="__ADD_NEW__" className="font-bold text-indigo-600 focus:text-indigo-700">
                                                                    + Otro tipo de proceso...
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Entidad / Origen del Proceso */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Entidad / Jurisdicción</Label>
                                                    <Select
                                                        value={formData.fuente || "RAMA"}
                                                        onValueChange={v => setFormData({ ...formData, fuente: v })}
                                                    >
                                                        <SelectTrigger className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="RAMA">Rama Judicial (SAMAI / CPNU / TYBA)</SelectItem>
                                                            <SelectItem value="SIC">Superintendencia de Industria y Comercio (SIC)</SelectItem>
                                                            <SelectItem value="SUPERSOCIEDADES">Superintendencia de Sociedades</SelectItem>
                                                            <SelectItem value="OTRO">Otro</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Portal Conectado</Label>
                                                    <div className="flex items-center gap-2 h-10 px-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700">
                                                        {(formData.fuente === "SIC" || ((formData.numero || "").includes("-") && (formData.numero || "").length <= 11)) ? (
                                                            <>
                                                                <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                                                                <span>SIC - Servicios Web Oficiales</span>
                                                            </>
                                                        ) : (formData.fuente === "SUPERSOCIEDADES" || (formData.numero || "").includes("-01-")) ? (
                                                            <>
                                                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                                                <span>Supersociedades - Baranda Virtual</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                                                <span>Rama Judicial (SAMAI / CPNU / TYBA)</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Parámetros Desagregados si es SIC */}
                                            {(formData.fuente === "SIC" || ((formData.numero || "").includes("-") && (formData.numero || "").length <= 11)) && (
                                                <div className="p-4 bg-gradient-to-r from-purple-50/70 to-indigo-50/70 border border-purple-200/80 rounded-2xl space-y-4 animate-fade-in">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-bold text-purple-950 flex items-center gap-2">
                                                            <Shield className="h-4 w-4 text-purple-600" /> Parámetros Específicos SIC (Industria y Comercio)
                                                        </span>
                                                        <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px]">SIC Búsqueda</Badge>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                                        <div>
                                                            <Label className="text-xs font-medium text-gray-600">Tipo Doc.</Label>
                                                            <Select value={formData.tipo_documento || "CC"} onValueChange={v => setFormData({ ...formData, tipo_documento: v })}>
                                                                <SelectTrigger className="rounded-xl bg-white text-xs h-9"><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="CC">Cédula (CC)</SelectItem>
                                                                    <SelectItem value="NIT">NIT Empresa</SelectItem>
                                                                    <SelectItem value="CE">Cédula Ext. (CE)</SelectItem>
                                                                    <SelectItem value="PASAPORTE">Pasaporte</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div className="sm:col-span-2">
                                                            <Label className="text-xs font-medium text-gray-600">Cédula / NIT</Label>
                                                            <Input
                                                                value={formData.nit_cedula || ""}
                                                                onChange={e => setFormData({ ...formData, nit_cedula: e.target.value })}
                                                                placeholder="Número de documento..."
                                                                className="rounded-xl bg-white text-xs h-9"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-medium text-gray-600">Año Radicación</Label>
                                                            <Input
                                                                value={formData.anho || ""}
                                                                onChange={e => setFormData({ ...formData, anho: e.target.value })}
                                                                placeholder="Ej: 2024"
                                                                className="rounded-xl bg-white text-xs h-9 font-mono"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">Delegatura SIC</Label>
                                                        <Select
                                                            value={formData.delegatura || "proteccion_consumidor"}
                                                            onValueChange={v => setFormData({ ...formData, delegatura: v })}
                                                        >
                                                            <SelectTrigger className="rounded-xl bg-white text-xs h-9"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="proteccion_consumidor">Delegatura de Protección al Consumidor</SelectItem>
                                                                <SelectItem value="propiedad_industrial">Delegatura de Propiedad Industrial (Signos Distintivos/Patentes)</SelectItem>
                                                                <SelectItem value="competencia_desleal">Delegatura para Asuntos Jurisdiccionales / Competencia Desleal</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Parámetros Desagregados si es Supersociedades */}
                                            {(formData.fuente === "SUPERSOCIEDADES" || (formData.numero || "").includes("-01-")) && (
                                                <div className="p-4 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 border border-emerald-200/80 rounded-2xl space-y-4 animate-fade-in">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-bold text-emerald-950 flex items-center gap-2">
                                                            <Building2 className="h-4 w-4 text-emerald-600" /> Parámetros Superintendencia de Sociedades
                                                        </span>
                                                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">Supersociedades Oficial</Badge>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <div>
                                                            <Label className="text-xs font-medium text-gray-600">NIT Sociedad Concursada</Label>
                                                            <Input
                                                                value={formData.nit_sociedad || ""}
                                                                onChange={e => setFormData({ ...formData, nit_sociedad: e.target.value })}
                                                                placeholder="Sin dígito de verificación..."
                                                                className="rounded-xl bg-white text-xs h-9"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-medium text-gray-600">Razón Social</Label>
                                                            <Input
                                                                value={formData.razon_social || ""}
                                                                onChange={e => setFormData({ ...formData, razon_social: e.target.value })}
                                                                placeholder="Nombre de la sociedad o concursada..."
                                                                className="rounded-xl bg-white text-xs h-9"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-gray-600">Delegatura / Área</Label>
                                                        <Select
                                                            value={formData.delegatura || "insolvencia"}
                                                            onValueChange={v => setFormData({ ...formData, delegatura: v })}
                                                        >
                                                            <SelectTrigger className="rounded-xl bg-white text-xs h-9"><SelectValue /></SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="insolvencia">Procedimientos de Insolvencia (Ley 1116 de 2006)</SelectItem>
                                                                <SelectItem value="mercantiles">Procedimientos Mercantiles (Conflictos Societarios)</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="space-y-3 pt-2 border-t border-gray-100">
                                                <div className="flex items-center justify-between">
                                                    <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                                        <Tag className="h-4 w-4 text-legal-blue" />
                                                        Etiquetas y Clasificación del Expediente
                                                    </Label>
                                                    <span className="text-[11px] text-gray-400">
                                                        {localTags.length} seleccionada{localTags.length === 1 ? "" : "s"}
                                                    </span>
                                                </div>

                                                {/* Selected tags chip list with quick remove */}
                                                <div className="border border-gray-200 rounded-xl p-3 bg-white min-h-[46px] flex flex-wrap items-center gap-2">
                                                    {localTags.length === 0 ? (
                                                        <span className="text-xs text-gray-400 italic">Sin etiquetas asignadas. Haz clic en las sugerencias abajo o escribe una nueva.</span>
                                                    ) : (
                                                        localTags.map(tag => (
                                                            <span
                                                                key={tag}
                                                                className="bg-blue-50 text-legal-blue border border-blue-200 px-2.5 py-1 rounded-lg text-xs flex items-center gap-1.5 font-semibold transition-all hover:bg-blue-100/70"
                                                            >
                                                                {tag}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleTag(tag)}
                                                                    className="hover:text-red-500 font-bold ml-0.5 text-slate-400 hover:text-red-600 transition-colors"
                                                                    title="Eliminar etiqueta"
                                                                >
                                                                    &times;
                                                                </button>
                                                            </span>
                                                        ))
                                                    )}
                                                </div>

                                                {/* Quick presets and custom tag input */}
                                                <div className="space-y-2 pt-1">
                                                    <div className="flex items-center justify-between">
                                                        <Label className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Sugerencias rápidas</Label>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {["Urgente", "Importante", "En Revisión", "Fiscalía", "Civil", "Penal", "Laboral", "Cobro PH"].map(preset => {
                                                            const isSelected = localTags.includes(preset);
                                                            return (
                                                                <Badge
                                                                    key={preset}
                                                                    onClick={() => toggleTag(preset)}
                                                                    className={`cursor-pointer text-xs py-1 px-2.5 rounded-lg border transition-all ${
                                                                        isSelected
                                                                            ? "bg-legal-blue text-white border-legal-blue shadow-sm"
                                                                            : "bg-white text-gray-600 border-gray-200 hover:bg-blue-50/60 hover:text-legal-blue hover:border-blue-200"
                                                                    }`}
                                                                >
                                                                    {isSelected ? "✓ " : "+ "}
                                                                    {preset}
                                                                </Badge>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Input for custom tag */}
                                                    <div className="flex gap-2 pt-1">
                                                        <Input
                                                            placeholder="Escribe una etiqueta personalizada (ej: Segunda Instancia, Embargo)..."
                                                            className="rounded-xl text-xs h-9"
                                                            value={newTagInput}
                                                            onChange={e => setNewTagInput(e.target.value)}
                                                            onKeyDown={e => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleAddCustomTag();
                                                                }
                                                            }}
                                                        />
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="rounded-xl border-gray-200 hover:bg-blue-50 hover:text-legal-blue text-xs shrink-0 h-9 px-3"
                                                            onClick={handleAddCustomTag}
                                                        >
                                                            Agregar
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    )}
                                </Card>

                                {/* Datos Opcionales */}
                                <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                                    <button
                                        onClick={() => setDatosOpcionalesOpen(!datosOpcionalesOpen)}
                                        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50/50 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                                            {datosOpcionalesOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                                            Datos Opcionales
                                        </span>
                                        <Badge className="bg-gray-50 text-gray-500 border border-gray-200 text-[10px]">Opcional</Badge>
                                    </button>
                                    {datosOpcionalesOpen && (
                                        <CardContent className="p-6 space-y-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Juzgado</Label>
                                                    <Input
                                                        value={formData.juzgado}
                                                        onChange={e => setFormData({ ...formData, juzgado: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="Ej: Juzgado 2 Civil Municipal"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Jurisdicción</Label>
                                                    <Select value={formData.jurisdiccion} onValueChange={v => setFormData({ ...formData, jurisdiccion: v })}>
                                                        <SelectTrigger className="rounded-xl bg-white"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="ordinaria">Ordinaria</SelectItem>
                                                            <SelectItem value="contencioso">Contencioso Administrativo</SelectItem>
                                                            <SelectItem value="constitucional">Constitucional</SelectItem>
                                                            <SelectItem value="especial">Especial</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Cuantía</Label>
                                                    <Input
                                                        value={formData.cuantia}
                                                        onChange={e => setFormData({ ...formData, cuantia: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="$0.00"
                                                        type="text"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Fecha de Inicio</Label>
                                                    <Input
                                                        value={formData.fechaInicio}
                                                        onChange={e => setFormData({ ...formData, fechaInicio: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        type="date"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Fecha de Radicación</Label>
                                                    <Input
                                                        value={formData.fechaRadicacion}
                                                        onChange={e => setFormData({ ...formData, fechaRadicacion: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        type="date"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Ponente</Label>
                                                    <Input
                                                        value={formData.ponente}
                                                        onChange={e => setFormData({ ...formData, ponente: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="Nombre del magistrado/juez ponente"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Clase de Proceso</Label>
                                                    <Input
                                                        value={formData.clase}
                                                        onChange={e => setFormData({ ...formData, clase: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="Ej: Verbal, Ejecutivo, etc."
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Recurso</Label>
                                                    <Input
                                                        value={formData.recurso}
                                                        onChange={e => setFormData({ ...formData, recurso: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="Tipo de recurso"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Naturaleza</Label>
                                                    <Input
                                                        value={formData.naturaleza}
                                                        onChange={e => setFormData({ ...formData, naturaleza: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Medida Cautelar</Label>
                                                    <Input
                                                        value={formData.medidaCautelar}
                                                        onChange={e => setFormData({ ...formData, medidaCautelar: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Instancia</Label>
                                                    <Select value={formData.instancia} onValueChange={v => setFormData({ ...formData, instancia: v })}>
                                                        <SelectTrigger className="rounded-xl bg-white"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="primera">Primera Instancia</SelectItem>
                                                            <SelectItem value="segunda">Segunda Instancia</SelectItem>
                                                            <SelectItem value="casacion">Casación</SelectItem>
                                                            <SelectItem value="revision">Revisión</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium text-gray-600">Descripción / Notas</Label>
                                                    <Textarea
                                                        value={formData.descripcion}
                                                        onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                                                        className="rounded-xl bg-white"
                                                        placeholder="Notas sobre el proceso..."
                                                    />
                                                </div>
                                            </div>

                                            {/* Technical Metadata Display */}
                                            {scrapperMetadata?.metadata && (
                                                <div className="mt-8 pt-6 border-t border-gray-100">
                                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                                        <Database className="h-3 w-3" /> Metadatos Técnicos (Vigilancia)
                                                    </h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {Object.entries(scrapperMetadata.metadata).map(([key, value]) => {
                                                            if (["sujetos_completos", "link_descarga", "sujetos_texto"].includes(key)) return null;
                                                            let displayValue = String(value || "");
                                                            if (typeof value === "object" && value !== null) {
                                                                if (Array.isArray(value)) {
                                                                    displayValue = value.map(v => typeof v === 'object' && v !== null ? (v.nombre || v.name || JSON.stringify(v)) : String(v)).join(", ");
                                                                } else {
                                                                    displayValue = JSON.stringify(value);
                                                                }
                                                            }
                                                            return (
                                                                <div key={key} className="flex flex-col p-3 bg-gray-50/50 rounded-xl border border-gray-100">
                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">{key}</span>
                                                                    <span className="text-sm text-gray-700 font-medium truncate" title={displayValue}>{displayValue}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </CardContent>
                                    )}
                                </Card>

                                {/* Save */}
                                <div className="flex justify-center pt-2">
                                    <Button
                                        className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15 px-10"
                                        onClick={handleSave}
                                    >
                                        <Save className="h-4 w-4 mr-2" /> Actualizar Expediente
                                    </Button>
                                </div>
                            </div>
                        )}
                        {/* ====== TIMELINE (Integrated) ====== */}
                        {activeSection === "timeline" && (
                            <div className="space-y-6">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">Historial de Actuaciones</h2>
                                        <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                            Cronología desde la Rama Judicial. Se resaltan <span className="font-semibold text-violet-700">estados electrónicos</span> y{" "}
                                            <span className="font-semibold text-cyan-700">traslados electrónicos</span> según el texto de la actuación.
                                        </p>
                                        {!isLoadingTimeline && timeline.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                <Badge variant="outline" className="rounded-lg text-xs font-semibold border-slate-200 bg-white">
                                                    Total: {actuacionKindStats.total}
                                                </Badge>
                                                <Badge className="rounded-lg text-xs font-semibold bg-violet-50 text-violet-800 border border-violet-100">
                                                    Estados: {actuacionKindStats.estado}
                                                </Badge>
                                                <Badge className="rounded-lg text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-100">
                                                    Traslados: {actuacionKindStats.traslado}
                                                </Badge>
                                                <Badge variant="secondary" className="rounded-lg text-xs font-semibold">
                                                    Otras: {actuacionKindStats.otro}
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 shrink-0">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-xl border-legal-blue text-legal-blue hover:bg-legal-blue/5"
                                            onClick={handleDeepSync}
                                            disabled={isVectorizing}
                                        >
                                            {isVectorizing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                                            Deep Sync & Vectorizar
                                        </Button>
                                        <Button variant="outline" size="sm" className="rounded-xl hover:bg-gray-50" onClick={() => {
                                            const rad = (formData.numero || process?.case_number || (!isUuid(id) ? id : "") || "").replace(/\s/g, "");
                                            if (rad) loadScrapperData(rad);
                                        }}>
                                            <Clock className="h-4 w-4 mr-2" /> Sincronizar
                                        </Button>
                                    </div>
                                </div>

                                {!isLoadingTimeline && timeline.length > 0 && (
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Filtrar por tipo</span>
                                        <ToggleGroup
                                            type="single"
                                            value={actuacionKindFilter}
                                            onValueChange={(v) => {
                                                if (!v) {
                                                    setActuacionKindFilter("all");
                                                    return;
                                                }
                                                if (v === "all" || v === "estado" || v === "traslado") setActuacionKindFilter(v);
                                            }}
                                            className="justify-start flex-wrap"
                                        >
                                            <ToggleGroupItem value="all" aria-label="Todas" className="rounded-lg text-xs px-3 data-[state=on]:bg-white data-[state=on]:shadow-sm">
                                                Todas
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="estado" aria-label="Estados" className="rounded-lg text-xs px-3 data-[state=on]:bg-violet-100 data-[state=on]:text-violet-900">
                                                Estados electrónicos
                                            </ToggleGroupItem>
                                            <ToggleGroupItem value="traslado" aria-label="Traslados" className="rounded-lg text-xs px-3 data-[state=on]:bg-cyan-100 data-[state=on]:text-cyan-900">
                                                Traslados electrónicos
                                            </ToggleGroupItem>
                                        </ToggleGroup>
                                    </div>
                                )}

                                {isLoadingTimeline ? (
                                    <div className="py-20 flex flex-col items-center justify-center space-y-4">
                                        <Loader2 className="h-10 w-10 text-legal-blue animate-spin" />
                                        <p className="text-gray-500 animate-pulse font-medium">Cargando actuaciones desde la rama judicial...</p>
                                    </div>
                                ) : timeline.length === 0 ? (
                                    <Card className="border-dashed border-2 py-16 text-center">
                                        <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-600">No se encontraron actuaciones</h3>
                                        <p className="text-gray-400 max-w-sm mx-auto mt-2">
                                            Asegúrate de que el radicado sea correcto (23 dígitos) y que el proceso esté siendo vigilado.
                                        </p>
                                    </Card>
                                ) : filteredTimelineEntries.length === 0 ? (
                                    <Card className="border border-amber-100 bg-amber-50/40 py-10 text-center">
                                        <p className="text-sm text-amber-900 font-medium">No hay actuaciones en esta categoría.</p>
                                        <p className="text-xs text-amber-800/80 mt-2">Prueba con otra pestaña del filtro o revisa &quot;Todas&quot;.</p>
                                    </Card>
                                ) : (
                                    <div className="relative space-y-8 before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                                        {filteredTimelineEntries.map(({ item, idx, cat }) => {
                                            const dotClass =
                                                cat.kind === "estado"
                                                    ? "bg-violet-500 shadow-violet-200"
                                                    : cat.kind === "traslado"
                                                        ? "bg-cyan-500 shadow-cyan-200"
                                                        : item.riesgo_ia === "Alto"
                                                            ? "bg-rose-500 shadow-rose-200"
                                                            : item.riesgo_ia === "Medio"
                                                                ? "bg-amber-500 shadow-amber-200"
                                                                : "bg-legal-blue shadow-blue-200";
                                            const borderAccent =
                                                cat.kind === "estado"
                                                    ? "ring-1 ring-violet-100 border-violet-100/80"
                                                    : cat.kind === "traslado"
                                                        ? "ring-1 ring-cyan-100 border-cyan-100/80"
                                                        : "border-transparent";
                                            return (
                                                <div key={`${idx}-${item.fecha}-${(item.actuacion || "").slice(0, 24)}`} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                                                    <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-white shadow-sm shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                                                        <div className={`w-3 h-3 rounded-full ${dotClass} shadow-lg`} />
                                                    </div>
                                                    <Card className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-5 border-none shadow-premium bg-white/70 backdrop-blur-sm hover:shadow-xl transition-all hover:-translate-y-0.5 ${borderAccent}`}>
                                                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <time className="text-xs font-bold text-legal-blue bg-blue-50 px-2.5 py-1 rounded-full">{item.fecha}</time>
                                                                <Badge
                                                                    variant="outline"
                                                                    className={`text-[10px] font-bold uppercase tracking-wide gap-1 ${cat.kind === "estado"
                                                                        ? "border-violet-200 bg-violet-50 text-violet-800"
                                                                        : cat.kind === "traslado"
                                                                            ? "border-cyan-200 bg-cyan-50 text-cyan-800"
                                                                            : "border-slate-200 bg-slate-50 text-slate-600"
                                                                        }`}
                                                                >
                                                                    {cat.kind === "estado" ? (
                                                                        <ClipboardList className="h-3 w-3" />
                                                                    ) : cat.kind === "traslado" ? (
                                                                        <Send className="h-3 w-3" />
                                                                    ) : (
                                                                        <FileText className="h-3 w-3" />
                                                                    )}
                                                                    {cat.label}
                                                                </Badge>
                                                            </div>
                                                            {item.riesgo_ia && (
                                                                <Badge
                                                                    className={
                                                                        item.riesgo_ia === "Alto"
                                                                            ? "bg-rose-50 text-rose-700 border-rose-100"
                                                                            : item.riesgo_ia === "Medio"
                                                                                ? "bg-amber-50 text-amber-700 border-amber-100"
                                                                                : "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                                    }
                                                                >
                                                                    Riesgo {item.riesgo_ia}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <h3 className="font-bold text-gray-900 leading-snug text-[15px]">{item.actuacion}</h3>
                                                        {item.descripcion && (
                                                            <p className="text-sm text-gray-600 mt-2 leading-relaxed whitespace-pre-wrap">{item.descripcion}</p>
                                                        )}

                                                        {/* Linked documents from Archivos */}
                                                        {(() => {
                                                            const linkedFiles = getFilesForActuacion(item);
                                                            if (!linkedFiles.length) return null;
                                                            return (
                                                                <div className="mt-3 flex flex-col gap-1.5">
                                                                    {linkedFiles.map((f) => (
                                                                        <button
                                                                            key={f.id}
                                                                            onClick={() => {
                                                                                setHighlightedFileId(f.id);
                                                                                setActiveSection("archivos");
                                                                            }}
                                                                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50/80 border border-emerald-100 hover:bg-emerald-100 hover:border-emerald-200 transition-all text-left group"
                                                                        >
                                                                            <Paperclip className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                                                            <span className="text-xs font-medium text-emerald-800 truncate">{f.file_name}</span>
                                                                            <ExternalLink className="h-3 w-3 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-auto" />
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}

                                                        {item.accion_ia && (
                                                            <div className="mt-4 p-3 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-xl border border-blue-100 flex items-start gap-3">
                                                                <Zap className="h-4 w-4 text-legal-blue shrink-0 mt-0.5" />
                                                                <div>
                                                                    <p className="text-[10px] font-bold text-legal-blue uppercase tracking-wider mb-1">Acción sugerida (IA)</p>
                                                                    <p className="text-xs text-legal-navy/80 font-medium">{item.accion_ia}</p>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {(item.has_file || item.document_url) && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="mt-3 h-8 w-full rounded-lg bg-gray-50 hover:bg-white border border-gray-100 text-gray-600"
                                                                onClick={() => {
                                                                    const docUrl = item.document_url || item.anexo_path;
                                                                    if (docUrl) {
                                                                        setHighlightedFileId(docUrl);
                                                                        setActiveSection("archivos");
                                                                    }
                                                                }}
                                                            >
                                                                <Paperclip className="h-3.5 w-3.5 mr-2" /> Ver anexo
                                                            </Button>
                                                        )}
                                                    </Card>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ====== ANÁLISIS IA PRO ====== */}
                        {activeSection === "analisis" && (
                            <div id="analisis-ia-pro-section" className="space-y-6">
                                {!hasProAccess ? (
                                    <Card className="border border-purple-200/80 bg-gradient-to-b from-purple-50/50 via-white to-indigo-50/40 shadow-xl rounded-3xl overflow-hidden p-8 sm:p-12 text-center">
                                        <div className="max-w-xl mx-auto space-y-6">
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-legal-blue via-purple-600 to-indigo-700 flex items-center justify-center mx-auto text-white shadow-lg shadow-purple-500/25">
                                                <Brain className="h-8 w-8" />
                                            </div>
                                            <div className="space-y-2">
                                                <Badge className="bg-purple-100 text-purple-800 border border-purple-200 font-bold text-[10px] uppercase tracking-wider px-3 py-1 rounded-full">
                                                    Acceso Exclusivo • Planes Profesional &amp; Empresarial
                                                </Badge>
                                                <h2 className="text-2xl font-black text-gray-900 tracking-tight">Estrategia Legal IA</h2>
                                                <p className="text-sm text-gray-600 leading-relaxed">
                                                    Esta función avanzada requiere una suscripción a nuestros planes <strong>Profesional</strong> o <strong>Empresarial</strong>. Analiza automáticamente el expediente completo con IA para extraer estrategias procesales, causales de nulidad y vulnerabilidades de la contraparte.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left pt-2">
                                                <div className="p-4 bg-white rounded-2xl border border-purple-100 shadow-sm">
                                                    <Shield className="h-5 w-5 text-purple-600 mb-2" />
                                                    <h4 className="text-xs font-bold text-gray-900">Nulidades y Riesgos</h4>
                                                    <p className="text-[10px] text-gray-500 mt-1">Detección de causales de nulidad y debilidades procesales.</p>
                                                </div>
                                                <div className="p-4 bg-white rounded-2xl border border-purple-100 shadow-sm">
                                                    <TrendingUp className="h-5 w-5 text-legal-blue mb-2" />
                                                    <h4 className="text-xs font-bold text-gray-900">Estrategia por Rol</h4>
                                                    <p className="text-[10px] text-gray-500 mt-1">Acciones tácticas adaptadas según si eres demandante, demandado o tercero.</p>
                                                </div>
                                                <div className="p-4 bg-white rounded-2xl border border-purple-100 shadow-sm">
                                                    <Sparkles className="h-5 w-5 text-amber-500 mb-2" />
                                                    <h4 className="text-xs font-bold text-gray-900">Fundamentación IA</h4>
                                                    <p className="text-[10px] text-gray-500 mt-1">Base jurisprudencial y legal para sostener la estrategia.</p>
                                                </div>
                                            </div>

                                            <div className="pt-4">
                                                <Button
                                                    onClick={() => navigate("/planes")}
                                                    className="rounded-2xl bg-gradient-to-r from-legal-blue via-purple-600 to-indigo-700 hover:opacity-95 text-white font-bold px-8 shadow-xl shadow-purple-500/25 h-12 text-sm"
                                                >
                                                    <Sparkles className="h-4 w-4 mr-2" /> Mejorar a Plan Profesional o Empresarial
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                ) : (
                                    <>
                                        {timeline.length === 0 && processFiles.length === 0 && (
                                            <div className="p-4 bg-amber-50/90 border border-amber-200/90 rounded-2xl flex items-center gap-3 text-amber-900 shadow-sm animate-fade-in mb-4">
                                                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                                                <div className="text-xs">
                                                    <p className="font-bold text-amber-950">Información insuficiente para análisis</p>
                                                    <p className="text-amber-800/90 mt-0.5">
                                                        No hay actuaciones ni archivos cargados en este expediente. Para ejecutar la <strong>Estrategia Legal IA</strong> se requiere al menos una actuación o documento adjunto.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div>
                                                <h2 className="text-xl font-bold text-gray-900">
                                                    {scraperApi.getRamaLabel((analysis as any)?.rama_derecho)
                                                        ? `🛡️ ${scraperApi.getRamaLabel((analysis as any)?.rama_derecho)} — Estrategia Procesal`
                                                        : "🛡️ Análisis Estratégico IA"}
                                                </h2>
                                                <p className="text-sm text-gray-500">
                                                    Nulidades, vulnerabilidades de la contraparte y acciones tácticas según la rama del derecho
                                                </p>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
                                                <div className="w-48">
                                                    <Select value={clientRole} onValueChange={setClientRole}>
                                                        <SelectTrigger className="w-full bg-white h-10 border-purple-200">
                                                            <SelectValue placeholder="Selecciona tu rol" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="neutral">Neutral</SelectItem>
                                                            <SelectItem value="demandante">Demandante / Accionante</SelectItem>
                                                            <SelectItem value="demandado">Demandado / Accionado</SelectItem>
                                                            <SelectItem value="tercero">Tercero Interesado</SelectItem>
                                                            <SelectItem value="victima">Víctima</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <Button
                                                    onClick={handleRunAnalysis}
                                                    disabled={isAnalyzing || (timeline.length === 0 && processFiles.length === 0)}
                                                    title={(timeline.length === 0 && processFiles.length === 0) ? "El expediente no cuenta con actuaciones ni archivos para analizar" : undefined}
                                                    className={cn(
                                                        "rounded-xl transition-all h-10 px-4 font-bold text-xs shadow-md",
                                                        (timeline.length === 0 && processFiles.length === 0) || isAnalyzing
                                                            ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed shadow-none"
                                                            : "bg-gradient-to-r from-legal-blue to-purple-600 hover:scale-105 text-white shadow-purple-200"
                                                    )}
                                                >
                                                    {isAnalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Brain className="h-4 w-4 mr-2" />}
                                                    {analysis ? "Recalcular Análisis" : "Ejecutar Análisis"}
                                                </Button>
                                            </div>
                                        </div>

                                        {analysis && (
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                <Card className="p-4 border border-indigo-100 bg-indigo-50/40">
                                                    <p className="text-[10px] uppercase font-bold text-indigo-500">Actuaciones evaluadas</p>
                                                    <p className="text-2xl font-bold text-indigo-700">{uniqueTimeline.length}</p>
                                                </Card>
                                                <Card className="p-4 border border-emerald-100 bg-emerald-50/40">
                                                    <p className="text-[10px] uppercase font-bold text-emerald-500">Acciones sugeridas</p>
                                                    <p className="text-2xl font-bold text-emerald-700">{(analysis?.tareas_estrategicas || []).length}</p>
                                                </Card>
                                                <Card className="p-4 border border-amber-100 bg-amber-50/40">
                                                    <p className="text-[10px] uppercase font-bold text-amber-500">Cobertura de riesgo</p>
                                                    <p className="text-2xl font-bold text-amber-700">{(analysis?.riesgo_breakdown || []).length} frentes</p>
                                                </Card>
                                            </div>
                                        )}

                                        {!analysis && !isAnalyzing ? (
                                            <Card className="py-20 text-center bg-gradient-to-br from-white to-gray-50 border-dashed border-2">
                                                <div className="bg-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                                                    <Brain className="h-8 w-8 text-purple-600" />
                                                </div>
                                                <h3 className="text-lg font-bold text-gray-900">¿Listo para el Poder de la IA?</h3>
                                                <p className="text-gray-500 max-w-md mx-auto mt-2 mb-8 text-sm">
                                                    Nuestra IA experta analizará todas las actuaciones del proceso, identificará riesgos ocultos y te dará probabilidades de éxito basadas en jurisprudencia similar.
                                                </p>

                                                <Button onClick={handleRunAnalysis} className="rounded-xl bg-white text-purple-600 border border-purple-200 hover:bg-purple-50 px-8">
                                                    Comenzar Análisis Estratégico
                                                </Button>
                                            </Card>
                                        ) : isAnalyzing ? (
                                            <div className="space-y-6">
                                                <Card className="p-10 text-center animate-pulse border-purple-100 bg-purple-50/10">
                                                    <div className="flex justify-center mb-4">
                                                        <Sparkles className="h-10 w-10 text-purple-400 animate-bounce" />
                                                    </div>
                                                    <h3 className="text-lg font-bold text-purple-900">Procesando Expediente...</h3>
                                                    <p className="text-purple-600/70 text-sm max-w-xs mx-auto">La IA está leyendo las actuaciones y calculando la matriz de riesgos stratégicos.</p>
                                                </Card>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                                {/* Risk Score & Stats */}
                                                <div className="lg:col-span-1 space-y-6">
                                                    <Card className="p-6 relative overflow-hidden bg-white border-none shadow-premium group">
                                                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                                            <Shield className="h-24 w-24 text-legal-blue" />
                                                        </div>
                                                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Puntuación de Riesgo</h3>
                                                        <div className="flex flex-col items-center py-4">
                                                            <div className="relative flex items-center justify-center">
                                                                <svg className="h-40 w-40 transform -rotate-90">
                                                                    <circle cx="80" cy="80" r="70" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                                                                    <circle cx="80" cy="80" r="70" fill="transparent" stroke={analysis?.riesgo_score && analysis.riesgo_score > 70 ? '#f43f5e' : analysis?.riesgo_score && analysis.riesgo_score > 40 ? '#f59e0b' : '#3b82f6'} strokeWidth="12" strokeDasharray={440} strokeDashoffset={440 - (440 * (analysis?.riesgo_score || 0)) / 100} strokeLinecap="round" className="transition-all duration-1000 ease-out" />
                                                                </svg>
                                                                <div className="absolute flex flex-col items-center">
                                                                    <span className="text-4xl font-black text-gray-900">{analysis?.riesgo_score || 0}</span>
                                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Sobre 100</span>
                                                                </div>
                                                            </div>
                                                            <Badge className={`mt-4 px-4 py-1 rounded-full ${analysis?.riesgo_score && analysis.riesgo_score > 70 ? 'bg-rose-100 text-rose-700' : analysis?.riesgo_score && analysis.riesgo_score > 40 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} border-none font-bold`}>
                                                                {analysis?.riesgo_score && analysis.riesgo_score > 70 ? 'RIESGO CRÍTICO' : analysis?.riesgo_score && analysis.riesgo_score > 40 ? 'RIESGO MODERADO' : 'RIESGO BAJO'}
                                                            </Badge>
                                                        </div>
                                                        <div className="mt-6 space-y-3">
                                                            {(Array.isArray(analysis?.riesgo_breakdown) ? analysis.riesgo_breakdown : []).map((item, idx) => (
                                                                <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-gray-50/50 border border-gray-100">
                                                                    <span className="text-xs font-semibold text-gray-600">
                                                                        {item.label || (item as any).nombre || (item as any).categoria || `Factor ${idx + 1}`}
                                                                    </span>
                                                                    <span className={`text-xs font-bold ${item.puntos > 20 ? 'text-rose-500' : 'text-legal-blue'}`}>+{item.puntos ?? 0} pts</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </Card>

                                                    <Card className="p-6 bg-gradient-to-br from-legal-navy to-gray-900 text-white border-none shadow-premium relative overflow-hidden">
                                                        <TrendingUp className="absolute -bottom-4 -right-4 h-24 w-24 text-white/5 rotate-12" />
                                                        <h3 className="text-sm font-bold text-white/60 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                            <Sparkles className="h-4 w-4" /> Probabilidades de Éxito
                                                        </h3>
                                                        <div className="space-y-6">
                                                            {Object.entries(analysis?.probabilidades_exito || {}).map(([escenario, prob], idx) => {
                                                                const probFormatted = scraperApi.formatProbabilidad(prob);
                                                                return (
                                                                    <div key={idx} className="space-y-2">
                                                                        <div className="flex justify-between text-xs font-bold uppercase tracking-wide">
                                                                            <span>{escenario.replace(/_/g, " ")}</span>
                                                                            <span className="text-legal-teal">{probFormatted}</span>
                                                                        </div>
                                                                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                                                            <div className="h-full bg-gradient-to-r from-legal-blue to-legal-teal transition-all duration-1000" style={{ width: probFormatted }}></div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </Card>
                                                </div>

                                                {/* Summary & Tasks */}
                                                <div className="lg:col-span-2 space-y-6">
                                                    {/* Nuevo: Reloj Procesal Colombiano */}
                                                    {analysis?.reloj_procesal && (
                                                        <RelojProcesalCard 
                                                            reloj={analysis.reloj_procesal}
                                                            onGenerateDraft={handleGenerateDraft} 
                                                            onActionClick={() => {
                                                                const actionTitle = analysis.reloj_procesal?.termino_critico?.includes("Libertad") 
                                                                    ? "Solicitud de Libertad Inmediata" 
                                                                    : "Impulso Procesal por Términos";
                                                                handleCreateTask({
                                                                    titulo: actionTitle,
                                                                    descripcion: analysis.reloj_procesal?.recomendacion_reloj || "Acción urgente por reloj procesal",
                                                                    prioridad: "alta"
                                                                });
                                                            }}
                                                        />
                                                    )}

                                                    {/* Nuevo: Matriz Probatoria Forense */}
                                                    {analysis?.matriz_probatoria && (
                                                        <MatrizProbatoriaPanel matriz={analysis.matriz_probatoria} onGenerateDraft={handleGenerateDraft} />
                                                    )}

                                                    <Card className="p-8 border-none shadow-premium bg-white">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                                            <Shield className="h-5 w-5 text-purple-500" /> Estrategia Procesal IA
                                                        </h3>
                                                        <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed font-semibold border-l-4 border-purple-200 pl-4 py-1">
                                                            {renderSafeText((analysis as any)?.resumen || (analysis as any)?.resumen_legal)}
                                                        </div>

                                                        {/* Ataques Disponibles */}
                                                        {scraperApi.getAnalysisList(analysis as any, "ataques_disponibles", "ataques_inmediatos", "ataques").length > 0 && (
                                                            <div className="mt-8 space-y-3">
                                                                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                                                    <Zap className="h-3 w-3 text-amber-500" /> 🔥 Acciones Tácticas Inmediatas
                                                                </h4>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {scraperApi.getAnalysisList(analysis as any, "ataques_disponibles", "ataques_inmediatos", "ataques").map((ataque: any, i: number) => (
                                                                        <Badge key={i} className="bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-lg">
                                                                            ✔ {typeof ataque === 'object' ? (ataque.punto || ataque.label || JSON.stringify(ataque)) : ataque}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Card>

                                                    {/* Pronóstico de Fallo del Juez / Juzgado (Hero Banner) */}
                                                    <Card className="p-6 bg-gradient-to-r from-indigo-900 via-indigo-850 to-purple-900 text-white rounded-2xl border-none shadow-xl relative overflow-hidden group">
                                                        <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                                                            <Scale className="h-32 w-32 text-indigo-200" />
                                                        </div>
                                                        <div className="relative z-10 space-y-3">
                                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                                <div className="flex items-center gap-2.5">
                                                                    <div className="p-2 rounded-xl bg-white/10 text-indigo-200 backdrop-blur-md">
                                                                        <Scale className="h-5 w-5" />
                                                                    </div>
                                                                    <h3 className="text-xs font-black uppercase tracking-widest text-indigo-200">
                                                                        ⚖️ Pronóstico de Fallo del Juez / Juzgado
                                                                    </h3>
                                                                </div>
                                                                <Badge className="bg-indigo-500/30 text-indigo-100 border border-indigo-400/30 text-[10px] uppercase font-bold px-3 py-1 rounded-full">
                                                                    Resultado Estimado
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm md:text-base text-indigo-50 leading-relaxed font-medium pt-1">
                                                                {renderSafeText(
                                                                    (analysis as any)?.pronostico_fallo_juez || (analysis as any)?.posible_fallo_juez || (analysis as any)?.recomendacion_general,
                                                                    "El juez o juzgado resolverá aplicando las normas vigentes de la materia y el material probatorio aportado por las partes."
                                                                )}
                                                            </p>
                                                        </div>
                                                    </Card>

                                                    {/* Nulidades y Debilidades (Grilla Espaciosa de 2 Columnas) */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        {/* Oportunidades de Nulidad */}
                                                        <Card className="p-5 border border-rose-100 bg-rose-50/30 rounded-2xl shadow-sm space-y-3">
                                                            <h4 className="text-xs font-black text-rose-600 uppercase tracking-widest flex items-center gap-2">
                                                                <AlertCircle className="h-4 w-4 text-rose-600" /> 🔴 Oportunidades de Nulidad
                                                            </h4>
                                                            <div className="space-y-2.5">
                                                                {(Array.isArray((analysis as any)?.oportunidades_nulidad) ? (analysis as any).oportunidades_nulidad : []).length > 0 ? (
                                                                    (analysis as any).oportunidades_nulidad.map((point: any, i: number) => {
                                                                        const pointText = typeof point === 'object' ? (point.punto || point.label || JSON.stringify(point)) : String(point);
                                                                        return (
                                                                            <div key={i} className="text-xs p-3.5 bg-white rounded-xl border border-rose-200/70 text-rose-950 flex flex-col gap-2.5 shadow-xs font-medium hover:border-rose-300 transition-all">
                                                                                <div className="flex items-start gap-2.5">
                                                                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-rose-500" />
                                                                                    <span className="flex-1 leading-relaxed">{pointText}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between pt-2 border-t border-rose-100/60">
                                                                                    <span className="text-[10px] text-rose-600/80 font-bold uppercase tracking-wider">Causal Procesal</span>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={() => handleGenerateDraft({
                                                                                            titulo: `Incidente de Nulidad Procesal`,
                                                                                            descripcion: `Promover incidente de nulidad procesal fundamentado en: ${pointText}. Exigir restablecimiento del debido proceso y reposicion de actuaciones.`,
                                                                                            accion: `Incidente de Nulidad: ${pointText}`,
                                                                                            objetivo: `Declaratoria de nulidad procesal y saneamiento de vicios sustanciales`
                                                                                        })}
                                                                                        className="h-7 px-3 text-[11px] rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-200 transition-all font-semibold"
                                                                                    >
                                                                                        <Zap className="h-3 w-3 mr-1" />  Redactar Incidente
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic p-3">No se detectaron nulidades procesales evidentes.</p>
                                                                )}
                                                            </div>
                                                        </Card>

                                                        {/* Debilidades de la contraparte */}
                                                        <Card className="p-5 border border-amber-100 bg-amber-50/30 rounded-2xl shadow-sm space-y-3">
                                                            <h4 className="text-xs font-black text-amber-700 uppercase tracking-widest flex items-center gap-2">
                                                                <Search className="h-4 w-4 text-amber-600" /> ⚠️ Debilidades de la Contraparte
                                                            </h4>
                                                            <div className="space-y-2.5">
                                                                {scraperApi.getAnalysisList(analysis as any, "debilidades_acusacion", "debilidades_parte_contraria", "debilidades").length > 0 ? (
                                                                    scraperApi.getAnalysisList(analysis as any, "debilidades_acusacion", "debilidades_parte_contraria", "debilidades").map((point: any, i: number) => {
                                                                        const pointText = typeof point === 'object' ? (point.punto || point.label || JSON.stringify(point)) : String(point);
                                                                        return (
                                                                            <div key={i} className="text-xs p-3.5 bg-white rounded-xl border border-amber-200/70 text-amber-950 flex flex-col gap-2.5 shadow-xs font-medium hover:border-amber-300 transition-all">
                                                                                <div className="flex items-start gap-2.5">
                                                                                    <Search className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                                                                                    <span className="flex-1 leading-relaxed">{pointText}</span>
                                                                                </div>
                                                                                <div className="flex items-center justify-between pt-2 border-t border-amber-100/60">
                                                                                    <span className="text-[10px] text-amber-700/80 font-bold uppercase tracking-wider">Ventaja Tactica</span>
                                                                                    <Button
                                                                                        size="sm"
                                                                                        onClick={() => handleGenerateDraft({
                                                                                            titulo: `Memorial de Oposicion y Ataque Procesal`,
                                                                                            descripcion: `Atacar la debilidad de la contraparte consistente en: ${pointText}. Solicitar rechazo o exclusion probatoria.`,
                                                                                            accion: `Atacar debilidad de contraparte: ${pointText}`,
                                                                                            objetivo: `Debilitar la teoría del caso de la contraparte y solicitar decision judicial favorable`
                                                                                        })}
                                                                                        className="h-7 px-3 text-[11px] rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-600 hover:text-white border border-amber-200 transition-all font-semibold"
                                                                                    >
                                                                                        <Zap className="h-3 w-3 mr-1" />  Atacar / Oponerse
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <p className="text-xs text-gray-400 italic p-3">Sin debilidades identificadas en este analisis.</p>
                                                                )}
                                                            </div>
                                                        </Card>
                                                    </div>

                                                    <h3 className="text-lg font-bold text-gray-900 mt-8 mb-4 flex items-center gap-2">
                                                        <Brain className="h-5 w-5 text-emerald-500" /> Acciones Estratégicas Sugeridas (Top 4)
                                                    </h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {(() => {
                                                            const allTasks = Array.isArray(analysis?.tareas_estrategicas) ? analysis.tareas_estrategicas : [];
                                                            const visibleTasks = showAllActions ? allTasks : allTasks.slice(0, 4);
                                                            return visibleTasks.map((task: any, idx) => (
                                                            <Card key={idx} className="p-5 border-none shadow-sm bg-gradient-to-br from-white to-gray-50/30 hover:shadow-md transition-shadow group">
                                                                <div className="flex justify-between items-start mb-3">
                                                                    <Badge className={`${task.prioridad === 'alta' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                                                                        } border-none px-3 py-1 text-[10px] font-black uppercase tracking-widest`}>
                                                                        {task.prioridad === 'alta' ? 'URGENTE / CRÍTICO' : 'DEFENSA PROACTIVA'}
                                                                    </Badge>
                                                                    <span className="text-[10px] font-bold text-gray-400">
                                                                        {task.plazo_dias ? `${task.plazo_dias} días` : (task.plazo || (task as any).plazo_recomendado || "—")}
                                                                    </span>
                                                                </div>
                                                                <h4 className="font-bold text-gray-900 text-sm mb-2 group-hover:text-legal-blue transition-colors">
                                                                    {renderSafeText(task.titulo || task.accion)}
                                                                </h4>
                                                                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                                                                    {renderSafeText(task.descripcion || task.objetivo)}
                                                                </p>

                                                                {(task.justificacion || (task as any).fundamento_juridico) && (
                                                                    <div className="bg-blue-50/50 p-2.5 rounded-lg mb-4 text-[10px] text-blue-800 border-l-2 border-blue-400">
                                                                        <strong>Base Legal:</strong> {renderSafeText(task.justificacion || (task as any).fundamento_juridico)}
                                                                    </div>
                                                                )}

                                                                <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100/50">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[9px] uppercase font-black text-gray-300">Impacto</span>
                                                                        <span className="text-[10px] font-bold text-emerald-600">{task.impacto || (task as any).impacto_esperado || "Alto impacto"}</span>
                                                                    </div>
                                                                    <div className="flex gap-1.5">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={() => {
                                                                                setSelectedActionForFeedback(task);
                                                                                setFeedbackReason("");
                                                                                setFeedbackRelatedActuacion("");
                                                                                setFeedbackRelatedPrueba("");
                                                                                setIsFeedbackDialogOpen(true);
                                                                            }}
                                                                            className="h-8 rounded-lg bg-gray-50 border border-gray-200 text-gray-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all text-xs"
                                                                            title="Esta acción no aplica"
                                                                        >
                                                                            <X className="h-3.5 w-3.5 mr-1" /> No aplica
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            onClick={() => handleGenerateDraft(task)}
                                                                            className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all text-xs shadow-xs"
                                                                        >
                                                                            <Zap className="h-3 w-3 mr-1.5" />  Redactar Memorial
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </Card>
                                                            ));
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* ====== RAG CHAT (Integrated) ====== */}
                        {activeSection === "rag" && (
                            <div className="h-[600px] flex flex-col animate-in fade-in slide-in-from-right-4 duration-500">
                                <Card className="flex-1 border-none shadow-premium bg-white flex flex-col overflow-hidden">
                                    <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-legal-blue/5 to-transparent">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-legal-blue to-legal-navy flex items-center justify-center shadow-lg shadow-blue-100">
                                                <MessageSquare className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-900 leading-none">Asistente de Expediente</h3>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">Conecta con tus PDF anexos</p>
                                            </div>
                                        </div>
                                        <Badge className="bg-emerald-50 text-emerald-700 border-none pulse">IA ACTIVA</Badge>
                                    </div>

                                    <ScrollArea className="flex-1 p-6">
                                        {chatMessages.length === 0 ? (
                                            <div className="h-full flex flex-col items-center justify-center text-center py-20 opacity-40">
                                                <Sparkles className="h-12 w-12 text-legal-blue mb-4" />
                                                <h4 className="text-lg font-bold text-legal-navy">Consulta Inteligente</h4>
                                                <p className="text-sm max-w-xs mx-auto">Haz preguntas específicas sobre los anexos cargados en este expediente.</p>
                                                <div className="mt-8 flex flex-wrap justify-center gap-2">
                                                    {["¿Cuál es la cuantía?", "¿Hay medidas cautelares?", "¿Quiénes son los testigos?"].map(q => (
                                                        <button key={q} onClick={() => setChatInput(q)} className="text-[10px] font-bold py-2 px-3 bg-gray-100 rounded-full hover:bg-legal-blue hover:text-white transition-colors">
                                                            {q}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {chatMessages.map((msg, i) => (
                                                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`}>
                                                        <div className={`max-w-[80%] p-4 rounded-2xl text-sm ${msg.role === 'user'
                                                            ? 'bg-legal-blue text-white rounded-tr-none shadow-md'
                                                            : 'bg-gray-100 text-gray-800 rounded-tl-none font-medium'
                                                            }`}>
                                                            {msg.role === 'user' ? (
                                                                msg.content
                                                            ) : (
                                                                <div className="space-y-3">
                                                                    <div className="prose prose-sm max-w-none text-inherit leading-relaxed">
                                                                        <ReactMarkdown
                                                                            remarkPlugins={[remarkGfm]}
                                                                            components={{
                                                                                a: ({ href, children }) => {
                                                                                    if (href?.startsWith('#source-')) {
                                                                                        const idx = parseInt(href.replace('#source-', ''), 10);
                                                                                        const cit = msg.citations?.find(c => c.index === idx);
                                                                                        if (cit) {
                                                                                            return (
                                                                                                <Tooltip>
                                                                                                    <TooltipTrigger asChild>
                                                                                                        <a href={href} className="inline-flex items-center justify-center font-bold text-legal-blue hover:underline mx-0.5">
                                                                                                            {children}
                                                                                                        </a>
                                                                                                    </TooltipTrigger>
                                                                                                    <TooltipContent className="max-w-[320px] p-3 text-xs leading-relaxed bg-slate-900 border border-slate-800 text-white rounded-xl shadow-xl z-50">
                                                                                                        <p className="whitespace-normal break-words">
                                                                                                            {cit.snippet}
                                                                                                        </p>
                                                                                                    </TooltipContent>
                                                                                                </Tooltip>
                                                                                            );
                                                                                        }
                                                                                    }
                                                                                    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                                                                                }
                                                                            }}
                                                                        >
                                                                            {msg.content ? msg.content.replace(/\[(\d+)\]/g, "[$1](#source-$1)") : ""}
                                                                        </ReactMarkdown>
                                                                    </div>

                                                                    {msg.citations && msg.citations.length > 0 && (
                                                                        <div className="mt-3 pt-3 border-t border-gray-200/50 flex flex-col gap-1.5">
                                                                            <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider flex items-center gap-1">
                                                                                <Paperclip className="h-3 w-3" /> Fuentes Citadas
                                                                            </span>
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {msg.citations.map((cit, idx) => (
                                                                                    <Tooltip key={idx}>
                                                                                        <TooltipTrigger asChild>
                                                                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 bg-white hover:bg-gray-200 border border-gray-200 text-gray-700 rounded-lg cursor-pointer transition-all shadow-sm">
                                                                                                <span className="flex items-center justify-center h-4 w-4 bg-legal-blue text-white rounded-full text-[9px] font-bold">
                                                                                                    {cit.index}
                                                                                                </span>
                                                                                                <span className="max-w-[120px] truncate">{cit.source}</span>
                                                                                                <span className="text-[9px] font-normal text-gray-400">
                                                                                                    ({Math.round(cit.similarity * 100)}%)
                                                                                                </span>
                                                                                            </span>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent className="max-w-[320px] p-3 text-xs leading-relaxed bg-slate-900 border border-slate-800 text-white rounded-xl shadow-xl z-50">
                                                                                            <p className="whitespace-normal break-words">
                                                                                                {cit.snippet}
                                                                                            </p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                {isChatting && (
                                                    <div className="flex justify-start">
                                                        <div className="bg-gray-100 p-4 rounded-2xl rounded-tl-none flex items-center gap-2">
                                                            <Loader2 className="h-4 w-4 animate-spin text-legal-blue" />
                                                            <span className="text-xs font-bold text-gray-400">La IA está escribiendo...</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </ScrollArea>

                                    <div className="p-4 bg-gray-50 border-t border-gray-100">
                                        <div className="relative">
                                            <Input
                                                value={chatInput}
                                                onChange={e => setChatInput(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                                                placeholder="Pregunta algo sobre el expediente..."
                                                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-legal-blue/20 bg-white pr-12 h-12"
                                            />
                                            <Button
                                                size="icon"
                                                onClick={handleSendMessage}
                                                disabled={!chatInput.trim() || isChatting}
                                                className="absolute right-1 top-1 h-10 w-10 rounded-lg bg-legal-blue shadow-lg shadow-blue-200"
                                            >
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        )}

                        {/* ====== CLIENTES ====== */}
                        {activeSection === "clientes" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Clientes Vinculados</h2>
                                        <p className="text-sm text-gray-400">Clientes asociados a este expediente</p>
                                    </div>
                                    <Dialog open={isLinkClientOpen} onOpenChange={setIsLinkClientOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" size="sm">
                                                <Plus className="h-4 w-4 mr-1.5" /> Vincular Cliente
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl">
                                            <DialogHeader>
                                                <DialogTitle>Vincular Cliente al Expediente</DialogTitle>
                                            </DialogHeader>
                                            <div className="py-4 space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Seleccionar Cliente</Label>
                                                    <Select value={selectedClientToLink} onValueChange={setSelectedClientToLink}>
                                                        <SelectTrigger className="rounded-xl">
                                                            <SelectValue placeholder="Busca un cliente..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {clients.map(c => (
                                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Rol del Cliente en el Proceso</Label>
                                                    <Select value={selectedRoleToLink} onValueChange={setSelectedRoleToLink}>
                                                        <SelectTrigger className="rounded-xl">
                                                            <SelectValue placeholder="Selecciona el rol..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="Demandante">Demandante / Accionante</SelectItem>
                                                            <SelectItem value="Demandado">Demandado / Accionado</SelectItem>
                                                            <SelectItem value="Tercero Interesado">Tercero Interesado</SelectItem>
                                                            <SelectItem value="Víctima">Víctima</SelectItem>
                                                            <SelectItem value="Apoderado">Apoderado / Representante</SelectItem>
                                                            <SelectItem value="Cliente General">Cliente General</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="ghost" className="rounded-xl" onClick={() => setIsLinkClientOpen(false)}>Cancelar</Button>
                                                <Button className="rounded-xl bg-legal-blue" onClick={handleLinkClient}>Vincular</Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-gray-100">
                                                    <TableHead className="text-gray-500 font-medium">Cliente</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Email</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Teléfono</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Rol</TableHead>
                                                    <TableHead className="text-gray-500 font-medium text-right">Acciones</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {effectiveClientName ? (
                                                    <TableRow className="border-gray-100 hover:bg-blue-50/30 transition-colors">
                                                        <TableCell className="font-medium">
                                                            <div className="flex items-center gap-3">
                                                                <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center font-bold text-xs">
                                                                    {effectiveClientName.substring(0, 2).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <span className="font-semibold text-gray-900 block">{effectiveClientName}</span>
                                                                    {linkedClientObj && <span className="text-[10px] text-emerald-600 font-medium">Cliente Registrado</span>}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-gray-500">{effectiveClientEmail || linkedClientObj?.email || "—"}</TableCell>
                                                        <TableCell className="text-gray-500">{effectiveClientPhone || linkedClientObj?.phone || "—"}</TableCell>
                                                        <TableCell>
                                                            <Badge className={cn(
                                                                "border px-2 py-0.5 text-xs font-semibold rounded-lg",
                                                                effectiveClientRole.toLowerCase().includes("demandado") || effectiveClientRole.toLowerCase().includes("accionado")
                                                                    ? "bg-rose-50 text-rose-700 border-rose-200"
                                                                    : effectiveClientRole.toLowerCase().includes("tercero")
                                                                        ? "bg-amber-50 text-amber-700 border-amber-200"
                                                                        : effectiveClientRole.toLowerCase().includes("apoderado")
                                                                            ? "bg-purple-50 text-purple-700 border-purple-200"
                                                                            : "bg-blue-50 text-blue-700 border-blue-200"
                                                            )}>
                                                                {effectiveClientRole}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                title="Desvincular cliente"
                                                                className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg h-8 w-8 p-0"
                                                                onClick={handleUnlinkClient}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center py-12 text-gray-400">
                                                            <Users className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                                            <p className="text-sm">No hay clientes vinculados a este expediente.</p>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </Card>
                            </div>
                        )}

                        {/* ====== INFORME AL CLIENTE ====== */}
                        {activeSection === "informe_cliente" && (
                            <div className="space-y-6">
                                <div className="p-5 bg-gradient-to-r from-blue-50 via-indigo-50/40 to-slate-50 border border-blue-200/80 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
                                    <div className="flex items-center gap-3.5">
                                        <div className="w-11 h-11 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-md shadow-blue-500/20 shrink-0">
                                            <FileCheck className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-lg font-bold text-gray-900 tracking-tight">Informe de Gestión para el Cliente</h2>
                                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] font-bold px-2 py-0.5">
                                                    Editable &amp; Descargable
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Reporte formal automático en lenguaje claro que sintetiza las actuaciones de la Rama Judicial y las gestiones de su apoderado.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-xl border-gray-200 hover:bg-white text-gray-700 shadow-xs text-xs font-semibold"
                                            onClick={handleRegenerateClientReport}
                                            title="Regenerar con las actuaciones y datos más recientes"
                                        >
                                            <RefreshCw className="h-3.5 w-3.5 mr-1.5 text-blue-600" />
                                            Regenerar
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-xl border-gray-200 hover:bg-white text-gray-700 shadow-xs text-xs font-semibold"
                                            onClick={handleCopyClientReportText}
                                            title="Copiar texto plano al portapapeles"
                                        >
                                            <Copy className="h-3.5 w-3.5 mr-1.5 text-slate-600" />
                                            Copiar Texto
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/20 text-white text-xs font-bold"
                                            onClick={handleDownloadClientReportPdf}
                                        >
                                            <Download className="h-3.5 w-3.5 mr-1.5" />
                                            Descargar PDF
                                        </Button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="p-3.5 rounded-xl border border-slate-100 bg-white shadow-xs">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Destinatario</span>
                                        <span className="text-sm font-semibold text-gray-800 truncate block mt-0.5">
                                            {effectiveClientName || (linkedClientObj ? linkedClientObj.name : "Sin cliente vinculado")}
                                        </span>
                                    </div>
                                    <div className="p-3.5 rounded-xl border border-slate-100 bg-white shadow-xs">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Radicado Oficial</span>
                                        <span className="text-sm font-mono font-bold text-blue-700 truncate block mt-0.5">
                                            {(formData.numero || process?.case_number || id || "S.N.").replace(/\s/g, "")}
                                        </span>
                                    </div>
                                    <div className="p-3.5 rounded-xl border border-slate-100 bg-white shadow-xs">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Actuaciones Incluidas</span>
                                        <span className="text-sm font-bold text-emerald-700 block mt-0.5">
                                            {timeline.length} actuaciones registradas
                                        </span>
                                    </div>
                                </div>

                                <Card className="border border-gray-200/80 shadow-sm rounded-2xl overflow-hidden bg-white">
                                    <CardHeader className="bg-slate-50/70 border-b border-gray-100 py-3 px-5 flex flex-row items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-blue-600" />
                                            <CardTitle className="text-xs font-bold uppercase tracking-wider text-gray-600">
                                                Borrador Editable del Informe
                                            </CardTitle>
                                        </div>
                                        <span className="text-[11px] text-gray-400 font-medium">
                                            Puedes modificar cualquier párrafo antes de exportar
                                        </span>
                                    </CardHeader>
                                    <CardContent className="p-5">
                                        <div className="rounded-xl border border-gray-100 overflow-hidden shadow-inner bg-white min-h-[550px]">
                                            <JoditEditor
                                                ref={reportEditorRef}
                                                value={clientReportHtml}
                                                config={reportEditorConfig}
                                                onBlur={newContent => setClientReportHtml(newContent)}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* ====== PARTES ====== */}
                        {activeSection === "partes" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Partes del Proceso</h2>
                                        <p className="text-sm text-gray-400">Demandantes, demandados y terceros</p>
                                    </div>
                                    <Dialog open={isAddPartOpen} onOpenChange={setIsAddPartOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" size="sm">
                                                <Plus className="h-4 w-4 mr-1.5" /> Agregar parte
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl">
                                            <DialogHeader><DialogTitle>Agregar Parte al Proceso</DialogTitle></DialogHeader>
                                            <div className="py-4 space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Nombre de la Parte</Label>
                                                    <Input className="rounded-xl" placeholder="Nombre completo..." value={newPartName} onChange={e => setNewPartName(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Rol</Label>
                                                    <Select value={newPartRole} onValueChange={setNewPartRole}>
                                                        <SelectTrigger className="rounded-xl">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="demandante">Demandante</SelectItem>
                                                            <SelectItem value="demandado">Demandado</SelectItem>
                                                            <SelectItem value="tercero">Tercero Interesado</SelectItem>
                                                            <SelectItem value="apoderado">Apoderado</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="ghost" className="rounded-xl" onClick={() => setIsAddPartOpen(false)}>Cancelar</Button>
                                                <Button className="rounded-xl bg-legal-blue" onClick={handleSavePart}>Agregar</Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </div>

                                {(() => {
                                    const sujetosArray: any[] = Array.isArray(scrapperMetadata?.metadata?.sujetos)
                                        ? scrapperMetadata.metadata.sujetos
                                        : Array.isArray(scrapperMetadata?.metadata?.sujetos_procesales)
                                        ? scrapperMetadata.metadata.sujetos_procesales
                                        : Array.isArray((scrapperMetadata as any)?.sujetos)
                                        ? (scrapperMetadata as any).sujetos
                                        : Array.isArray((process as any)?.metadata?.sujetos)
                                        ? (process as any).metadata.sujetos
                                        : [];

                                    const demandantesFromSujetos = sujetosArray
                                        .filter((s: any) => {
                                            const t = String(s.tipo || "").toLowerCase();
                                            const name = String(s.nombre || "").trim();
                                            return name && (t.includes("demandante") || t.includes("accionante") || t.includes("convocante") || t.includes("ejecutante")) && !t.includes("apo") && !t.includes("apoderado");
                                        })
                                        .map((s: any) => s.nombre.trim());

                                    const demandadosFromSujetos = sujetosArray
                                        .filter((s: any) => {
                                            const t = String(s.tipo || "").toLowerCase();
                                            const name = String(s.nombre || "").trim();
                                            return name && (t.includes("demandado") || t.includes("accionado") || t.includes("convocado") || t.includes("ejecutado") || t.includes("indiciado") || t.includes("causante")) && !t.includes("apo") && !t.includes("apoderado");
                                        })
                                        .map((s: any) => s.nombre.trim());

                                    const otrosFromSujetos = sujetosArray
                                        .filter((s: any) => {
                                            const t = String(s.tipo || "").toLowerCase();
                                            const name = String(s.nombre || "").trim();
                                            if (!name || name === "Sin Tipo de Sujeto" || name === s.tipo) return false;
                                            const isDem = (t.includes("demandante") || t.includes("accionante") || t.includes("convocante") || t.includes("ejecutante")) && !t.includes("apo") && !t.includes("apoderado");
                                            const isDemd = (t.includes("demandado") || t.includes("accionado") || t.includes("convocado") || t.includes("ejecutado") || t.includes("indiciado") || t.includes("causante")) && !t.includes("apo") && !t.includes("apoderado");
                                            return !isDem && !isDemd;
                                        })
                                        .map((s: any) => ({
                                            nombre: s.nombre.trim(),
                                            tipo: s.tipo && s.tipo !== "Sin Tipo de Sujeto" && s.tipo !== "1" ? s.tipo.trim() : "Sujeto Procesal"
                                        }));

                                    const rawSujetos = scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.metadata?.sujetos_texto || scrapperMetadata?.asunto || "";
                                    const rawLines = rawSujetos.split('\n').filter((l: string) => l.trim());

                                    const parsedDemRaw = rawLines
                                        .filter((s: string) => s.toLowerCase().includes('demandante') || s.toLowerCase().includes('accionante'))
                                        .map((s: string) => s.split('\t')[1] || s.split(/\s{2,}/)[1] || s.replace(/^.*?\t|.*?\s{2,}/, ''));

                                    const parsedDemdRaw = rawLines
                                        .filter((s: string) => s.toLowerCase().includes('demandado') || s.toLowerCase().includes('indiciado') || s.toLowerCase().includes('causante'))
                                        .map((s: string) => s.split('\t')[1] || s.split(/\s{2,}/)[1] || s.replace(/^.*?\t|.*?\s{2,}/, ''));

                                    const demandantesRaw = Array.from(new Set([...demandantesFromSujetos, ...parsedDemRaw])).filter(Boolean);
                                    const demandadosRaw = Array.from(new Set([...demandadosFromSujetos, ...parsedDemdRaw])).filter(Boolean);

                                    let partesDemandante = analysis?.partes?.demandante 
                                        || scrapperMetadata?.demandante 
                                        || scrapperMetadata?.metadata?.demandante 
                                        || (process as any)?.demandante 
                                        || (demandantesFromSujetos.length > 0 ? demandantesFromSujetos : null);

                                    let partesDemandado = analysis?.partes?.demandado 
                                        || scrapperMetadata?.demandado 
                                        || scrapperMetadata?.metadata?.demandado 
                                        || (process as any)?.demandado 
                                        || (demandadosFromSujetos.length > 0 ? demandadosFromSujetos : null);

                                    if (partesDemandante || partesDemandado) {
                                        const cleanName = (name: string) => {
                                            return name
                                                .toLowerCase()
                                                .normalize("NFD")
                                                .replace(/[\u0300-\u036f]/g, "")
                                                .replace(/[^a-z0-9]/g, " ")
                                                .trim();
                                        };

                                        const nameMatchesAny = (namesList: any, rawList: string[]) => {
                                            const normalizedRaw = rawList.map(n => cleanName(n));
                                            const checkList = Array.isArray(namesList) ? namesList : [namesList];
                                            return checkList.some(name => {
                                                if (typeof name !== 'string') return false;
                                                const cleaned = cleanName(name);
                                                return normalizedRaw.some(raw => {
                                                    if (!raw || !cleaned) return false;
                                                    return raw.includes(cleaned) || cleaned.includes(raw) ||
                                                        (raw.split(" ").filter(w => w.length > 3).some(w => cleaned.includes(w)));
                                                });
                                            });
                                        };

                                        let shouldSwap = false;

                                        // 1. Data-driven check using raw subjects if available
                                        if (demandantesRaw.length > 0 || demandadosRaw.length > 0) {
                                            const demIsActuallyDefendant = partesDemandante ? nameMatchesAny(partesDemandante, demandadosRaw) : false;
                                            const demdIsActuallyPlaintiff = partesDemandado ? nameMatchesAny(partesDemandado, demandantesRaw) : false;

                                            const demIsActuallyPlaintiff = partesDemandante ? nameMatchesAny(partesDemandante, demandantesRaw) : false;
                                            const demdIsActuallyDefendant = partesDemandado ? nameMatchesAny(partesDemandado, demandadosRaw) : false;

                                            if ((demIsActuallyDefendant || demdIsActuallyPlaintiff) && !(demIsActuallyPlaintiff || demdIsActuallyDefendant)) {
                                                shouldSwap = true;
                                            }
                                        }

                                        // 2. Keyword-based fallback check if not determined by raw subjects
                                        if (!shouldSwap) {
                                            const demStr = JSON.stringify(partesDemandante || "").toLowerCase();
                                            const demdStr = JSON.stringify(partesDemandado || "").toLowerCase();

                                            const isDemDefendant = demStr.includes("demandado") || demStr.includes("ejecutado") || demStr.includes("accionado") || demStr.includes("indiciado") || demStr.includes("causante");
                                            const isDemdPlaintiff = demdStr.includes("demandante") || demdStr.includes("ejecutante") || demdStr.includes("accionante");

                                            const isDemPlaintiff = demStr.includes("demandante") || demStr.includes("ejecutante") || demStr.includes("accionante");
                                            const isDemdDefendant = demdStr.includes("demandado") || demdStr.includes("ejecutado") || demdStr.includes("accionado") || demdStr.includes("indiciado") || demdStr.includes("causante");

                                            if ((isDemDefendant || isDemdPlaintiff) && !(isDemPlaintiff || isDemdDefendant)) {
                                                shouldSwap = true;
                                            }
                                        }

                                        if (shouldSwap) {
                                            partesDemandante = analysis?.partes?.demandado;
                                            partesDemandado = analysis?.partes?.demandante;
                                        }
                                    }

                                    return (
                                        <div className="grid gap-4 md:grid-cols-2 mt-6">
                                            {/* Demandante */}
                                            <Card className="border border-gray-100/80 shadow-sm animate-fade-in-up">
                                                <CardHeader className="pb-3 text-blue-500">
                                                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                        <div className="w-7 h-7 rounded-lg bg-blue-500 flex items-center justify-center">
                                                            <Users className="h-3.5 w-3.5 text-white" />
                                                        </div>
                                                        Demandante
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    {partesDemandante ? (
                                                        <div className="space-y-2">
                                                            {Array.isArray(partesDemandante) ? (
                                                                partesDemandante?.map((name, index) => (
                                                                    <div key={index} className="p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                                                                        <p className="text-sm font-medium text-blue-900">{name}</p>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                                                                    <p className="text-sm font-medium text-blue-900">{partesDemandante}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : demandantesRaw.length > 0 ? (
                                                        <div className="space-y-2">
                                                            {demandantesRaw.map((name: string, index: number) => (
                                                                <div key={index} className="p-3 bg-blue-50/20 rounded-xl border border-dashed border-blue-200">
                                                                    <p className="text-sm font-medium text-blue-900">{name}</p>
                                                                    <p className="text-[10px] text-blue-400 font-bold uppercase mt-1">Vigilancia</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : casePartes.filter(p => (p.description || "").includes("rol=demandante")).length > 0 ? (
                                                        <div className="space-y-2">
                                                            {casePartes
                                                                .filter(p => (p.description || "").includes("rol=demandante"))
                                                                .map((p, idx) => (
                                                                    <div key={`p-dem-${idx}`} className="p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                                                                        <p className="text-sm font-medium text-blue-900">{p.title}</p>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    ) : (scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.asunto) ? (
                                                        <div className="p-3 bg-blue-50/20 rounded-xl border border-dashed border-blue-200">
                                                            <p className="text-xs text-blue-400 italic">Datos en proceso de refinamiento por IA...</p>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 text-center py-6">Sin demandantes registrados.</p>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Demandado */}
                                            <Card className="border border-gray-100/80 shadow-sm animate-fade-in-up stagger-1">
                                                <CardHeader className="pb-3 text-rose-500">
                                                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                        <div className="w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center">
                                                            <Users className="h-3.5 w-3.5 text-white" />
                                                        </div>
                                                        Demandado
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    {partesDemandado ? (
                                                        <div className="space-y-2">
                                                            {Array.isArray(partesDemandado) ? (
                                                                partesDemandado?.map((name, index) => (
                                                                    <div key={index} className="p-3 bg-rose-50/50 rounded-xl border border-rose-100/50">
                                                                        <p className="text-sm font-medium text-rose-900">{name}</p>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="p-3 bg-rose-50/50 rounded-xl border border-rose-100/50">
                                                                    <p className="text-sm font-medium text-rose-900">{partesDemandado}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : demandadosRaw.length > 0 ? (
                                                        <div className="space-y-2">
                                                            {demandadosRaw.map((name: string, index: number) => (
                                                                <div key={index} className="p-3 bg-rose-50/20 rounded-xl border border-dashed border-rose-200">
                                                                    <p className="text-sm font-medium text-rose-900">{name}</p>
                                                                    <p className="text-[10px] text-rose-400 font-bold uppercase mt-1">Vigilancia</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : casePartes.filter(p => (p.description || "").includes("rol=demandado")).length > 0 ? (
                                                        <div className="space-y-2">
                                                            {casePartes
                                                                .filter(p => (p.description || "").includes("rol=demandado"))
                                                                .map((p, idx) => (
                                                                    <div key={`p-demad-${idx}`} className="p-3 bg-rose-50/50 rounded-xl border border-rose-100/50">
                                                                        <p className="text-sm font-medium text-rose-900">{p.title}</p>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    ) : (scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.asunto) ? (
                                                        <div className="p-3 bg-rose-50/20 rounded-xl border border-dashed border-rose-200">
                                                            <p className="text-xs text-rose-400 italic">Ver 'Información de Vigilancia' abajo.</p>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 text-center py-6">Sin demandados registrados.</p>
                                                    )}
                                                </CardContent>
                                            </Card>

                                            {/* Otros Sujetos / Raw Metadata */}
                                            {(analysis?.partes?.otros || otrosFromSujetos.length > 0 || scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.asunto || casePartes.some(p => (p.description || "").includes("rol=tercero") || (p.description || "").includes("rol=apoderado"))) && (
                                                <Card className="border border-gray-100/80 shadow-sm animate-fade-in-up stagger-2 md:col-span-2">
                                                    <CardHeader className="pb-3 text-gray-500">
                                                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                                                            <div className="w-7 h-7 rounded-lg bg-gray-500 flex items-center justify-center">
                                                                <Search className="h-3.5 w-3.5 text-white" />
                                                            </div>
                                                            {analysis?.partes?.otros ? "Otros Sujetos / Terceros" : "Otros Sujetos Procesales (Vigilancia)"}
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        {analysis?.partes?.otros ? (
                                                            <div className="grid gap-2 sm:grid-cols-2">
                                                                {Array.isArray(analysis.partes.otros) ? (
                                                                    analysis.partes.otros?.map((name, index) => (
                                                                        <div key={index} className="p-3 bg-gray-50/80 rounded-xl border border-gray-100/50">
                                                                            <p className="text-xs font-medium text-gray-700">{name}</p>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <div className="p-3 bg-gray-50/80 rounded-xl border border-gray-100/50 col-span-2">
                                                                        <p className="text-xs font-medium text-gray-700">{analysis.partes.otros}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : otrosFromSujetos.length > 0 ? (
                                                            <div className="grid gap-2 sm:grid-cols-2">
                                                                {otrosFromSujetos.map((item, index) => (
                                                                    <div key={`suj-ot-${index}`} className="p-3 bg-gray-50/80 rounded-xl border border-gray-100/50">
                                                                        <p className="text-xs font-medium text-gray-800">{item.nombre}</p>
                                                                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">{item.tipo}</p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : casePartes.filter(p => (p.description || "").includes("rol=tercero") || (p.description || "").includes("rol=apoderado")).length > 0 ? (
                                                            <div className="grid gap-2 sm:grid-cols-2">
                                                                {casePartes
                                                                    .filter(p => (p.description || "").includes("rol=tercero") || (p.description || "").includes("rol=apoderado"))
                                                                    .map((p, index) => (
                                                                        <div key={`p-ot-${index}`} className="p-3 bg-gray-50/80 rounded-xl border border-gray-100/50">
                                                                            <p className="text-xs font-medium text-gray-700">{p.title}</p>
                                                                            <p className="text-[10px] text-gray-400 uppercase mt-1">{(p.description || "").includes("rol=apoderado") ? "Apoderado" : "Tercero"}</p>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        ) : (scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.asunto) ? (
                                                            <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100 border-dashed">
                                                                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Información del Sistema (Metadatos)</p>
                                                                <p className="text-sm text-gray-600 italic whitespace-pre-wrap">{scrapperMetadata?.metadata?.sujetos_completos || scrapperMetadata?.asunto}</p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 text-center py-4">No hay otros sujetos detectados.</p>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* ====== ADELANTOS ====== */}
                        {activeSection === "adelantos" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Provisiones y Gastos Financieros</h2>
                                        <p className="text-sm text-gray-400">Control de anticipos de clientes y gastos asociados al expediente</p>
                                    </div>
                                    <Dialog open={isAddAdelantoOpen} onOpenChange={setIsAddAdelantoOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-orange-600 hover:to-amber-500 shadow-md shadow-amber-500/15" size="sm">
                                                <Plus className="h-4 w-4 mr-1.5" /> Registrar Provisión/Gasto
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl">
                                            <DialogHeader><DialogTitle>Registrar Provisión o Gasto de Fondos</DialogTitle></DialogHeader>
                                            <div className="py-4 space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Concepto</Label>
                                                    <Input className="rounded-xl" placeholder="Ej: Gastos de notificación, viáticos..." value={newAdelantoConcept} onChange={e => setNewAdelantoConcept(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Valor</Label>
                                                    <Input className="rounded-xl" placeholder="$0.00" value={newAdelantoAmount} onChange={e => setNewAdelantoAmount(e.target.value)} />
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="ghost" className="rounded-xl" onClick={() => setIsAddAdelantoOpen(false)}>Cancelar</Button>
                                                <Button className="rounded-xl bg-amber-600 text-white hover:bg-amber-700" onClick={handleSaveAdelanto}>Registrar</Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                {caseAdelantos.length > 0 ? (
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                        {caseAdelantos.map((adelanto, i) => (
                                            <Card key={`manual-adelanto-${i}`} className="border border-emerald-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden relative group">
                                                <div className="absolute top-0 right-0 p-1.5 rounded-bl-xl text-[10px] font-bold uppercase bg-emerald-500 text-white">
                                                    Registrado
                                                </div>
                                                <CardContent className="p-4 pt-6">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-emerald-50">
                                                            <Coins className="h-5 w-5 text-emerald-600" />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{adelanto.created_at ? new Date(adelanto.created_at).toLocaleDateString() : "Hoy"}</p>
                                                            <p className="text-sm font-bold text-gray-900 line-clamp-2">{adelanto.title}</p>
                                                            <p className="text-xs text-emerald-700 font-semibold mt-1">
                                                                {(adelanto.description || "").replace("[ADELANTO]", "").trim()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                                                <Coins className="h-7 w-7 text-gray-300" />
                                            </div>
                                            <p className="text-gray-400 text-sm">No hay provisiones de fondos o gastos registrados en este expediente.</p>
                                            <p className="text-xs text-gray-300 mt-1 italic">Registra los gastos y adelantos recibidos usando el botón superior.</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* ====== TRÁMITES ====== */}
                        {activeSection === "tramites" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Trámites del Expediente</h2>
                                        <p className="text-sm text-gray-400">Actuaciones y gestiones asociadas</p>
                                    </div>
                                    <Dialog open={isAddTramiteOpen} onOpenChange={setIsAddTramiteOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" size="sm">
                                                <Plus className="h-4 w-4 mr-1.5" /> Nuevo tramite
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl">
                                            <DialogHeader><DialogTitle>Nuevo Trámite</DialogTitle></DialogHeader>
                                            <div className="py-4 space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Descripción del Trámite</Label>
                                                    <Input className="rounded-xl" placeholder="Ej: Pago de aranceles..." value={newTramiteTitle} onChange={e => setNewTramiteTitle(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Entidad</Label>
                                                    <Input className="rounded-xl" placeholder="Ej: Notaría, Juzgado..." value={newTramiteEntity} onChange={e => setNewTramiteEntity(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Responsable</Label>
                                                    <Input className="rounded-xl" placeholder="Nombre del abogado o asistente..." value={newTramiteResponsible} onChange={e => setNewTramiteResponsible(e.target.value)} />
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="ghost" className="rounded-xl" onClick={() => setIsAddTramiteOpen(false)}>Cancelar</Button>
                                                <Button className="rounded-xl bg-legal-blue" onClick={handleSaveTramite}>Crear</Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-gray-100">
                                                    <TableHead className="text-gray-500 font-medium">Trámite</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Entidad</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Responsable</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Vencimiento</TableHead>
                                                    <TableHead className="text-gray-500 font-medium">Estado</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {caseTramites.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center py-12 text-gray-400">
                                                            <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                                            <p className="text-sm">No hay trámites vinculados.</p>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : caseTramites.slice(0, 5).map(t => {
                                                    const entMatch = String(t.description || "").match(/entidad=([^\n]+)/);
                                                    const entVal = entMatch?.[1]?.trim();
                                                    const entity = (entVal && entVal !== "null" && entVal !== "undefined") ? entVal : "—";
                                                    return (
                                                        <TableRow key={t.id} className="border-gray-100 hover:bg-blue-50/30 transition-colors">
                                                            <TableCell className="font-medium">{t.title}</TableCell>
                                                            <TableCell className="text-gray-500">{entity}</TableCell>
                                                            <TableCell className="text-gray-500">{((t.description || "").match(/responsable=([^\n]+)/)?.[1]) || "—"}</TableCell>
                                                            <TableCell className="text-gray-500">{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</TableCell>
                                                            <TableCell>
                                                                <Badge className={
                                                                    t.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                                        t.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                                            'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                }>{t.status === 'pending' ? 'Pendiente' : t.status === 'in_progress' ? 'En Curso' : 'Completado'}</Badge>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </Card>
                            </div>
                        )}

                        {/* ====== TAREAS ====== */}
                        {activeSection === "tareas" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Tareas del Expediente</h2>
                                        <p className="text-sm text-gray-400">Pendientes y seguimiento de actividades</p>
                                    </div>
                                    <Dialog open={isAddTaskOpen} onOpenChange={setIsAddTaskOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" size="sm">
                                                <Plus className="h-4 w-4 mr-1.5" /> Nueva tarea
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl">
                                            <DialogHeader><DialogTitle>Nueva Tarea</DialogTitle></DialogHeader>
                                            <div className="py-4 space-y-4">
                                                <div className="space-y-2">
                                                    <Label>Título de la Tarea</Label>
                                                    <Input className="rounded-xl" placeholder="Ej: Revisar radicado..." value={newTaskTitle} onChange={e => setNewTaskTitle(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Vencimiento</Label>
                                                    <Input className="rounded-xl" type="date" value={newTaskDate} onChange={e => setNewTaskDate(e.target.value)} />
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <Button variant="ghost" className="rounded-xl" onClick={() => setIsAddTaskOpen(false)}>Cancelar</Button>
                                                <Button className="rounded-xl bg-legal-blue" onClick={handleSaveTask}>Asignar</Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                <Card className="border border-gray-100/80 shadow-sm">
                                    <CardContent className="p-0 divide-y divide-gray-100">
                                        {(caseTareas.length === 0 && (!analysis?.tareas_estrategicas || analysis.tareas_estrategicas.length === 0)) ? (
                                            <div className="p-12 text-center text-gray-400">
                                                <div className="w-14 h-14 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                                                    <CheckCircle2 className="h-7 w-7 text-gray-300" />
                                                </div>
                                                <p className="text-sm">No hay tareas pendientes para este expediente.</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* AI Suggested Tasks */}
                                                {analysis?.tareas_estrategicas?.map((task, i) => (
                                                    <div key={`ai-${i}`} className="p-4 hover:bg-blue-50/20 transition-all group relative overflow-hidden">
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-indigo-600"></div>
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex items-start gap-4">
                                                                <div className="mt-1">
                                                                    <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                                                                        <Sparkles className="h-5 w-5" />
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <h3 className="font-bold text-gray-900 group-hover:text-legal-blue transition-colors">
                                                                            {task.titulo || task.accion}
                                                                        </h3>
                                                                        <Badge className="bg-blue-50 text-blue-700 text-[10px] border-blue-200">Recomendación IA</Badge>
                                                                    </div>
                                                                    <p className="text-sm text-gray-500 mb-2">{task.descripcion || task.objetivo}</p>
                                                                    <div className="flex items-center gap-4 text-xs text-gray-400">
                                                                        <div className="flex items-center gap-1.5 ">
                                                                            <Clock className="h-3.5 w-3.5" />
                                                                            Plazo: <span className="text-blue-600 font-bold">{task.plazo || `${task.plazo_dias} días`}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1.5">
                                                                            <Brain className="h-3.5 w-3.5" />
                                                                            Sugerencia Automática
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col gap-2">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="rounded-lg h-8 px-3 border-blue-200 text-blue-700 hover:bg-blue-600 hover:text-white transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                                                                    onClick={() => handleAcceptSuggestion(task)}
                                                                >
                                                                    <CheckSquare className="h-3.5 w-3.5" /> Aceptar Tarea
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="rounded-lg h-8 px-3 text-gray-400 hover:text-legal-blue hover:bg-transparent"
                                                                    onClick={() => handleGenerateDraft(task)}
                                                                >
                                                                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Generar Borrador
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Local Tasks */}
                                                {caseTareas.map(t => (
                                                    <div key={t.id} className="flex items-center gap-4 p-4 hover:bg-blue-50/30 transition-colors">
                                                        <Checkbox checked={t.status === 'completed'} className="rounded-md" />
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-900 text-sm">{t.title}</p>
                                                            <p className="text-xs text-gray-400 mt-0.5">{((t.description || "").match(/responsable=([^\n]+)/)?.[1]) || "Sin asignar"}</p>
                                                        </div>
                                                        <Badge className={t.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}>
                                                            {t.status === 'completed' ? 'Completado' : 'Pendiente'}
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Hitos Procesales de la Agenda (IA) */}
                                {analysis?.adelantos && analysis.adelantos.length > 0 && (
                                    <div className="space-y-3 pt-4">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                            <Calendar className="h-4 w-4 text-indigo-500" /> Hitos de Agenda Procesal (IA)
                                        </h3>
                                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                            {analysis.adelantos.map((adelanto, i) => (
                                                <Card key={`agenda-hito-${i}`} className="border border-indigo-100/80 bg-gradient-to-br from-indigo-50/20 to-white shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden relative group">
                                                    <div className={`absolute top-0 right-0 p-1.5 rounded-bl-xl text-[9px] font-bold uppercase ${adelanto.importancia === 'Alta' ? 'bg-red-500 text-white animate-pulse' :
                                                        adelanto.importancia === 'Media' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
                                                        }`}>
                                                        {adelanto.importancia}
                                                    </div>
                                                    <CardContent className="p-4 pt-6">
                                                        <div className="flex items-start gap-3">
                                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${adelanto.importancia === 'Alta' ? 'bg-red-50 text-red-500' : 'bg-indigo-50 text-indigo-500'
                                                                }`}>
                                                                <Calendar className="h-4.5 w-4.5" />
                                                            </div>
                                                            <div className="space-y-1 min-w-0 flex-1">
                                                                <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">{adelanto.fecha}</p>
                                                                <p className="text-xs font-bold text-gray-900 leading-snug break-words" title={adelanto.evento}>{adelanto.evento}</p>
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ====== ARCHIVOS ====== */}
                        {activeSection === "archivos" && (
                            <div className="space-y-5">

                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Archivos del Expediente</h2>
                                        <p className="text-sm text-gray-400 flex items-center gap-2">
                                            <span>Documentos, anexos y evidencias vinculadas a este radicado</span>
                                            {/* {storageUsage && (
                                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full select-none ${storageUsage.percentage >= 100
                                                    ? "bg-red-100 text-red-700"
                                                    : storageUsage.percentage >= 80
                                                        ? "bg-amber-100 text-amber-700"
                                                        : "bg-gray-100 text-gray-600"
                                                    }`}>
                                                    {storageUsage.total_mb} MB / 5,000 MB usados
                                                </span>
                                            )} */}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {/* <Button
                                            variant="outline"
                                            className="rounded-xl border-blue-500 text-blue-600 hover:bg-blue-50"
                                            size="sm"
                                            disabled={isUploading || (storageUsage && storageUsage.percentage >= 100)}
                                            onClick={() => {
                                                const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
                                                const developerKey = import.meta.env.VITE_GOOGLE_DEVELOPER_KEY;
                                                if (!clientId || !developerKey) {
                                                    toast({ variant: "destructive", title: "Falta configuración", description: "Falta configurar las credenciales de Google Drive en el archivo .env." });
                                                    return;
                                                }
                                                openPicker({
                                                    clientId,
                                                    developerKey,
                                                    viewId: "DOCS",
                                                    showUploadView: true,
                                                    showUploadFolders: true,
                                                    supportDrives: true,
                                                    multiselect: false,
                                                    callbackFunction: (data) => {
                                                        if (data.action === 'picked') {
                                                            setPickerData(data);
                                                        }
                                                    }
                                                });
                                            }}
                                        >
                                            <Database className="h-4 w-4 mr-1.5" /> Conectar Google Drive
                                        </Button> */}
                                        <Button
                                            variant="outline"
                                            className="rounded-xl border-indigo-500 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 shadow-sm font-semibold gap-1.5"
                                            size="sm"
                                            disabled={storageUsage && storageUsage.percentage >= 100}
                                            onClick={() => {
                                                setIsLinkDraftingModalOpen(true);
                                                loadMyDocuments();
                                            }}
                                        >
                                            <FileCheck className="h-4 w-4 text-indigo-500" />
                                            Vincular Redacción v2
                                        </Button>
                                        <div className="relative">
                                            <Button
                                                className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15"
                                                size="sm"
                                                disabled={isUploading || (storageUsage && storageUsage.percentage >= 100)}
                                                onClick={() => document.getElementById('file-upload')?.click()}
                                            >
                                                {isUploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                                                Subir Archivo
                                            </Button>
                                            <input id="file-upload" type="file" multiple className="hidden" onChange={handleFileUpload} />
                                        </div>
                                    </div>
                                </div>

                                {processFiles.length > 0 ? (
                                    <Card className="border border-gray-100/80 shadow-sm overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <Table>
                                                <TableHeader className="bg-gray-50/50">
                                                    <TableRow>
                                                        <TableHead className="font-semibold text-gray-600">Nombre</TableHead>
                                                        <TableHead className="font-semibold text-gray-600 text-center">Tamaño</TableHead>
                                                        <TableHead className="font-semibold text-gray-600 text-center">Fecha</TableHead>
                                                        <TableHead className="font-semibold text-gray-600 text-right">Acciones</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {processFiles.map((file) => (
                                                        <TableRow
                                                            key={file.id}
                                                            id={`file-row-${file.id}`}
                                                            className={`hover:bg-gray-50/50 transition-colors group ${highlightedFileId === file.id
                                                                ? "bg-amber-50/80 dark:bg-amber-950/25 border-l-2 border-l-amber-500 animate-pulse"
                                                                : ""
                                                                }`}
                                                        >
                                                            <TableCell>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-100 transition-colors">
                                                                        <FileText className="h-4 w-4" />
                                                                    </div>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <div className="flex items-center flex-wrap gap-2">
                                                                            <button
                                                                                className="font-medium text-gray-900 text-sm text-left hover:text-blue-600 transition-colors focus:outline-none truncate max-w-[300px]"
                                                                                onClick={() => {
                                                                                    const url = getFileUrl(file);
                                                                                    if (url) openDocument(url, file.file_name);
                                                                                }}
                                                                            >
                                                                                {file.file_name}
                                                                            </button>
                                                                            {vectorizedFiles[file.file_name] && (
                                                                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] py-0 px-1 font-bold rounded-md shrink-0">
                                                                                    Vectorizado
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-[10px] text-gray-400 uppercase tracking-tighter">{file.content_type}</span>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-center text-sm text-gray-500">
                                                                {(file.file_size / 1024 / 1024).toFixed(2)} MB
                                                            </TableCell>
                                                            {/* Single Column: Real Document Date */}
                                                            <TableCell className="text-center text-sm font-medium text-gray-700">
                                                                {(() => {
                                                                    // 1. Direct date fields on file object
                                                                    const directDate = file.fecha_documento || file.fecha_actuacion || file.fecha;
                                                                    if (directDate) {
                                                                        if (typeof directDate === 'string' && directDate.includes('T')) {
                                                                            return new Date(directDate).toLocaleDateString();
                                                                        }
                                                                        if (typeof directDate === 'string' && directDate.includes('-')) {
                                                                            const parts = directDate.split('T')[0].split('-');
                                                                            if (parts.length === 3 && parts[0].length === 4) {
                                                                                return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                                                            }
                                                                        }
                                                                        return String(directDate);
                                                                    }

                                                                    // 2. Match actuacion date from timeline
                                                                    if (file.file_name && Array.isArray(timeline) && timeline.length > 0) {
                                                                        const cleanName = file.file_name.toLowerCase();
                                                                        const matchedAct = timeline.find((act: any) => {
                                                                            const actDesc = (act.descripcion || act.actuacion || "").toLowerCase();
                                                                            const actPath = (act.anexo_path || "").toLowerCase();
                                                                            return (actPath && actPath.includes(cleanName)) || (cleanName.length > 8 && actDesc.includes(cleanName.slice(0, 15)));
                                                                        });
                                                                        if (matchedAct && matchedAct.fecha) {
                                                                            const actDateStr = String(matchedAct.fecha);
                                                                            if (actDateStr.includes('-')) {
                                                                                const parts = actDateStr.split('T')[0].split('-');
                                                                                if (parts.length === 3 && parts[0].length === 4) {
                                                                                    return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                                                                }
                                                                            }
                                                                            return actDateStr;
                                                                        }
                                                                    }

                                                                    // 3. Parse 8-digit date pattern YYYYMMDD in filename
                                                                    if (file.file_name) {
                                                                        const match = file.file_name.match(/(20\d{2})[-_.]?(0[1-9]|1[0-2])[-_.]?(0[1-9]|[12]\d|3[01])/);
                                                                        if (match) {
                                                                            return `${match[3]}/${match[2]}/${match[1]}`;
                                                                        }
                                                                    }

                                                                    return "—";
                                                                })()}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {vectorizingFiles[file.file_name] ? (
                                                                    <div className="flex items-center justify-end gap-1.5 text-xs text-amber-600 font-medium select-none">
                                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Vectorizando...
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className={`h-8 rounded-lg ${vectorizedFiles[file.file_name]
                                                                                ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                                                                : "text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                                                                }`}
                                                                            onClick={() => handleVectorizeFile(file.file_name)}
                                                                        >
                                                                            <Brain className="h-4 w-4 mr-1.5" /> {vectorizedFiles[file.file_name] ? "Re-vectorizar" : "Vectorizar"}
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                                            onClick={() => {
                                                                                const url = getFileUrl(file);
                                                                                if (url) openDocument(url, file.file_name);
                                                                            }}
                                                                        >
                                                                            <Eye className="h-4 w-4 mr-1.5" /> Ver
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                                                            onClick={() => handleFileDelete(file.id)}
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow className="bg-gray-50/40 border-t-2 border-gray-200/60 font-semibold select-none">
                                                        <TableCell>
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                                                    <Database className="h-4 w-4" />
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-gray-800">Total Consolidado</span>
                                                                    <span className="text-[10px] text-gray-400">Suma total de archivos del expediente</span>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center text-sm font-bold text-gray-900">
                                                            {(() => {
                                                                const bytes = processFiles.reduce((acc, f) => acc + (f.file_size || 0), 0);
                                                                const mb = bytes / (1024 * 1024);
                                                                return `${mb.toFixed(2)} MB`;
                                                            })()}
                                                        </TableCell>
                                                        <TableCell className="text-center text-sm text-gray-400">
                                                            —
                                                        </TableCell>
                                                        <TableCell className="text-right text-xs text-gray-400">
                                                            {processFiles.length} archivos
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </Card>
                                ) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                                                <FileText className="h-7 w-7 text-gray-300" />
                                            </div>
                                            <p className="text-gray-400 text-sm mb-4">No hay archivos adjuntos a este expediente.</p>
                                            <div className="relative inline-block">
                                                <Button
                                                    variant="outline"
                                                    className="rounded-xl border-dashed"
                                                    disabled={isUploading || (storageUsage && storageUsage.percentage >= 100)}
                                                    onClick={() => document.getElementById('file-upload-drop')?.click()}
                                                >
                                                    <Upload className="h-4 w-4 mr-1.5" /> Arrastra o selecciona archivos
                                                </Button>
                                                <input id="file-upload-drop" type="file" multiple className="hidden" onChange={handleFileUpload} />
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                                {/* Documents from actuaciones/publications */}
                                {(() => {
                                    const docsFromTimeline = uniqueTimeline.filter(a => a.document_url);
                                    if (docsFromTimeline.length === 0) return null;
                                    return (
                                        <div className="mt-6">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="p-1.5 bg-emerald-100 rounded-lg">
                                                    <Database className="h-4 w-4 text-emerald-600" />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-gray-900">Documentos de Actuaciones</h3>
                                                    <p className="text-[11px] text-gray-400">{docsFromTimeline.length} documentos vinculados desde el portal judicial</p>
                                                </div>
                                            </div>
                                            <Card className="border border-emerald-100/80 shadow-sm overflow-hidden">
                                                <div className="overflow-x-auto">
                                                    <Table>
                                                        <TableHeader className="bg-emerald-50/50">
                                                            <TableRow>
                                                                <TableHead className="font-semibold text-gray-600">Documento</TableHead>
                                                                <TableHead className="font-semibold text-gray-600">Actuación</TableHead>
                                                                <TableHead className="font-semibold text-gray-600 text-center">Fecha</TableHead>
                                                                <TableHead className="font-semibold text-gray-600 text-right">Acciones</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {docsFromTimeline.map((act, idx) => (
                                                                <TableRow
                                                                    key={`doc-${idx}`}
                                                                    data-doc-url={act.document_url}
                                                                    className={`hover:bg-emerald-50/30 transition-colors group ${highlightedFileId === act.document_url
                                                                        ? "bg-amber-50/80 dark:bg-amber-950/25 border-l-2 border-l-amber-500 animate-pulse"
                                                                        : ""
                                                                        }`}
                                                                >
                                                                    <TableCell>
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg group-hover:bg-emerald-100 transition-colors">
                                                                                <FileText className="h-4 w-4" />
                                                                            </div>
                                                                            <div className="flex flex-col">
                                                                                <span className="font-medium text-gray-900 text-sm line-clamp-1">
                                                                                    {act.descripcion?.replace("Documento: ", "") || act.actuacion || "Documento"}
                                                                                </span>
                                                                                <span className="text-[10px] text-gray-400 uppercase tracking-tighter">
                                                                                    {act.source || act.publication_type || "Portal Judicial"}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </TableCell>
                                                                    <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">
                                                                        {act.actuacion || "—"}
                                                                    </TableCell>
                                                                    <TableCell className="text-center text-sm text-gray-500">
                                                                        {act.fecha || "—"}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-8 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                                                            onClick={() => window.open(act.document_url!, "_blank", "noopener,noreferrer")}
                                                                        >
                                                                            <ExternalLink className="h-4 w-4 mr-1.5" /> Abrir
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                            </Card>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* ====== EMAILS ====== */}
                        {activeSection === "emails" && (
                            <div className="space-y-5">
                                <div className="p-3.5 bg-gradient-to-r from-amber-50 to-orange-50/60 border border-amber-200/80 rounded-2xl flex items-center justify-between shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-xl bg-amber-100/80 text-amber-700 flex items-center justify-center font-bold text-xs">
                                            <Sparkles className="h-4 w-4 text-amber-600 animate-pulse" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-amber-900">Módulo de Correo Judicial</h3>
                                                <Badge className="bg-amber-500 text-white border-none font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full">
                                                    Versión Beta
                                                </Badge>
                                            </div>
                                            <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                                                Nos encontramos en versión Beta. La funcionalidad de envío y seguimiento de notificaciones judiciales se encuentra en fase de pruebas.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-lg font-bold text-gray-900">Correos Notificados</h2>
                                    </div>
                                    <Dialog open={isComposeEmailOpen} onOpenChange={(open) => {
                                        setIsComposeEmailOpen(open);
                                        if (open) {
                                            loadProcessEmails();
                                            if (process) {
                                                setEmailSubject(`Notificación Legal: Expediente ${formData.numero || process.case_number}`);
                                            }
                                        }
                                    }}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15" size="sm">
                                                <Mail className="h-4 w-4 mr-1.5" /> Redactar Correo
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent className="rounded-2xl max-w-7xl w-[96vw] max-h-[96vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl">
                                            <div className="bg-gradient-to-r from-[#1e3a5f] via-indigo-700 to-[#4f46e5] p-6 flex items-center justify-between shrink-0">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-1 ring-white/20">
                                                        <Mail className="h-5 w-5 text-white" />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2.5">
                                                            <DialogTitle className="text-white text-lg font-bold tracking-tight">Redactor de Correo Certificado</DialogTitle>
                                                            <Badge className="bg-amber-400/20 text-amber-200 border border-amber-300/30 text-[9px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                                                <Coins className="h-3 w-3 text-amber-300" /> 4 Créditos
                                                            </Badge>
                                                        </div>
                                                        <DialogDescription className="text-white/70 text-xs mt-0.5">Comunicación con trazabilidad legal · Ley 2213/2022</DialogDescription>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge className="bg-emerald-500/90 text-white border-none px-3 py-1.5 rounded-full flex items-center gap-1.5 text-[10px] font-bold shadow-lg shadow-emerald-500/25">
                                                        <Shield className="h-3.5 w-3.5" /> Trazable
                                                    </Badge>
                                                </div>
                                            </div>

                                            <ScrollArea className="flex-1 overflow-y-auto">
                                                <div className="p-8 space-y-6">
                                                    {/* Tipo de correo */}
                                                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-200/90 shadow-xs">
                                                        <Checkbox
                                                            id="is-judicial"
                                                            checked={emailIsJudicial}
                                                            onCheckedChange={(c) => setEmailIsJudicial(c === true)}
                                                            className="border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                                        />
                                                        <div>
                                                            <label htmlFor="is-judicial" className="text-xs font-bold text-slate-900 cursor-pointer flex items-center gap-2">
                                                                Notificación Judicial Formal
                                                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] font-bold py-0">
                                                                    Ley 2213 Art. 8
                                                                </Badge>
                                                            </label>
                                                            <p className="text-[11px] text-slate-500 font-medium">
                                                                Activa los requerimientos legales y la declaración juramentada para notificaciones procesales.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Remitente (From) */}
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                                                Remitente (Enviar desde) *
                                                            </Label>

                                                            {/* Sub-diálogo rápido para verificar email sin salir de la vista */}
                                                            <Dialog open={isQuickVerifyOpen} onOpenChange={setIsQuickVerifyOpen}>
                                                                <DialogTrigger asChild>
                                                                    <button className="text-xs text-indigo-600 hover:text-indigo-800 font-bold transition flex items-center gap-1">
                                                                        + Verificar Correo Nuevo
                                                                    </button>
                                                                </DialogTrigger>
                                                                <DialogContent className="rounded-2xl max-w-md p-5 border-none shadow-2xl">
                                                                    <DialogTitle className="text-base font-bold text-gray-800">
                                                                        Verificar Dirección de Correo
                                                                    </DialogTitle>
                                                                    <DialogDescription className="text-xs text-gray-400">
                                                                        Introduce la dirección que deseas utilizar. Te enviaremos un código OTP de 6 dígitos de inmediato.
                                                                    </DialogDescription>

                                                                    <div className="space-y-4 mt-3">
                                                                        <div className="space-y-1">
                                                                            <Label className="text-xs text-gray-500 uppercase font-bold">Email a verificar *</Label>
                                                                            <Input
                                                                                type="email"
                                                                                placeholder="abogado@gmail.com"
                                                                                value={quickSenderEmail}
                                                                                onChange={(e) => setQuickSenderEmail(e.target.value)}
                                                                                disabled={!!quickVerifyingEmail}
                                                                                className="rounded-xl"
                                                                            />
                                                                        </div>
                                                                        <div className="space-y-1">
                                                                            <Label className="text-xs text-gray-500 uppercase font-bold">Nombre del remitente</Label>
                                                                            <Input
                                                                                placeholder="Juan Pérez"
                                                                                value={quickSenderName}
                                                                                onChange={(e) => setQuickSenderName(e.target.value)}
                                                                                disabled={!!quickVerifyingEmail}
                                                                                className="rounded-xl"
                                                                            />
                                                                        </div>

                                                                        {!quickVerifyingEmail ? (
                                                                            <Button
                                                                                className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700 font-bold gap-2"
                                                                                onClick={handleQuickRegister}
                                                                                disabled={isQuickRegistering}
                                                                            >
                                                                                {isQuickRegistering ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar Código de Seguridad"}
                                                                            </Button>
                                                                        ) : (
                                                                            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl space-y-3">
                                                                                <p className="text-xs text-amber-700 font-medium">
                                                                                    Ingresa el código OTP enviado a <strong>{quickVerifyingEmail}</strong>:
                                                                                </p>
                                                                                <div className="flex gap-2 items-center">
                                                                                    <Input
                                                                                        maxLength={6}
                                                                                        placeholder="000000"
                                                                                        value={quickSenderOtp}
                                                                                        onChange={(e) => setQuickSenderOtp(e.target.value)}
                                                                                        className="rounded-xl font-mono text-center tracking-widest text-lg bg-white h-10"
                                                                                    />
                                                                                    <Button
                                                                                        className="rounded-xl bg-amber-600 hover:bg-amber-700 font-bold"
                                                                                        onClick={handleQuickVerify}
                                                                                        disabled={isQuickVerifying}
                                                                                    >
                                                                                        {isQuickVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validar"}
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </DialogContent>
                                                            </Dialog>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            <div className="space-y-1">
                                                                <Label className="text-[10px] text-gray-400 font-bold uppercase">Correo Remitente</Label>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex-1">
                                                                        <Select
                                                                            value={selectedSender}
                                                                            onValueChange={(val) => {
                                                                                setSelectedSender(val);
                                                                                const found = verifiedSenders.find(s => s.email === val);
                                                                                if (found?.name) setEmailSenderName(found.name);
                                                                            }}
                                                                        >
                                                                            <SelectTrigger className="rounded-xl border-gray-100 bg-gray-50 focus:bg-white h-11 text-xs">
                                                                                <SelectValue placeholder="Selecciona el correo remitente" />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {verifiedSenders.map((s) => (
                                                                                    <SelectItem key={s.email} value={s.email}>
                                                                                        {s.name} &lt;{s.email}&gt; ({s.type === 'smtp' ? 'Mi SMTP' : 'Sistema'})
                                                                                    </SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                    {selectedSender && selectedSender !== "notificacionesjudiciales@miasistentelegalia.com" && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            disabled={isDeletingSender}
                                                                            className="h-11 px-3 rounded-xl border-red-200 bg-red-50/70 text-red-600 hover:bg-red-100 hover:border-red-300 shrink-0 font-bold text-xs gap-1.5"
                                                                            title="Eliminar este correo remitente de mis autorizados"
                                                                            onClick={(e) => handleDeleteSender(selectedSender, e)}
                                                                        >
                                                                            {isDeletingSender ? <Loader2 className="h-4 w-4 animate-spin text-red-600" /> : <Trash2 className="h-4 w-4 text-red-600" />}
                                                                            <span>Eliminar</span>
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="space-y-1">
                                                                <Label className="text-[10px] text-gray-400 font-bold uppercase">Nombre del Remitente (Personalizable)</Label>
                                                                <Input
                                                                    className="rounded-xl border-gray-100 bg-gray-50 focus:bg-white h-11 text-xs font-medium"
                                                                    placeholder="Ej: Dr. Pepito Pérez - Abogado"
                                                                    value={emailSenderName}
                                                                    onChange={(e) => setEmailSenderName(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>

                                                        {verifiedSenders.length === 1 && verifiedSenders[0].type === 'resend_system' && (
                                                            <p className="text-[10px] text-indigo-600 font-medium leading-relaxed">
                                                                💡 Para enviar desde tu propio correo institucional, configúralo en <strong>Ajustes del Despacho &gt; Correo Saliente (SMTP)</strong>.
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Destinatario */}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Email destinatario *</Label>
                                                            <Input
                                                                type="email"
                                                                className="rounded-xl border-gray-100 bg-gray-50 focus:bg-white h-11"
                                                                placeholder="juez@ramajudicial.gov.co"
                                                                value={emailToAddress}
                                                                onChange={(e) => setEmailToAddress(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre destinatario</Label>
                                                            <Input
                                                                className="rounded-xl border-gray-100 bg-gray-50 focus:bg-white h-11"
                                                                placeholder="Juzgado 1 Civil"
                                                                value={emailToName}
                                                                onChange={(e) => setEmailToName(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Origen de dirección obligatorio para Ley 2213 */}
                                                    {emailIsJudicial && (
                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                                                                ¿Cómo obtuvo esta dirección? * (Ley 2213 Art. 8)
                                                            </Label>
                                                            <Input
                                                                className="rounded-xl border-amber-100 bg-amber-50/30 focus:bg-white focus:border-amber-300 h-11"
                                                                placeholder="Ej: Directorio oficial Rama Judicial"
                                                                value={emailToAddressProof}
                                                                onChange={(e) => setEmailToAddressProof(e.target.value)}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Asunto */}
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Asunto *</Label>
                                                        <Input
                                                            className="rounded-xl border-gray-100 bg-gray-50 focus:bg-white h-11 text-base"
                                                            value={emailSubject}
                                                            onChange={(e) => setEmailSubject(e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <Label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Cuerpo del Mensaje</Label>
                                                            <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                                                                <CheckSquare className="h-3 w-3" /> Formato Enriquecido Activo
                                                            </span>
                                                        </div>
                                                        <div className="rounded-2xl border border-gray-100 overflow-hidden shadow-inner bg-white min-h-[500px]">
                                                            <JoditEditor
                                                                ref={editorRef}
                                                                value={emailBodyHtml}
                                                                config={editorConfig}
                                                                onBlur={newContent => setEmailBodyHtml(newContent)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Archivos Adjuntos - Prominent File List */}
                                                    <div className="space-y-3">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                                                    Documentos Adjuntos al Correo
                                                                </Label>
                                                                <Badge className="bg-indigo-100 text-indigo-700 font-bold text-[11px] px-2.5 py-0.5 rounded-full border border-indigo-200">
                                                                    {attachedFiles.length} {attachedFiles.length === 1 ? 'archivo' : 'archivos'}
                                                                </Badge>
                                                            </div>

                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="outline" size="sm" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50 font-bold gap-2 text-xs text-slate-700 shadow-sm" disabled={isUploadingEmailFile}>
                                                                        {isUploadingEmailFile ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                                                                        ) : (
                                                                            <Paperclip className="h-4 w-4 text-indigo-600" />
                                                                        )}
                                                                        {isUploadingEmailFile ? `Subiendo ${uploadingEmailFileName}...` : "Adjuntar Documento"}
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent className="w-60 rounded-2xl overflow-hidden shadow-2xl border-slate-200 p-1" align="end">
                                                                    <DropdownMenuLabel className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3 py-2">Seleccionar Origen</DropdownMenuLabel>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem className="gap-2.5 px-3 py-2.5 focus:bg-indigo-50 focus:text-indigo-700 cursor-pointer rounded-xl text-xs font-semibold" onClick={() => setIsProcessFileSelectorOpen(true)}>
                                                                        <Database className="h-4 w-4 text-indigo-500" /> Desde el Expediente
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem className="gap-2.5 px-3 py-2.5 focus:bg-indigo-50 focus:text-indigo-700 cursor-pointer rounded-xl text-xs font-semibold" onClick={() => document.getElementById('email-file-upload')?.click()}>
                                                                        <Upload className="h-4 w-4 text-indigo-500" /> Desde mi Equipo (PC)
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                            <input id="email-file-upload" type="file" className="hidden" onChange={handleEmailFileUpload} />
                                                        </div>

                                                        {/* Dedicated Attachment List Box */}
                                                        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-3 min-h-[70px] flex flex-col justify-center">
                                                            {isUploadingEmailFile && (
                                                                <div className="flex items-center gap-3 p-3 bg-indigo-50/90 border border-indigo-200 rounded-xl mb-2 animate-pulse">
                                                                    <Loader2 className="h-5 w-5 animate-spin text-indigo-600 shrink-0" />
                                                                    <div className="flex-1 truncate">
                                                                        <p className="text-xs font-bold text-indigo-900 truncate">Subiendo {uploadingEmailFileName}...</p>
                                                                        <p className="text-[10px] text-indigo-600">Almacenando de forma segura para adjuntar al correo</p>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {attachedFiles.length > 0 ? (
                                                                <div className="space-y-2">
                                                                    {attachedFiles.map((file, i) => (
                                                                        <div
                                                                            key={file.id || i}
                                                                            className="flex items-center justify-between p-3 bg-white border border-slate-200/90 rounded-xl shadow-xs hover:border-indigo-300 transition-all group"
                                                                        >
                                                                            <div className="flex items-center gap-3 min-w-0 pr-3">
                                                                                <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                                                                                    <FileText className="h-4 w-4 text-indigo-600" />
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                    <p className="text-xs font-bold text-slate-800 truncate leading-snug">
                                                                                        {file.file_name || file.name || "Archivo adjunto"}
                                                                                    </p>
                                                                                    <p className="text-[10px] text-slate-400 font-medium flex items-center gap-2">
                                                                                        <span className="text-emerald-600 font-semibold">✓ Adjunto verificado</span>
                                                                                        {file.file_size && <span>· {(file.file_size / 1024).toFixed(1)} KB</span>}
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-8 w-8 p-0 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0 transition-colors"
                                                                                title="Quitar adjunto"
                                                                                onClick={() => toggleEmailAttachment(file)}
                                                                            >
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : !isUploadingEmailFile && (
                                                                <div className="py-4 text-center">
                                                                    <Paperclip className="h-6 w-6 text-slate-300 mx-auto mb-1" />
                                                                    <p className="text-xs text-slate-400 font-medium">No se han adjuntado archivos a esta notificación.</p>
                                                                    <p className="text-[10px] text-slate-400">Puedes adjuntar memoriales, poderes o pruebas en PDF/Word.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <Dialog open={isProcessFileSelectorOpen} onOpenChange={setIsProcessFileSelectorOpen}>
                                                        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden">
                                                            <DialogHeader className="bg-gray-50 p-6 border-b border-gray-100">
                                                                <DialogTitle className="text-gray-900 font-bold">Seleccionar del Expediente</DialogTitle>
                                                                <DialogDescription className="text-xs text-gray-400">Archivos vinculados a este proceso</DialogDescription>
                                                            </DialogHeader>
                                                            <ScrollArea className="h-80">
                                                                {processFiles.length > 0 ? (
                                                                    <div className="p-4 space-y-2">
                                                                        {processFiles.map((f) => (
                                                                            <div
                                                                                key={f.id}
                                                                                className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer ${attachedFiles.find(a => a.id === f.id) ? 'bg-blue-50/50 border-blue-200 text-blue-700' : 'bg-white border-gray-100 hover:border-blue-100 hover:bg-gray-50'}`}
                                                                                onClick={() => toggleEmailAttachment(f)}
                                                                            >
                                                                                <div className="flex items-center gap-3 truncate">
                                                                                    <div className={`p-2 rounded-lg ${attachedFiles.find(a => a.id === f.id) ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                                                                        <FileText className="h-4 w-4" />
                                                                                    </div>
                                                                                    <div className="truncate">
                                                                                        <p className="text-sm font-bold truncate">{f.file_name}</p>
                                                                                        <p className="text-[10px] opacity-70 uppercase font-bold">{new Date(f.created_at).toLocaleDateString()}</p>
                                                                                    </div>
                                                                                </div>
                                                                                {attachedFiles.find(a => a.id === f.id) && (
                                                                                    <div className="bg-blue-500 text-white rounded-full p-1 shadow-sm">
                                                                                        <CheckSquare className="h-4 w-4" />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="py-20 text-center">
                                                                        <FileText className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                                                                        <p className="text-gray-400 text-sm">No hay archivos registrados.</p>
                                                                    </div>
                                                                )}
                                                            </ScrollArea>
                                                            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                                                                <Button className="rounded-xl bg-legal-blue px-6" onClick={() => setIsProcessFileSelectorOpen(false)}>Listos</Button>
                                                            </div>
                                                        </DialogContent>
                                                    </Dialog>

                                                    {/* Panel Unificado de Garantías y Cumplimiento Legal (Ley 2213 de 2022) - Versión Clara y Formal */}
                                                    <div className="rounded-2xl border border-slate-200/90 bg-slate-50/90 text-slate-800 p-5 space-y-4 shadow-sm">
                                                        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                                                            <div className="flex items-center gap-2.5">
                                                                <Shield className="h-4 w-4 text-emerald-600" />
                                                                <span className="text-xs font-bold uppercase tracking-wider text-slate-900">
                                                                    Certificación & Cumplimiento Legal (Ley 2213 de 2022)
                                                                </span>
                                                            </div>
                                                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-bold">
                                                                Sello Digital de Autenticidad
                                                            </Badge>
                                                        </div>

                                                        <p className="text-[11px] text-slate-600 leading-relaxed font-medium">
                                                            Este correo cuenta con certificado de estampado cronológico e inmutabilidad con Hash SHA-256. Genera constancia con valor probatorio procesal conforme a la Ley 527 de 1999 y Ley 2213 de 2022.
                                                        </p>

                                                        <div className="space-y-3 pt-1">
                                                            {emailIsJudicial && (
                                                                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-slate-300 transition-colors shadow-xs">
                                                                    <Checkbox
                                                                        id="oath-check"
                                                                        checked={emailOathAccepted}
                                                                        onCheckedChange={(c) => setEmailOathAccepted(c === true)}
                                                                        className="mt-0.5 border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                                                    />
                                                                    <label htmlFor="oath-check" className="text-xs text-slate-700 font-medium leading-normal cursor-pointer select-none">
                                                                        <strong className="text-slate-900 font-bold">Declaración Juramentada:</strong> Declaro bajo la gravedad del juramento que la dirección de correo ingresada corresponde al destinatario y es la correcta (Art. 8 Ley 2213 de 2022).
                                                                    </label>
                                                                </div>
                                                            )}

                                                            <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-white border border-slate-200 hover:border-slate-300 transition-colors shadow-xs">
                                                                <div className="flex items-center gap-3">
                                                                    <Checkbox
                                                                        id="terms-check"
                                                                        checked={emailTermsAccepted}
                                                                        onCheckedChange={(c) => setEmailTermsAccepted(c === true)}
                                                                        className="border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                                                                    />
                                                                    <label htmlFor="terms-check" className="text-xs text-slate-700 font-medium leading-normal cursor-pointer select-none">
                                                                        Acepto los <button type="button" className="font-bold underline text-indigo-700 hover:text-indigo-900" onClick={() => setIsTermsModalOpen(true)}>Términos del Servicio de Correo Certificado</button> y la responsabilidad sobre los testimonios de entrega.
                                                                    </label>
                                                                </div>
                                                                <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px] font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg shrink-0 px-2.5" onClick={() => setIsTermsModalOpen(true)}>
                                                                    Ver Términos
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ScrollArea>

                                            {/* Modal de Términos y Condiciones completas */}
                                            <Dialog open={isTermsModalOpen} onOpenChange={setIsTermsModalOpen}>
                                                <DialogContent className="max-w-2xl max-h-[85vh] rounded-2xl p-0 overflow-hidden flex flex-col border-none shadow-2xl">
                                                    <div className="bg-gradient-to-r from-legal-blue to-legal-navy p-5 flex items-center justify-between shrink-0">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                                                                <FileText className="h-4 w-4 text-white" />
                                                            </div>
                                                            <div>
                                                                <DialogTitle className="text-white text-base font-bold">Términos y Condiciones del Servicio</DialogTitle>
                                                                <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Correo Electrónico Certificado · MI ASISTENTE LEGAL IA</p>
                                                                <DialogDescription className="sr-only">Términos y condiciones detallados para el uso del servicio de correo electrónico certificado e imprevistos de entrega/no entrega.</DialogDescription>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <ScrollArea className="flex-1 overflow-y-auto max-h-[calc(85vh-130px)]">
                                                        <div className="p-6 text-xs text-gray-700 space-y-4 font-normal leading-relaxed">
                                                            <div className="space-y-3">
                                                                <h4 className="font-bold text-sm text-gray-900">¿Qué cubren estas condiciones?</h4>
                                                                <p>
                                                                    Sabemos que es tentador saltarse estas Condiciones del Servicio, pero es importante establecer qué puedes esperar de nosotros cuando usas nuestros servicios, y qué esperamos nosotros de ti.
                                                                </p>
                                                                <p>
                                                                    El presente contenido será parte integral de los acuerdos de nivel del servicio generales. Este documento establece los términos y condiciones para el uso del servicio de Correo Electrónico Certificado proporcionado por <strong>MI ASISTENTE LEGAL IA</strong>. El suscriptor reconoce que está familiarizado, comprende y acepta en su totalidad estos términos y condiciones.
                                                                </p>
                                                            </div>

                                                            <div className="space-y-2 pt-2 border-t border-gray-100">
                                                                <h4 className="font-bold text-sm text-gray-900">Conceptos y definiciones</h4>
                                                                <p>
                                                                    Para efectos de comprensión de la información aquí suministrada, es importante que entiendas algunos conceptos básicos referentes a la normativa de firma electrónica y certificación de correos en Colombia.
                                                                </p>
                                                                <p>
                                                                    <strong>CORREO ELECTRÓNICO CERTIFICADO:</strong> Se refiere a un servicio que permite enviar correos electrónicos con un alto grado de seguridad y autenticidad, ya que se emite un certificado que verifica la identidad del remitente, la integridad del mensaje y la fecha y hora de envío y recepción. Este tipo de correo electrónico suele utilizarse en situaciones en las que se requiere una prueba legal de envío y recepción de un mensaje, como en contratos o notificaciones importantes. En Colombia, la firma electrónica se rige por la Ley 527 de 1999, que establece que la firma electrónica tendrá la misma validez jurídica que la firma manuscrita, siempre y cuando cumpla con los requisitos de autenticidad, integridad y no repudio establecidos por la ley.
                                                                </p>
                                                                <p>
                                                                    <strong>REMITENTE:</strong> Se refiere a la persona o entidad que envía un mensaje o paquete a otra persona o entidad. En el contexto de la comunicación por correo electrónico, el remitente es la persona que redacta y envía el correo electrónico.
                                                                </p>
                                                                <p>
                                                                    <strong>DESTINATARIO:</strong> Se refiere a la persona o entidad a la que va dirigido un mensaje o paquete. En el contexto de la comunicación por correo electrónico, el destinatario es la o las personas que reciben el correo electrónico enviado por el remitente.
                                                                </p>
                                                                <p>
                                                                    <strong>RECEPCIÓN DE MENSAJE:</strong> Es el procedimiento informático mediante el cual un servidor de correo electrónico recibe y almacena los correos electrónicos enviados por el remitente en la bandeja del destinatario. Cuando el remitente envía un correo electrónico a una dirección específica, el software del servidor de correo electrónico del destinatario verifica si esa dirección existe y está asociada a una cuenta válida. Si la dirección es válida, el servidor acepta el mensaje y lo guarda en su sistema.
                                                                </p>
                                                            </div>

                                                            <div className="space-y-3 pt-2 border-t border-gray-100">
                                                                <h4 className="font-bold text-sm text-gray-900">Condiciones generales de aceptación del servicio</h4>

                                                                <p><strong>1. Sobre los datos, la aceptación y la finalidad del servicio:</strong><br />
                                                                    Al aceptar estos términos y condiciones, manifiestas tu interés en utilizar nuestro servicio de correo electrónico certificado, según lo estipulado en este documento. Asimismo, te comprometes al pago oportuno por el consumo del servicio y confirmas que todos los datos que nos proporcionas son veraces, que has comprobado personalmente su veracidad y que cuentas con la debida autorización para compartir datos personales privados, semiprivados o sensibles. Reconoces y aceptas nuestras políticas de protección de datos personales. Está rotundamente prohibido utilizar nuestro servicio para enviar información o comunicaciones con fines ilícitos o que violen los derechos humanos. En MI ASISTENTE LEGAL IA no tenemos la capacidad de emitir ninguna valoración sobre el contenido de los correos electrónicos certificados que envías. Por lo tanto, la responsabilidad del contenido del mensaje recae exclusivamente en ti, como emisor.</p>

                                                                <p><strong>2. Sobre las limitaciones y alcances del servicio prestado por nosotros:</strong><br />
                                                                    Si por error voluntario o involuntario no relacionas correctamente el correo electrónico del destinatario, los documentos adjuntos o la información en el cuerpo del mensaje, no asumiremos ninguna responsabilidad ni indemnizaremos por perjuicios. Tampoco nos hacemos responsables si el destinatario no acepta mensajes de cuentas desconocidas o tiene algún tipo de bloqueo. No podemos garantizar la apertura o lectura en el 100% de los casos, ya que esto queda al arbitrio del receptor del mensaje. Garantizamos que todos los correos electrónicos certificados queden con firma electrónica y cuenten con portal de consulta de veracidad.</p>

                                                                <p><strong>3. Derechos que tienes al usar el servicio:</strong><br />
                                                                    Puedes utilizar el servicio siempre y cuando cumplas con las disposiciones establecidas. Tienes derecho a recibir confirmación de envío mediante un acta generada por la plataforma informática (acuse de recibo digital con sello de tiempo). Bastará con la sola prueba de recepción por parte del servidor de destino conforme a la normatividad vigente.</p>

                                                                <p><strong>4. Sobre el testigo de no entrega y la responsabilidad de su uso:</strong><br />
                                                                    En los casos en que no sea posible la entrega del mensaje de datos al servidor de destino por motivos ajenos a la plataforma, el sistema generará un testigo que deja constancia del intento de envío no exitoso y su causa técnica. Reconoces y aceptas que este testigo de no entrega no constituye acuse de recibo ni prueba de entrega. El uso, la invocación o la aportación de dicho testigo ante cualquier autoridad o tercero es de tu exclusiva responsabilidad como suscriptor.</p>
                                                            </div>
                                                        </div>
                                                    </ScrollArea>

                                                    <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between shrink-0">
                                                        <span className="text-[10px] text-gray-500 font-medium">Mi Asistente Legal IA © Todos los derechos reservados.</span>
                                                        <Button
                                                            className="rounded-xl bg-legal-blue px-6 font-bold"
                                                            onClick={() => {
                                                                setEmailTermsAccepted(true);
                                                                setIsTermsModalOpen(false);
                                                            }}
                                                        >
                                                            Entendido y Aceptar
                                                        </Button>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>

                                            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between shrink-0">
                                                <Button variant="ghost" className="rounded-xl font-bold text-gray-400 hover:text-gray-600" onClick={() => setIsComposeEmailOpen(false)} disabled={isSendingEmail}>
                                                    Cancelar
                                                </Button>
                                                <div className="flex gap-3">
                                                    <Button
                                                        className="rounded-xl bg-gradient-to-r from-legal-blue to-indigo-700 px-8 shadow-md font-bold gap-2"
                                                        onClick={handleSendEmail}
                                                        disabled={isSendingEmail}
                                                    >
                                                        {isSendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar Notificación (4 Créditos)
                                                    </Button>
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                                {isLoadingEmails ? (
                                    <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-legal-blue" /></div>
                                ) : legalEmails.length > 0 ? (
                                    <div className="space-y-3">
                                        {legalEmails.map((email) => {
                                            const statusLabel = legalEmailsApi.getEmailStatusLabel(email.status);
                                            const statusColor = legalEmailsApi.getEmailStatusColor(email.status);
                                            const isRead = email.status === 'read' || !!email.read_at;

                                            return (
                                                <div key={email.id} className={`flex items-center justify-between p-4 border rounded-xl bg-white shadow-sm transition-all cursor-pointer ${isRead ? 'border-violet-200 bg-violet-50/20 hover:border-violet-300' : 'border-gray-100 hover:border-blue-100'}`} onClick={() => handleViewEmailDetail(email.id)}>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isRead ? 'bg-violet-100 text-violet-700 shadow-sm' : email.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : email.status === 'failed' ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-600'}`}>
                                                            {isRead ? <MailCheck className="h-5 w-5" /> : <Mail className="h-4.5 w-4.5" />}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-sm font-bold text-gray-800">{email.subject}</p>
                                                                {isRead && (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-violet-700 bg-violet-100/80 px-2 py-0.5 rounded-full border border-violet-200" title={`Abierto el ${new Date(email.read_at!).toLocaleString()}`}>
                                                                        <CheckCheck className="h-3 w-3 text-violet-600" /> Leído
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[11px] text-gray-400 font-medium">
                                                                Para: {email.to_name ? `${email.to_name} <${email.to_email}>` : email.to_email} · Enviado el {new Date(email.sent_at || email.created_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {!isRead && (
                                                            <Badge className={`border ${statusColor}`}>
                                                                {statusLabel}
                                                            </Badge>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 text-xs font-bold text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200/80 rounded-lg px-2.5 gap-1.5 shadow-2xs"
                                                            title="Descargar Testigo / Acta oficial en PDF"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    toast({ title: "Generando Testigo PDF...", description: "Preparando documento de certificación digital." });
                                                                    await legalEmailsApi.downloadEmailCertificate(email.id, email.process_number || formData.numero);
                                                                } catch (err: any) {
                                                                    toast({ variant: "destructive", title: "Error en descarga", description: err.message || "No se pudo descargar el acta." });
                                                                }
                                                            }}
                                                        >
                                                            <Download className="h-3.5 w-3.5" /> Testigo PDF
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-lg text-gray-400 hover:text-gray-600" title="Ver detalle">
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50"
                                                            title="Eliminar correo"
                                                            onClick={(e) => handleDeleteEmail(email.id, e)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                                                <Mail className="h-7 w-7 text-gray-300" />
                                            </div>
                                            <p className="text-gray-400 text-sm">No hay correos vinculados a este expediente.</p>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Diálogo de detalle de correo enviado */}
                                <Dialog open={isEmailDetailOpen} onOpenChange={setIsEmailDetailOpen}>
                                    <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] p-0 overflow-hidden border-none shadow-2xl flex flex-col">
                                        <div className="bg-gradient-to-r from-legal-blue to-legal-navy p-5 flex items-center justify-between shrink-0">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                                                    <Eye className="h-4 w-4 text-white" />
                                                </div>
                                                <div>
                                                    <DialogTitle className="text-white text-base font-bold">Detalle de Notificación</DialogTitle>
                                                    <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Trazabilidad Legal Activa</p>
                                                    <DialogDescription className="sr-only">Visualización completa del correo judicial enviado y su estado de entrega/lectura.</DialogDescription>
                                                </div>
                                            </div>
                                            {selectedEmailDetail && (
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        className="rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-xs gap-1.5 border border-white/20 shadow-xs"
                                                        onClick={async () => {
                                                            try {
                                                                toast({ title: "Generando Testigo PDF...", description: "Preparando documento de certificación digital." });
                                                                await legalEmailsApi.downloadEmailCertificate(selectedEmailDetail.id, selectedEmailDetail.process_number || formData.numero);
                                                            } catch (err: any) {
                                                                toast({ variant: "destructive", title: "Error en descarga", description: err.message || "No se pudo descargar el acta." });
                                                            }
                                                        }}
                                                    >
                                                        <Download className="h-3.5 w-3.5" /> Descargar Testigo PDF
                                                    </Button>
                                                    <Badge className={`border ${legalEmailsApi.getEmailStatusColor(selectedEmailDetail.status)}`}>
                                                        {legalEmailsApi.getEmailStatusLabel(selectedEmailDetail.status)}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>

                                        {selectedEmailDetail && (
                                            <ScrollArea className="flex-1 overflow-y-auto">
                                                <div className="p-6 space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Enviado por</p>
                                                            <p className="text-xs font-bold text-gray-800">
                                                                {selectedEmailDetail.from_email || (selectedEmailDetail.provider === "smtp" ? "Servidor SMTP configurado" : "noreply@miasistentelegalia.com")}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Destinatario</p>
                                                            <p className="text-xs font-bold text-gray-800">{selectedEmailDetail.to_name ? `${selectedEmailDetail.to_name} <${selectedEmailDetail.to_email}>` : selectedEmailDetail.to_email}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Fecha y Hora de Envío</p>
                                                            <p className="text-xs font-medium text-gray-700">{selectedEmailDetail.sent_at ? new Date(selectedEmailDetail.sent_at).toLocaleString() : "-"}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Fecha de Apertura (Leído)</p>
                                                            <p className="text-xs font-bold text-violet-700">
                                                                {selectedEmailDetail.read_at ? new Date(selectedEmailDetail.read_at).toLocaleString() : "No registrado aún"}
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">ID de Entrega (Provider)</p>
                                                            <p className="text-xs font-mono text-gray-500 truncate" title={selectedEmailDetail.resend_email_id}>{selectedEmailDetail.resend_email_id || "SMTP directo"}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Asunto</p>
                                                        <p className="text-xs font-bold text-gray-800">{selectedEmailDetail.subject}</p>
                                                    </div>
                                                    {selectedEmailDetail.oath_statement && (
                                                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                                                            <p className="text-[10px] font-bold text-amber-700 uppercase mb-1">⚖️ Juramento registrado</p>
                                                            <p className="text-[10px] text-amber-800 leading-relaxed">{selectedEmailDetail.oath_statement}</p>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Cuerpo del mensaje</p>
                                                        <div
                                                            className="p-3 bg-gray-50 rounded-xl border text-sm text-gray-700 prose prose-sm max-w-none max-h-60 overflow-y-auto"
                                                            dangerouslySetInnerHTML={{ __html: selectedEmailDetail.body_html }}
                                                        />
                                                    </div>

                                                    {/* Archivos Adjuntos del Correo */}
                                                    {selectedEmailDetail.attachments && selectedEmailDetail.attachments.length > 0 && (
                                                        <div className="space-y-2 pt-2 border-t border-gray-100">
                                                            <p className="text-[10px] font-bold text-gray-400 uppercase">Documentos Adjuntos ({selectedEmailDetail.attachments.length})</p>
                                                            <div className="space-y-2">
                                                                {selectedEmailDetail.attachments.map((att) => (
                                                                    <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl">
                                                                        <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                                                            <FileText className="h-4 w-4 text-indigo-600 shrink-0" />
                                                                            <span className="text-xs font-bold text-slate-800 truncate">{att.file_name}</span>
                                                                        </div>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="rounded-lg h-7 text-[11px] font-bold gap-1 text-indigo-700 border-indigo-200 hover:bg-indigo-50 shrink-0"
                                                                            onClick={async () => {
                                                                                try {
                                                                                    const res = await fetch(`${BACKEND_URL}/api/v1/legal-emails/public/attachment/${att.id}?email_id=${selectedEmailDetail.id}`);
                                                                                    if (!res.ok) throw new Error("No se pudo descargar");
                                                                                    const data = await res.json();
                                                                                    window.open(data.download_url, "_blank");
                                                                                } catch (err: any) {
                                                                                    toast({ variant: "destructive", title: "Error", description: err.message || "Error al descargar adjunto." });
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Download className="h-3 w-3" /> Descargar
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </ScrollArea>
                                        )}
                                    </DialogContent>
                                </Dialog>
                            </div>
                        )}

                        {/* ====== DOCS BB.DD. ====== */}
                        {activeSection === "docs" && (
                            <div className="space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Documentos de Bases de Datos</h2>
                                        <p className="text-sm text-gray-400">Consultas judiciales y registros externos</p>
                                    </div>
                                    <Button
                                        className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy hover:from-legal-navy hover:to-legal-blue shadow-md shadow-legal-blue/15"
                                        size="sm"
                                        onClick={handleOpenExternalQuery}
                                    >
                                        <ExternalLink className="h-4 w-4 mr-1.5" /> Consultar
                                    </Button>
                                </div>
                                <Card className="border border-gray-100/80 shadow-sm">
                                    <CardContent className="p-12 text-center">
                                        <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                                            <ExternalLink className="h-7 w-7 text-gray-300" />
                                        </div>
                                        <p className="text-gray-400 text-sm">No hay consultas externas asociadas.</p>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* ====== AI ARTIFACTS ====== */}
                        {activeSection === "investigaciones" && (
                            <div className="space-y-5">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Investigaciones Legales</h2>
                                    <p className="text-sm text-gray-400">Jurisprudencia de esta causa</p>
                                </div>
                                {caseResearch.length > 0 ? caseResearch.map(r => (
                                    <Card
                                        key={r.id}
                                        className="border border-gray-100/80 shadow-sm transition-all hover:shadow-md hover:border-fuchsia-200 cursor-pointer"
                                        onClick={() => handleOpenResearchWorkspace(r)}
                                    >
                                        <CardContent className="p-5">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-gray-800 line-clamp-2">
                                                        {r.query || r.metadata?.query || r.metadata?.research_topic || "Investigación legal"}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                                        {r.metadata?.summary || r.metadata?.executive_summary || "Abre para revisar el resultado completo y continuar la investigación."}
                                                    </p>
                                                </div>
                                                <Badge variant="outline" className="text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 shrink-0">
                                                    {r.status || "Completado"}
                                                </Badge>
                                            </div>
                                            <div className="flex flex-wrap items-center justify-between mt-4 pt-3 border-t border-gray-50 gap-2">
                                                <span className="text-xs text-gray-400">{new Date(r.created_at).toLocaleString()}</span>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 rounded-lg"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenResearchWorkspace(r);
                                                        }}
                                                    >
                                                        <Search className="h-3.5 w-3.5 mr-1.5" /> Abrir investigación
                                                    </Button>
                                                    {getArtifactUrl(r) && (
                                                        <Button
                                                            size="sm"
                                                            className="h-8 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDocument(getArtifactUrl(r)!, "Resultado de investigación");
                                                            }}
                                                        >
                                                            <Eye className="h-3.5 w-3.5 mr-1.5" /> Vista previa
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-400 text-sm">No hay investigaciones asociadas a este caso.</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}

                        {activeSection === "contratos" && (
                            <div className="space-y-5">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Análisis de Contratos</h2>
                                    <p className="text-sm text-gray-400">Revisiones de riesgo contractual</p>
                                </div>
                                {caseContracts.length > 0 ? caseContracts.map(c => (
                                    <Card
                                        key={c.id}
                                        className="border border-gray-100/80 shadow-sm transition-all hover:shadow-md hover:border-emerald-200 cursor-pointer"
                                        onClick={() => handleOpenContractWorkspace(c)}
                                    >
                                        <CardContent className="p-5">
                                            <div className="flex justify-between mb-2">
                                                <p className="font-semibold text-gray-800">Cód: {c.session_id?.substring(0, 15) || "REV-" + c.id.substring(0, 8)}</p>
                                                <Badge className={`${c.compliance_status?.toLowerCase().includes('alto') || (c.risk_score > 70) ? 'bg-red-50 text-red-700' :
                                                    c.compliance_status?.toLowerCase().includes('medio') || (c.risk_score > 30) ? 'bg-amber-50 text-amber-700' :
                                                        'bg-green-50 text-green-700'
                                                    } hover:opacity-80 transition-all border-none`}>
                                                    {c.compliance_status || (c.risk_score ? `Riesgo: ${c.risk_score}%` : "Completado")}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-3 line-clamp-3 leading-relaxed">
                                                {c.summary_bluf || c.metadata?.summary_bluf || "Análisis Contractual disponible en el visor."}
                                            </p>
                                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
                                                <span className="text-[10px] font-medium text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-[9px] font-bold py-0 h-5 border-gray-200 text-gray-400 uppercase tracking-tighter">
                                                        {c.contract_type || 'General'}
                                                    </Badge>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 rounded-lg text-[10px]"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenContractWorkspace(c);
                                                        }}
                                                    >
                                                        <FileCheck className="h-3 w-3 mr-1" /> Abrir análisis
                                                    </Button>
                                                    {getArtifactUrl(c) && (
                                                        <Button
                                                            size="sm"
                                                            className="h-7 rounded-lg text-[10px] bg-emerald-600 hover:bg-emerald-700"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openDocument(getArtifactUrl(c)!, "Informe de análisis contractual");
                                                            }}
                                                        >
                                                            <Eye className="h-3 w-3 mr-1" /> Vista previa
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <FileCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-400 text-sm">No hay contratos revisados en este caso.</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}

                        {activeSection === "documentacion" && (
                            <div className="space-y-5">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Documentos Generados</h2>
                                    <p className="text-sm text-gray-400">Escritos y documentos elaborados con IA</p>
                                </div>
                                {caseDrafts.length > 0 ? caseDrafts.map(d => (
                                    <Card key={d.id} className="border border-gray-100/80 shadow-sm transition-all hover:shadow-md hover:border-blue-200">
                                        <CardContent className="p-5">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-bold text-gray-800 capitalize mb-1">{d.document_type?.replace(/_/g, " ")}</p>
                                                    <p className="text-xs font-mono text-gray-400">{d.session_id}</p>
                                                </div>
                                                <div className="flex flex-col sm:flex-row gap-2">
                                                    {d.metadata?.document_url && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                className="h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-xs"
                                                                onClick={() => openDocument(d.metadata.document_url, d.document_type || "Documento generado")}
                                                            >
                                                                <Eye className="h-3.5 w-3.5 mr-1.5" /> Vista previa
                                                            </Button>
                                                            <a href={d.metadata.document_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold transition-colors">
                                                                <ExternalLink className="h-3.5 w-3.5" /> Abrir DOCX
                                                            </a>
                                                        </>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 rounded-lg text-xs"
                                                        onClick={() => navigate("/dashboard/agents/document-drafting-v2")}
                                                    >
                                                        <FileEdit className="h-3.5 w-3.5 mr-1.5" /> Continuar redacción
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="mt-4 pt-3 border-t border-gray-100">
                                                <span className="text-xs text-gray-400">{new Date(d.created_at).toLocaleString()}</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <FileEdit className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-400 text-sm">No hay documentos generados.</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}

                        {activeSection === "actividad_ia" && (
                            <div className="space-y-5">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">Actividad IA del Expediente</h2>
                                        <p className="text-sm text-gray-400">Línea unificada de investigaciones, análisis y documentos generados</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge className="bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">
                                            Investigaciones: {caseResearch.length}
                                        </Badge>
                                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100">
                                            Contratos: {caseContracts.length}
                                        </Badge>
                                        <Badge className="bg-blue-50 text-blue-700 border border-blue-100">
                                            Documentos: {caseDrafts.length}
                                        </Badge>
                                    </div>
                                </div>

                                <Card className="border border-gray-100/80 shadow-sm">
                                    <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <Label className="text-[10px] uppercase text-gray-400 font-bold tracking-widest mb-1.5 block">Tipo</Label>
                                            <Select
                                                value={activityTypeFilter}
                                                onValueChange={(value: "all" | "investigacion" | "contrato" | "documento") => setActivityTypeFilter(value)}
                                            >
                                                <SelectTrigger className="rounded-xl h-10">
                                                    <SelectValue placeholder="Todos" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos</SelectItem>
                                                    <SelectItem value="investigacion">Investigaciones</SelectItem>
                                                    <SelectItem value="contrato">Análisis de contratos</SelectItem>
                                                    <SelectItem value="documento">Documentos generados</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase text-gray-400 font-bold tracking-widest mb-1.5 block">Estado</Label>
                                            <Select
                                                value={activityStatusFilter}
                                                onValueChange={(value: "all" | "completado" | "en_proceso" | "error") => setActivityStatusFilter(value)}
                                            >
                                                <SelectTrigger className="rounded-xl h-10">
                                                    <SelectValue placeholder="Todos" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos</SelectItem>
                                                    <SelectItem value="completado">Completado</SelectItem>
                                                    <SelectItem value="en_proceso">En proceso</SelectItem>
                                                    <SelectItem value="error">Error</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] uppercase text-gray-400 font-bold tracking-widest mb-1.5 block">Responsable</Label>
                                            <Select value={activityOwnerFilter} onValueChange={setActivityOwnerFilter}>
                                                <SelectTrigger className="rounded-xl h-10">
                                                    <SelectValue placeholder="Todos" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos</SelectItem>
                                                    {activityOwnerOptions.map((owner) => (
                                                        <SelectItem key={owner} value={owner}>
                                                            {owner}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </CardContent>
                                </Card>

                                {filteredActivityFeed.length > 0 ? (
                                    <div className="space-y-3">
                                        {filteredActivityFeed.map((activity) => (
                                            <Card
                                                key={activity.id}
                                                className="border border-gray-100/80 shadow-sm transition-all hover:shadow-md cursor-pointer"
                                                onClick={() => handleOpenActivity(activity)}
                                            >
                                                <CardContent className="p-5">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                                                                    {activity.type === "investigacion" ? "Investigación" : activity.type === "contrato" ? "Contrato" : "Documento"}
                                                                </Badge>
                                                                <Badge
                                                                    className={`text-[10px] ${activity.status === "error"
                                                                        ? "bg-red-50 text-red-700"
                                                                        : activity.status === "en_proceso"
                                                                            ? "bg-amber-50 text-amber-700"
                                                                            : "bg-green-50 text-green-700"
                                                                        }`}
                                                                >
                                                                    {activity.status === "en_proceso" ? "En proceso" : activity.status === "error" ? "Error" : "Completado"}
                                                                </Badge>
                                                                <Badge variant="outline" className="text-[10px] text-gray-500 border-gray-200">
                                                                    {activity.owner}
                                                                </Badge>
                                                            </div>
                                                            <p className="font-semibold text-gray-800 line-clamp-2">{activity.title}</p>
                                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{activity.description}</p>
                                                        </div>
                                                        <span className="text-[10px] text-gray-400 shrink-0">{new Date(activity.createdAt).toLocaleString()}</span>
                                                    </div>
                                                    <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenActivity(activity);
                                                            }}
                                                        >
                                                            <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Abrir
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            className="h-8 rounded-lg bg-slate-900 hover:bg-black"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePreviewActivity(activity);
                                                            }}
                                                        >
                                                            <Eye className="h-3.5 w-3.5 mr-1.5" /> Vista previa
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 rounded-lg"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                await handleShareActivity(activity);
                                                            }}
                                                        >
                                                            <Share2 className="h-3.5 w-3.5 mr-1.5" /> Compartir
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 rounded-lg"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                await handleCreateTaskFromActivity(activity);
                                                            }}
                                                        >
                                                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Crear tarea
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <Card className="border border-gray-100/80 shadow-sm">
                                        <CardContent className="p-12 text-center">
                                            <Layout className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500 text-sm font-semibold">No hay actividad para los filtros seleccionados.</p>
                                            <p className="text-gray-400 text-xs mt-1">Prueba cambiar el tipo, estado o responsable.</p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* ====== COMENTARIOS ====== */}
                        {activeSection === "comentarios" && (
                            <div className="space-y-5">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Comentarios</h2>
                                    <p className="text-sm text-gray-400">Notas y observaciones del equipo</p>
                                </div>
                                <Card className="border border-gray-100/80 shadow-sm">
                                    <CardContent className="p-5">
                                        <div className="flex gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                                                <span className="text-white text-xs font-bold">DR</span>
                                            </div>
                                            <div className="flex-1">
                                                <textarea
                                                    className="w-full min-h-[100px] rounded-xl border border-gray-200 p-3 text-sm placeholder:text-gray-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-legal-blue resize-none"
                                                    placeholder="Escribe un comentario sobre este expediente..."
                                                    value={commentText}
                                                    onChange={(e) => setCommentText(e.target.value)}
                                                />
                                                <div className="flex justify-end mt-2">
                                                    <Button
                                                        className="rounded-xl bg-gradient-to-r from-legal-blue to-legal-navy"
                                                        size="sm"
                                                        onClick={handleSaveComment}
                                                    >
                                                        <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Comentar
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-6 border-t border-gray-100 pt-4">
                                            {localComments.length === 0 ? (
                                                <div className="text-center py-6">
                                                    <MessageSquare className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                                                    <p className="text-gray-400 text-sm">No hay comentarios aún. ¡Sé el primero!</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-4 py-2">
                                                    {localComments.map((c, i) => (
                                                        <div key={i} className="flex gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-gray-400">
                                                                {c.name.substring(0, 2).toUpperCase()}
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-bold text-gray-700">{c.name}</span>
                                                                    <span className="text-[10px] text-gray-400">{c.date}</span>
                                                                </div>
                                                                <p className="text-sm text-gray-600 mt-0.5">{c.text}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ====== MAGIC DRAFT MODAL ====== */}
            <Dialog open={isDraftModalOpen} onOpenChange={setIsDraftModalOpen}>
                <DialogContent className="w-[99vw] max-w-[99vw] h-[98vh] max-h-[98vh] flex flex-col rounded-xl overflow-hidden p-0 border-none shadow-2xl bg-slate-950 sm:rounded-xl">
                    {/* Ultra Minimal Top Bar with Close Button */}
                    <div className="bg-slate-950 px-4 py-2 flex items-center justify-between border-b border-slate-800/80 shrink-0">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-300">
                            <Sparkles className="h-3.5 w-3.5 text-indigo-400" />
                            <span>Escrito Judicial Generado</span>
                            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[9px] font-semibold">
                                ✏️ Edición Libre
                            </Badge>
                        </div>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-800/80 rounded-lg h-7 w-7" onClick={() => setIsDraftModalOpen(false)}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="flex-1 overflow-hidden flex flex-col p-1 bg-slate-950 min-h-0">
                        {isDrafting ? (
                            <div className="flex-1 flex flex-col items-center justify-center space-y-6 bg-slate-950 rounded-xl border border-slate-800">
                                <div className="relative h-20 w-20">
                                    <div className="absolute inset-0 border-4 border-indigo-900 rounded-full"></div>
                                    <div className="absolute inset-0 border-4 border-indigo-500 rounded-full border-t-transparent animate-spin"></div>
                                    <div className="center-absolute">
                                        <Zap className="h-7 w-7 text-indigo-400 animate-pulse" />
                                    </div>
                                </div>
                                <div className="text-center space-y-1">
                                    <h3 className="text-base font-bold text-white">Redactando Escrito Judicial...</h3>
                                    <p className="text-slate-400 text-xs max-w-sm mx-auto">La IA está integrando la fecha actual, los datos del expediente y la jurisprudencia aplicable.</p>
                                </div>
                            </div>
                        ) : (
                            /* Main Editor Container — Occupies 100% of vertical modal height */
                            <div className="flex-1 h-full min-h-0 overflow-hidden flex flex-col border border-slate-800 rounded-lg bg-white shadow-2xl">
                                {isDraftModalOpen && (
                                    <LegalEditor
                                        documentId={id || process?.id || "temp-draft-doc"}
                                        initialContent={draftContent}
                                        userId={userId}
                                        userName={userName}
                                        onUpdate={(content) => setDraftContent(content)}
                                        onSave={(content) => {
                                            setDraftContent(content);
                                            toast({ title: "Guardado", description: "Borrador guardado localmente." });
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Compact Footer */}
                    <div className="px-5 py-3 bg-slate-950 border-t border-slate-800 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl border-blue-500/30 bg-blue-950/40 text-blue-300 hover:bg-blue-900/50 hover:text-white text-xs font-semibold"
                                onClick={() => handleExport('docx')}
                                disabled={!draftContent || isDrafting}
                            >
                                <FileText className="h-3.5 w-3.5 mr-1.5 text-blue-400" />
                                Exportar Word (.docx)
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl border-red-500/30 bg-red-950/40 text-red-300 hover:bg-red-900/50 hover:text-white text-xs font-semibold"
                                onClick={() => handleExport('pdf')}
                                disabled={!draftContent || isDrafting}
                            >
                                <FileText className="h-3.5 w-3.5 mr-1.5 text-red-400" />
                                Exportar PDF (.pdf)
                            </Button>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <Button variant="ghost" size="sm" className="rounded-xl text-slate-400 hover:text-white text-xs" onClick={() => setIsDraftModalOpen(false)}>
                                Cerrar
                            </Button>
                            <Button size="sm" className="rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs px-5 shadow-lg shadow-indigo-600/30" onClick={() => {
                                navigator.clipboard.writeText(draftContent);
                                toast({ title: "Copiado al portapapeles", description: "El borrador ha sido copiado exitosamente." });
                            }}>
                                <Zap className="h-3.5 w-3.5 mr-1.5" />
                                Copiar al Portapapeles
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de Validación de Datos Pre-generación de Documento */}
            <Dialog open={isValidationModalOpen} onOpenChange={setIsValidationModalOpen}>
                <DialogContent className="max-w-3xl rounded-2xl p-0 overflow-hidden border border-slate-200 shadow-2xl bg-white flex flex-col max-h-[90vh]">
                    <DialogHeader className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 text-white border-b border-slate-800">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-indigo-500/20 border border-indigo-400/30 rounded-xl text-indigo-300">
                                    <FileText className="h-6 w-6" />
                                </div>
                                <div>
                                    <DialogTitle className="text-xl font-black tracking-tight text-white flex items-center gap-2">
                                        Datos del Escrito Judicial
                                        <Badge className="bg-indigo-500/30 text-indigo-200 border-indigo-400/30 text-[10px] font-bold">
                                            Validación Pre-Generación
                                        </Badge>
                                    </DialogTitle>
                                    <p className="text-xs text-slate-300 mt-1">
                                        Se utilizará la fecha actual y la información del expediente para generar un documento 100% completo, sin marcas [INSERTAR...].
                                    </p>
                                </div>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-6 flex-1 overflow-y-auto">
                        {/* Summary / Alert Banner */}
                        {missingFieldsList.length > 0 ? (
                            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                <div className="text-xs text-amber-900 space-y-1">
                                    <p className="font-bold">⚠️ Se detectaron campos recomendados faltantes ({missingFieldsList.length})</p>
                                    <p className="text-amber-800/90">
                                        Por favor complete los campos resaltados a continuación para evitar tener que editar manualmente el escrito final.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-3">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                                <div className="text-xs text-emerald-900">
                                    <span className="font-bold">Expediente listo: </span>
                                    Toda la información básica está completa con la fecha actual <strong className="underline">{draftValidationData.fecha_actual}</strong>.
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                            {/* Fecha Actual */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                    <Calendar className="h-3.5 w-3.5 text-indigo-600" />
                                    Fecha Actual del Documento
                                </Label>
                                <Input
                                    value={draftValidationData.fecha_actual}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, fecha_actual: e.target.value })}
                                    className="rounded-xl border-slate-200 text-xs font-medium bg-slate-50/50"
                                />
                            </div>

                            {/* Radicado */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                    <FileCheck className="h-3.5 w-3.5 text-indigo-600" />
                                    Número de Radicado / Proceso
                                </Label>
                                <Input
                                    value={draftValidationData.radicado}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, radicado: e.target.value })}
                                    className="rounded-xl border-slate-200 text-xs font-mono font-semibold"
                                />
                            </div>

                            {/* Despacho */}
                            <div className="space-y-1.5 md:col-span-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                        <Building2 className="h-3.5 w-3.5 text-indigo-600" />
                                        Despacho Judicial / Juzgado
                                    </Label>
                                    {!draftValidationData.despacho && (
                                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 font-bold">
                                            ⚠️ Faltante
                                        </Badge>
                                    )}
                                </div>
                                <Input
                                    value={draftValidationData.despacho}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, despacho: e.target.value })}
                                    placeholder="Ej: Juzgado 01 Laboral del Circuito de Medellín"
                                    className={`rounded-xl text-xs ${!draftValidationData.despacho ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}
                                />
                            </div>

                            {/* Demandante */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                        <User className="h-3.5 w-3.5 text-indigo-600" />
                                        Demandante / Accionante
                                    </Label>
                                    {!draftValidationData.demandante && (
                                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 font-bold">
                                            ⚠️ Faltante
                                        </Badge>
                                    )}
                                </div>
                                <Input
                                    value={draftValidationData.demandante}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, demandante: e.target.value })}
                                    placeholder="Nombre completo del demandante"
                                    className={`rounded-xl text-xs ${!draftValidationData.demandante ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}
                                />
                            </div>

                            {/* Demandado */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                        <Users className="h-3.5 w-3.5 text-indigo-600" />
                                        Demandado / Accionado
                                    </Label>
                                    {!draftValidationData.demandado && (
                                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 font-bold">
                                            ⚠️ Faltante
                                        </Badge>
                                    )}
                                </div>
                                <Input
                                    value={draftValidationData.demandado}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, demandado: e.target.value })}
                                    placeholder="Nombre completo o razón social del demandado"
                                    className={`rounded-xl text-xs ${!draftValidationData.demandado ? 'border-amber-400 bg-amber-50/30' : 'border-slate-200'}`}
                                />
                            </div>

                            {/* Clase de Proceso */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-slate-700">Clase / Tipo de Proceso</Label>
                                <Input
                                    value={draftValidationData.clase_proceso}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, clase_proceso: e.target.value })}
                                    placeholder="Ej: Ejecutivo Laboral, Ordinario, Tutela"
                                    className="rounded-xl border-slate-200 text-xs"
                                />
                            </div>

                            {/* Ciudad */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-slate-700">Ciudad / Municipio</Label>
                                <Input
                                    value={draftValidationData.ciudad}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, ciudad: e.target.value })}
                                    placeholder="Ej: Medellín"
                                    className="rounded-xl border-slate-200 text-xs"
                                />
                            </div>

                            {/* Abogado */}
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold text-slate-700">Nombre del Abogado / Apoderado</Label>
                                <Input
                                    value={draftValidationData.abogado}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, abogado: e.target.value })}
                                    placeholder="Nombre del Abogado"
                                    className="rounded-xl border-slate-200 text-xs"
                                />
                            </div>

                            {/* Tarjeta Profesional */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold text-slate-700">Tarjeta Profesional (T.P.)</Label>
                                    {!draftValidationData.tarjeta_profesional && (
                                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300 font-bold">
                                            ⚠️ Recomendado
                                        </Badge>
                                    )}
                                </div>
                                <Input
                                    value={draftValidationData.tarjeta_profesional}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, tarjeta_profesional: e.target.value })}
                                    placeholder="Ej: T.P. No. 123456 del C.S.J."
                                    className={`rounded-xl text-xs ${!draftValidationData.tarjeta_profesional ? 'border-amber-300 bg-amber-50/20' : 'border-slate-200'}`}
                                />
                            </div>

                            {/* Notificaciones */}
                            <div className="space-y-1.5 md:col-span-2">
                                <Label className="text-xs font-bold text-slate-700">Correo / Dirección de Notificaciones</Label>
                                <Input
                                    value={draftValidationData.direccion_notificacion}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, direccion_notificacion: e.target.value })}
                                    placeholder="Ej: notificaciones@estudiojuridico.com / Calle 50 # 45-20"
                                    className="rounded-xl border-slate-200 text-xs"
                                />
                            </div>

                            {/* Asunto */}
                            <div className="space-y-1.5 md:col-span-2">
                                <Label className="text-xs font-bold text-slate-700">Asunto / Título del Escrito</Label>
                                <Input
                                    value={draftValidationData.asunto}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, asunto: e.target.value })}
                                    placeholder="Título o tipo de memorial"
                                    className="rounded-xl border-slate-200 text-xs font-semibold"
                                />
                            </div>

                            {/* Objetivo */}
                            <div className="space-y-1.5 md:col-span-2">
                                <Label className="text-xs font-bold text-slate-700">Objetivo / Solicitud Principal (Petitum)</Label>
                                <Textarea
                                    value={draftValidationData.objetivo}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, objetivo: e.target.value })}
                                    placeholder="Describe qué se solicita en este memorial..."
                                    className="rounded-xl border-slate-200 text-xs min-h-[70px]"
                                />
                            </div>

                            {/* Instrucciones Adicionales */}
                            <div className="space-y-1.5 md:col-span-2">
                                <Label className="text-xs font-bold text-slate-700">Instrucciones Adicionales para la IA (Opcional)</Label>
                                <Textarea
                                    value={draftValidationData.instrucciones}
                                    onChange={(e) => setDraftValidationData({ ...draftValidationData, instrucciones: e.target.value })}
                                    placeholder="Ej: Enfocar en la suspensión de términos, solicitar entrega de títulos, citar sentencia T-123..."
                                    className="rounded-xl border-slate-200 text-xs min-h-[60px]"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                        <p className="text-[11px] text-slate-500 font-medium">
                            Costo: 4 Créditos IA para borrador estratégico completo
                        </p>
                        <div className="flex items-center gap-2.5">
                            <Button
                                variant="ghost"
                                className="rounded-xl text-xs font-semibold"
                                onClick={() => setIsValidationModalOpen(false)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-600/20 text-xs font-bold px-6"
                                onClick={handleConfirmAndGenerateDraft}
                            >
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generar Escrito Judicial Completo
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Feedback & Self-learning Dialog */}
            <Dialog open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen}>
                <DialogContent className="rounded-2xl max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-legal-blue" />
                            Personalizar Acción Estratégica
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4 text-left">
                        <p className="text-xs text-gray-500">
                            Indícanos por qué no aplica esta sugerencia para alimentar la base de aprendizaje del sistema.
                            Luego, asocia elementos clave de este proceso para que la IA genere una nueva acción específica.
                        </p>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-700">Motivo del rechazo (¿Por qué no aplica?)</Label>
                            <Textarea
                                placeholder="Ej: La contestación ya fue radicada sin tachas de falsedad / No contamos con la prueba física en este momento..."
                                value={feedbackReason}
                                onChange={(e) => setFeedbackReason(e.target.value)}
                                className="rounded-xl min-h-[70px] text-xs"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-700">Relación con Actuación Judicial (Opcional)</Label>
                            <Select value={feedbackRelatedActuacion} onValueChange={setFeedbackRelatedActuacion}>
                                <SelectTrigger className="rounded-xl text-xs">
                                    <SelectValue placeholder="Selecciona una actuación de la línea de tiempo..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {timeline.slice(0, 50).map((act, index) => (
                                        <SelectItem key={index} value={`[${act.fecha}] ${act.actuacion}`}>
                                            [{act.fecha}] {act.actuacion.substring(0, 50)}{act.actuacion.length > 50 ? '...' : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-700">Relación con Prueba / Archivo (Opcional)</Label>
                            <Select value={feedbackRelatedPrueba} onValueChange={setFeedbackRelatedPrueba}>
                                <SelectTrigger className="rounded-xl text-xs">
                                    <SelectValue placeholder="Selecciona un archivo del expediente..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {processFiles.length > 0 ? (
                                        processFiles.map((file, index) => (
                                            <SelectItem key={index} value={file.file_name}>
                                                {file.file_name}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <SelectItem value="Ninguna" disabled>
                                            No hay archivos cargados
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-700">Etapa Procesal del Proceso</Label>
                            <Select value={feedbackEtapaProcesal} onValueChange={setFeedbackEtapaProcesal}>
                                <SelectTrigger className="rounded-xl text-xs">
                                    <SelectValue placeholder="Selecciona la etapa procesal..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Demanda / Contestación">Demanda / Contestación</SelectItem>
                                    <SelectItem value="Pruebas / Instrucción">Pruebas / Instrucción</SelectItem>
                                    <SelectItem value="Alegatos de conclusión">Alegatos de conclusión</SelectItem>
                                    <SelectItem value="Sentencia / Fallo">Sentencia / Fallo</SelectItem>
                                    <SelectItem value="Recursos / Apelación">Recursos / Apelación</SelectItem>
                                    <SelectItem value="Ejecución de sentencia">Ejecución de sentencia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="ghost"
                            className="rounded-xl"
                            onClick={() => setIsFeedbackDialogOpen(false)}
                            disabled={isSubmittingFeedback}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className="rounded-xl bg-legal-blue hover:bg-legal-navy text-white px-5 flex items-center gap-1.5 shadow-md shadow-blue-200"
                            onClick={handleFeedbackSubmit}
                            disabled={isSubmittingFeedback || !feedbackReason.trim()}
                        >
                            {isSubmittingFeedback ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Generando...
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4" />
                                    Generar Acción Alternativa
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal para Vincular Redacción de Documentos v2 */}
            <Dialog open={isLinkDraftingModalOpen} onOpenChange={setIsLinkDraftingModalOpen}>
                <DialogContent className="max-w-2xl rounded-2xl p-0 overflow-hidden border border-slate-100 shadow-2xl bg-white flex flex-col">
                    <DialogHeader className="bg-slate-50/80 p-6 border-b border-slate-100">
                        <DialogTitle className="text-slate-900 font-bold text-lg flex items-center gap-2">
                            <FileCheck className="h-5 w-5 text-indigo-600 animate-pulse" />
                            Vincular Documento Redactado (Drafting v2)
                        </DialogTitle>
                        <p className="text-xs text-slate-400 mt-1">
                            Selecciona uno de tus documentos jurídicos generados en Drafting v2 para convertirlo y vincularlo a este proceso.
                        </p>
                    </DialogHeader>

                    <div className="p-6 space-y-5 flex-1 overflow-y-auto max-h-[450px]">
                        {/* Buscador de documentos redactados */}
                        <div className="space-y-3">
                            <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tus Documentos Guardados</Label>

                            <ScrollArea className="h-60 rounded-xl border border-slate-100 overflow-hidden bg-slate-50/30">
                                {loadingMyDocs ? (
                                    <div className="py-20 flex flex-col items-center justify-center gap-3">
                                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                                        <p className="text-xs text-slate-400 font-medium">Cargando tus documentos...</p>
                                    </div>
                                ) : myDocuments.length > 0 ? (
                                    <div className="p-3 space-y-2">
                                        {myDocuments.map((doc) => {
                                            const isSelected = selectedDocId === doc.document_id;
                                            const previewText = doc.content_text
                                                ? doc.content_text.replace(/<[^>]*>/g, '').substring(0, 120) + '...'
                                                : 'Sin contenido';

                                            return (
                                                <div
                                                    key={doc.document_id}
                                                    onClick={() => {
                                                        setSelectedDocId(doc.document_id);
                                                        setCustomFileName(doc.document_type || "Documento_Redactado");
                                                    }}
                                                    className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer flex flex-col gap-2 ${isSelected
                                                        ? 'border-indigo-500 bg-indigo-50/45 shadow-sm text-indigo-950'
                                                        : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/50 text-slate-700'
                                                        }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                                                                <FileText className="h-4 w-4" />
                                                            </div>
                                                            <div>
                                                                <h4 className="text-sm font-bold leading-tight">{doc.document_type || "Documento"}</h4>
                                                                <p className="text-[10px] text-slate-400 font-semibold mt-0.5">
                                                                    Actualizado el {new Date(doc.created_at).toLocaleDateString("es-CO")}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <Badge variant={isSelected ? 'default' : 'secondary'} className={`text-[9px] font-bold ${isSelected ? 'bg-indigo-600 hover:bg-indigo-600' : ''}`}>
                                                            v{doc.version_number}
                                                        </Badge>
                                                    </div>

                                                    <p className={`text-xs leading-relaxed line-clamp-2 ${isSelected ? 'text-indigo-900/80 font-medium' : 'text-slate-400'}`}>
                                                        {previewText}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="py-20 text-center">
                                        <FileText className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                                        <p className="text-slate-400 text-sm font-medium">No se encontraron documentos redactados.</p>
                                        <p className="text-[11px] text-slate-400 mt-1">Primero crea un documento en el generador de drafting.</p>
                                    </div>
                                )}
                            </ScrollArea>
                        </div>

                        {selectedDocId && (
                            <div className="space-y-4 pt-2 border-t border-slate-100 animate-in fade-in slide-in-from-bottom-3 duration-250">
                                {/* Formato de vinculación */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Formato de Exportación y Vinculación</Label>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setLinkFormat("docx")}
                                            className={`p-3 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold transition-all ${linkFormat === "docx"
                                                ? 'border-blue-500 bg-blue-50/50 text-blue-700 shadow-sm'
                                                : 'border-slate-100 bg-white hover:bg-slate-50 text-slate-600'
                                                }`}
                                        >
                                            <FileText className="h-4 w-4 text-blue-500" />
                                            Documento Word (.docx)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setLinkFormat("pdf")}
                                            className={`p-3 rounded-xl border flex items-center justify-center gap-2 text-xs font-bold transition-all ${linkFormat === "pdf"
                                                ? 'border-red-500 bg-red-50/50 text-red-700 shadow-sm'
                                                : 'border-slate-100 bg-white hover:bg-slate-50 text-slate-600'
                                                }`}
                                        >
                                            <FileText className="h-4 w-4 text-red-500" />
                                            Archivo PDF (.pdf)
                                        </button>
                                    </div>
                                </div>

                                {/* Nombre personalizado */}
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre del Archivo Destino</Label>
                                    <div className="flex items-center gap-2 bg-slate-50/50 border border-slate-200/60 rounded-xl px-3 py-1.5 focus-within:ring-1 focus-within:ring-indigo-500 focus-within:bg-white transition-all">
                                        <Input
                                            type="text"
                                            value={customFileName}
                                            onChange={(e) => setCustomFileName(e.target.value)}
                                            placeholder="Nombre del archivo (sin extensión)"
                                            className="border-0 p-0 h-auto bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-xs font-semibold text-slate-800"
                                        />
                                        <span className="text-xs text-slate-400 font-bold">.{linkFormat}</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400">Puedes renombrar el archivo como aparecerá dentro de los anexos del expediente.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2.5">
                        <Button
                            variant="ghost"
                            className="rounded-xl text-xs font-semibold"
                            onClick={() => setIsLinkDraftingModalOpen(false)}
                            disabled={isLinkingDraft}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-600/10 text-xs font-bold px-6"
                            onClick={handleLinkDraftingDocument}
                            disabled={isLinkingDraft || !selectedDocId}
                        >
                            {isLinkingDraft ? (
                                <>
                                    <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                    Generando y vinculando...
                                </>
                            ) : (
                                "Vincular al Expediente"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProcessDetailPage;
